<template>
  <div class="masterfiles-vehicle-container">
    <div class="left-menu-container">
      <Menus
        :menu="menuButtons"
        icon="https://dsmlib.logistics.systems/Icons/SteeringWheel.png"
        @menuClicked="menuClicked"
      />
    </div>
    <div class="right-tablebox-container">
      <TableBox
        :tableData="vehicleTableBoxData"
        :selectedRow.sync="vehicleTableBoxSelectedRow"
        title="Vehicle"
        :loading="vehicleTableBoxIsLoading"
        :dataType="vehicleTableBoxDataType"
        :maxSelection="1"
      />
    </div>
    <PopupEntry
      v-if="showPopupEntry"
      :show.sync="showPopupEntry"
      :title="popupEntryTitle"
      :createdBy="popupEntryCreatedBy"
      :updatedBy="popupEntryUpdatedBy"
      :requiredError="popupEntryRequiredError"
      @requiredClicked="popupEntryRequiredClicked"
      :successMessages="popupEntrySuccessMessages"
      :errorMessages="popupEntryErrorMessages"
      :viewMode="popupEntryInViewMode && !(canResetVehicleMileage && mileageHasChanges)"
      :disabledSave="mileageIsSaving"
      @save="popupEntrySaveClicked"
    >
      <div class="popup-entry-container">
        <div class="inventory-info-container">
          <Input
            class="inventory-info-input"
            :inputValue.sync="inventoryNoInputValue"
            placeholder="Inventory No"
            :isRequired="true"
            :errorMessage.sync="inventoryNoInputErrorMessage"
            :isDisabled="inventoryNoInputIsDisabled"
            ref="lorry_no"
            validator="masterfilecode,25,uppercase"
          />
          <Input
            class="inventory-info-input"
            :inputValue.sync="inventoryCodeInputValue"
            placeholder="Inventory Code"
            :isRequired="true"
            :errorMessage.sync="inventoryCodeInputErrorMessage"
            :isDisabled="inventoryCodeInputIsDisabled"
            ref="vehiclecode"
            validator="masterfilecode,20,uppercase"
          />
          <!-- hide temporarily, because there is no column in the database table to store this yet, unhide it when the column has been added -->
          <CheckBox
            v-if="false"
            class="inventory-info-input"
            :isChecked.sync="activeCheckBoxIsChecked"
            title="Active"
            :isDisabled="activeCheckBoxIsDisabled"
          />
        </div>
        <div class="tab-component-container">
          <Tab class="tab-component" :tabs="tabItems" :tabIndex.sync="selectedTabIndex">
            <div class="tab-container general" slot="General">
              <div class="vehicle-status-row" style="flex: 0 0 auto">
                <div class="availability-info-container">
                  <div class="availability-radio-container">
                    <RadioButtonSet
                      labelKey="label"
                      :buttons="availabilityRadioButtons"
                      itemKey="label"
                      title="Availability"
                      :showBox="false"
                      :isRequired="true"
                      :errorMessage.sync="availabilityRadioErrorMessage"
                      :isHorizontal="true"
                      :selected.sync="availabilityRadioValue"
                      ref="availability"
                      :isDisabled="availabilityRadioIsDisabled"
                    />
                  </div>
                  <div
                    v-if="availabilityRadioValue ? availabilityRadioValue.value === '0' : false"
                    class="availability-remark-container"
                  >
                    <div v-if="unavailabilityRemarkInputIsDisabled" style="color: red">
                      {{ unavailabilityRemarkLabelValue ? `**${unavailabilityRemarkLabelValue}` : '' }}
                    </div>
                    <Input
                      v-else
                      :inputValue.sync="unavailabilityRemarkInputValue"
                      :errorMessage.sync="unavailabilityRemarkInputErrorMessage"
                      :isRequired="true"
                      placeholder="Remarks"
                      ref="remark1"
                      style="width: 450px; max-width: 100%"
                      validator="text,150"
                    />
                    <!-- limit text to 150 although db accepts 200, becaues there's still text to be appended -->
                  </div>
                </div>
                <div class="vehicle-mileage-section">
                  <div class="vehicle-mileage-controls">
                    <Input
                      :inputValue.sync="currentMileageInputValue"
                      :errorMessage.sync="currentMileageInputErrorMessage"
                      placeholder="Current Mileage (KM)"
                      validator="positiveInteger,7"
                      :isDisabled="!canResetVehicleMileage || vehicleAction == 'New' || !vehicleDetails.Lorry_No || mileageIsSaving"
                    />
                  </div>
                </div>
              </div>
              <!-- each div is a single column -->
              <div style="display: flex; gap: 10px; flex: 1">
                <div style="flex: 1">
                  <Dropdown
                    :items="categoryDropdownItems"
                    itemKey="Full Description"
                    :selectedObject.sync="categoryDropdownSelectedItem"
                    placeholder="Category"
                    :isRequired="true"
                    :errorMessage.sync="categoryDropdownErrorMessage"
                    :isDisabled="categoryDropdownIsDisabled"
                    @selected="updateTyreGridTyreLayout"
                    ref="vehiclecategory"
                  />
                  <Input
                    :inputValue.sync="yearInputValue"
                    :errorMessage.sync="yearInputErrorMessage"
                    ref="year"
                    placeholder="Year"
                    :isDisabled="yearInputIsDisabled"
                    validator="positiveInteger,4"
                  />
                  <DateTimePicker
                    :value.sync="datePurchaseDateTimePickerValue"
                    :errorMessage.sync="datePurchaseDateTimePickerErrorMessage"
                    :minDate="new Date('1900-01-01')"
                    :maxDate="new Date()"
                    :showTime="false"
                    placeholder="Date Purchase"
                    :isDisabled="datePurchaseDateTimePickerIsDisabled"
                  />
                  <Input
                    :inputValue.sync="colourInputValue"
                    :errorMessage.sync="colourInputErrorMessage"
                    ref="color"
                    placeholder="Colour"
                    :isDisabled="colourInputIsDisabled"
                  />
                  <Input
                    :inputValue.sync="weightInputValue"
                    :errorMessage.sync="weightInputErrorMessage"
                    ref="netweight"
                    validator="positiveInteger"
                    placeholder="Weight (KG)"
                    :isDisabled="weightInputIsDisabled"
                  />
                  <Input
                    :inputValue.sync="widthInputValue"
                    :errorMessage.sync="widthInputErrorMessage"
                    ref="lorry_width"
                    validator="positiveInteger"
                    placeholder="Width (Feet)"
                    :isDisabled="widthInputIsDisabled"
                  />
                  <Input
                    :inputValue.sync="lengthInputValue"
                    :errorMessage.sync="lengthInputErrorMessage"
                    ref="lorry_length"
                    validator="positiveInteger"
                    placeholder="Length (Feet)"
                    :isRequired="false"
                    :isDisabled="lengthInputIsDisabled"
                  />
                  <Input
                    :inputValue.sync="heightInputValue"
                    :errorMessage.sync="heightInputErrorMessage"
                    ref="height"
                    validator="positiveInteger"
                    placeholder="Height (Feet)"
                    :isDisabled="heightInputIsDisabled"
                  />
                  <Input
                    :inputValue.sync="maxCapacityInputValue"
                    :errorMessage.sync="maxCapacityInputErrorMessage"
                    ref="maxcapacity"
                    validator="positiveInteger"
                    placeholder="Max. Capacity (M3)"
                    :isDisabled="maxCapacityInputIsDisabled"
                  />
                  <Input
                    :inputValue.sync="winchCapacityInputValue"
                    :errorMessage.sync="winchCapacityInputErrorMessage"
                    ref="winchcapacity"
                    validator="positiveInteger"
                    placeholder="Winch Capacity (M3)"
                    :isDisabled="winchCapacityInputIsDisabled"
                  />
                  <Input
                    :inputValue.sync="groundClearanceInputValue"
                    :errorMessage.sync="groundClearanceInputErrorMessage"
                    ref="groundclearance"
                    validator="positiveInteger"
                    placeholder="Ground Clearance"
                    :isDisabled="groundClearanceInputIsDisabled"
                  />
                  <Dropdown
                    :items="depreciationTypeDropdownItems"
                    itemKey="Description"
                    :selectedObject.sync="depreciationTypeDropdownSelectedItem"
                    placeholder="Depreciation Type"
                    :isDisabled="depreciationTypeDropdownIsDisabled"
                  />
                </div>
                <div style="flex: 1">
                  <Input
                    :inputValue.sync="vehicleModelInputValue"
                    :errorMessage.sync="vehicleModelInputErrorMessage"
                    ref="lorry_model"
                    placeholder="Model"
                    :isDisabled="vehicleModelInputIsDisabled"
                  />
                  <InputQuery
                    :items="trailerInputQueryItems"
                    itemKey="Trailer No"
                    descKey="Model"
                    :selectedObject.sync="trailerInputQuerySelectedObject"
                    placeholder="Trailer No"
                    :isDisabled="trailerInputQueryIsDisabled"
                  />
                  <Dropdown
                    :items="vehicleMadeInDropdownItems"
                    itemKey="Country"
                    :selectedObject.sync="vehicleMadeInDropdownSelectedItem"
                    placeholder="Made In"
                    :isDisabled="vehicleMadeInDropdownIsDisabled"
                  />
                  <Dropdown
                    :items="companyDropdownItems"
                    itemKey="Company Name"
                    :selectedObject.sync="companyDropdownSelectedItem"
                    placeholder="Company"
                    :isRequired="true"
                    :errorMessage.sync="companyDropdownErrorMessage"
                    :isDisabled="companyDropdownIsDisabled"
                    ref="company_code"
                  />
                  <Dropdown
                    :items="ownershipDropdownItems"
                    itemKey="Ownership"
                    :selectedObject.sync="ownershipDropdownSelectedItem"
                    placeholder="Ownership"
                    :isDisabled="ownershipDropdownIsDisabled"
                  />
                  <InputQuery
                    :items="supplierInputQueryItems"
                    itemKey="Supplier Code"
                    descKey="Supplier Name"
                    :selectedObject.sync="supplierInputQuerySelectedObject"
                    placeholder="Supplier"
                    :isDisabled="supplierInputQueryIsDisabled"
                  />
                  <Input
                    :inputValue.sync="priceInputValue"
                    :errorMessage.sync="priceInputErrorMessage"
                    ref="price"
                    :regex="decimalRegex"
                    placeholder="Price (RM)"
                    :isDisabled="priceInputIsDisabled"
                  />
                  <Input
                    :inputValue.sync="loanFromInputValue"
                    :errorMessage.sync="loanFromInputErrorMessage"
                    ref="fianceby"
                    placeholder="Loan From"
                    :isDisabled="loanFromInputIsDisabled"
                  />
                  <Input
                    :inputValue.sync="regCardNoInputValue"
                    placeholder="Reg Card No"
                    :isDisabled="regCardNoInputIsDisabled"
                    :errorMessage.sync="regCardNoInputErrorMessage"
                    ref="regcardno"
                  />
                  <Input
                    :inputValue.sync="regPlaceInputValue"
                    :errorMessage.sync="regPlaceInputErrorMessage"
                    ref="regplace"
                    placeholder="Reg Place"
                    :isDisabled="regPlaceInputIsDisabled"
                  />
                  <Input
                    :inputValue.sync="openingMileageInputValue"
                    :errorMessage.sync="openingMileageInputErrorMessage"
                    ref="openingmeter"
                    validator="positiveInteger"
                    placeholder="Opening Mileage"
                    :isDisabled="openingMileageInputIsDisabled"
                  />
                  <div style="display: flex; flex-direction: row">
                    <Dropdown
                      style="margin-right: 3px"
                      :items="teamDropdownItems"
                      itemKey="Team"
                      :selectedObject.sync="teamDropdownSelectedItem"
                      placeholder="Team"
                      :isDisabled="teamDropdownIsDisabled"
                    />
                    <CheckBox
                      style="margin-left: 3px"
                      :isChecked.sync="sideloaderCheckBoxIsChecked"
                      title="Sideloader"
                      :isDisabled="sideloaderCheckBoxIsDisabled"
                    />
                  </div>
                </div>
              </div>
            </div>
            <div class="tab-container engine-information" slot="Engine Information">
              <div class="engine-information-container">
                <div>
                  <Input
                    :inputValue.sync="engineNoInputValue"
                    placeholder="Engine No"
                    :errorMessage.sync="engineNoInputErrorMessage"
                    ref="engineno"
                    :isDisabled="engineNoInputIsDisabled"
                  />
                  <Input
                    :inputValue.sync="chassisNoInputValue"
                    placeholder="Chassis No"
                    :errorMessage.sync="chassisNoInputErrorMessage"
                    ref="chassisno"
                    :isDisabled="chassisNoInputIsDisabled"
                  />
                  <Input
                    :inputValue.sync="turboChargerNoInputValue"
                    placeholder="Turbo Charger No"
                    :errorMessage.sync="turboChargerNoInputErrorMessage"
                    ref="turbochargerno"
                    :isDisabled="turboChargerNoInputIsDisabled"
                  />
                </div>
                <div>
                  <Dropdown
                    :items="fuelDropdownItems"
                    itemKey="Type"
                    :selectedObject.sync="fuelDropdownSelectedItem"
                    placeholder="Fuel"
                    :isDisabled="fuelDropdownIsDisabled"
                  />
                  <Input
                    :inputValue.sync="horsePowerInputValue"
                    :errorMessage.sync="horsePowerInputErrorMessage"
                    validator="positiveInteger"
                    ref="horsepower"
                    placeholder="Horse Power"
                    :isDisabled="horsePowerInputIsDisabled"
                  />
                  <Input
                    :inputValue.sync="engineModelInputValue"
                    :errorMessage.sync="engineModelInputErrorMessage"
                    ref="enginemodel"
                    placeholder="Engine Model"
                    :isDisabled="engineModelInputIsDisabled"
                  />
                </div>
              </div>
              <div class="weight-information-container">
                <div>
                  <Input
                    :inputValue.sync="BDMInputValue"
                    :errorMessage.sync="BDMInputErrorMessage"
                    validator="positiveInteger"
                    ref="bdm"
                    placeholder="BDM"
                    :isDisabled="BDMInputIsDisabled"
                  />
                  <Input
                    :inputValue.sync="BTTInputValue"
                    :errorMessage.sync="BTTInputErrorMessage"
                    validator="positiveInteger"
                    ref="btt"
                    placeholder="BTT"
                    :isDisabled="BTTInputIsDisabled"
                  />
                  <Input
                    :inputValue.sync="kerbWeightInputValue"
                    :errorMessage.sync="kerbWeightInputErrorMessage"
                    validator="positiveInteger"
                    ref="kerbweight"
                    placeholder="Kerb Weight"
                    :isDisabled="kerbWeightInputIsDisabled"
                  />
                  <Input
                    :inputValue.sync="BTMInputValue"
                    :errorMessage.sync="BTMInputErrorMessage"
                    validator="positiveInteger"
                    ref="btm"
                    placeholder="BTM"
                    :isDisabled="BTMInputIsDisabled"
                  />
                  <Input
                    :inputValue.sync="BG1InputValue"
                    :errorMessage.sync="BG1InputErrorMessage"
                    validator="positiveInteger"
                    ref="bg1"
                    placeholder="BG1"
                    :isDisabled="BG1InputIsDisabled"
                  />
                  <Input
                    :inputValue.sync="BG2InputValue"
                    :errorMessage.sync="BG2InputErrorMessage"
                    validator="positiveInteger"
                    ref="bg2"
                    placeholder="BG2"
                    :isDisabled="BG2InputIsDisabled"
                  />
                  <Input
                    :inputValue.sync="BG3InputValue"
                    :errorMessage.sync="BG3InputErrorMessage"
                    validator="positiveInteger"
                    ref="bg3"
                    placeholder="BG3"
                    :isDisabled="BG3InputIsDisabled"
                  />
                  <Input
                    :inputValue.sync="BG4InputValue"
                    :errorMessage.sync="BG4InputErrorMessage"
                    validator="positiveInteger"
                    ref="bg4"
                    placeholder="BG4"
                    :isDisabled="BG4InputIsDisabled"
                  />
                </div>
                <div>
                  <Input
                    :inputValue.sync="BG5InputValue"
                    :errorMessage.sync="BG5InputErrorMessage"
                    validator="positiveInteger"
                    ref="bg5"
                    placeholder="BG5"
                    :isDisabled="BG5InputIsDisabled"
                  />
                  <Input
                    :inputValue.sync="BG6InputValue"
                    :errorMessage.sync="BG6InputErrorMessage"
                    validator="positiveInteger"
                    ref="bg6"
                    placeholder="BG6"
                    :isDisabled="BG6InputIsDisabled"
                  />
                  <Input
                    :inputValue.sync="BG7InputValue"
                    :errorMessage.sync="BG7InputErrorMessage"
                    validator="positiveInteger"
                    ref="bg7"
                    placeholder="BG7"
                    :isDisabled="BG7InputIsDisabled"
                  />
                  <Input
                    :inputValue.sync="BG8InputValue"
                    :errorMessage.sync="BG8InputErrorMessage"
                    validator="positiveInteger"
                    ref="bg8"
                    placeholder="BG8"
                    :isDisabled="BG8InputIsDisabled"
                  />
                  <Input
                    :inputValue.sync="BG9InputValue"
                    :errorMessage.sync="BG9InputErrorMessage"
                    validator="positiveInteger"
                    ref="bg9"
                    placeholder="BG9"
                    :isDisabled="BG9InputIsDisabled"
                  />
                  <Input
                    :inputValue.sync="BG10InputValue"
                    :errorMessage.sync="BG10InputErrorMessage"
                    validator="positiveInteger"
                    ref="bg10"
                    placeholder="BG10"
                    :isDisabled="BG10InputIsDisabled"
                  />
                  <Input
                    :inputValue.sync="BG11InputValue"
                    :errorMessage.sync="BG11InputErrorMessage"
                    validator="positiveInteger"
                    ref="bg11"
                    placeholder="BG11"
                    :isDisabled="BG11InputIsDisabled"
                  />
                  <Input
                    :inputValue.sync="BG12InputValue"
                    :errorMessage.sync="BG12InputErrorMessage"
                    validator="positiveInteger"
                    ref="bg12"
                    placeholder="BG12"
                    :isDisabled="BG12InputIsDisabled"
                  />
                </div>
              </div>
            </div>
            <div class="tab-container renewal-info" slot="Event">
              <TableBox
                title="Event"
                :tableData="eventTableDataFiltered"
                :dataType="eventTableDataType"
                :hideColumns="[]"
                hideBottomBlock
              />
            </div>
            <div class="tab-container renewal-info" slot="Renewal Info">
              <div class="call-to-action-buttons">
                <button v-if="vehicleAction != 'View'" @click="addRenewalClicked">Add Renewal</button>
                <button v-if="vehicleAction != 'View'" @click="editRenewalClicked">Edit Renewal</button>
                <button v-if="vehicleAction != 'View'" @click="deleteRenewalClicked">Delete Renewal</button>
                <button v-if="vehicleAction == 'View'" @click="viewRenewalClicked">View Renewal</button>
              </div>
              <div class="table-box-container">
                <TableBox
                  :tableData="renewalInfoTableBoxDataFiltered"
                  :selectedRow.sync="renewalInfoTableBoxSelectedRow"
                  title="Renewals"
                  :loading="renewalInfoTableBoxIsLoading"
                  :dataType="renewalInfoTableBoxDataType"
                  :hideColumns="renewalInfoTableBoxHiddenColumns"
                  hideBottomBlock
                />
                <div class="d-flex align-items-center" style="margin-top: 5px">
                  <CheckBoxV2
                    title="Show All (Include History)"
                    :isChecked.sync="renewalInfoTableShowHistoryCheckbox"
                    @checkBoxClicked="showAllClicked"
                  />
                  <div
                    class="d-flex ml-auto align-items-center"
                    style="gap: 4px"
                    v-if="renewalInfoTableShowHistoryCheckbox"
                  >
                    <img width="16px" src="https://dsmlib.logistics.systems/Icons/History_record.svg" />
                    Old Renewal Info
                  </div>
                </div>
              </div>
            </div>
            <div class="tab-container maintenance" slot="Maintenance">
              <div class="call-to-action-buttons">
                <button v-if="vehicleAction != 'View'" @click="addMaintenanceClicked">Add Maintenance</button>
                <button v-if="vehicleAction != 'View'" @click="editMaintenanceClicked">Edit Maintenance</button>
                <button v-if="vehicleAction != 'View'" @click="deleteMaintenanceClicked">Delete Maintenance</button>
                <button v-if="vehicleAction == 'View'" @click="viewMaintenanceClicked">View Maintenance</button>
              </div>
              <div class="table-box-container">
                <TableBox
                  :tableData="maintenanceTableBoxDataWithoutDeleted"
                  :selectedRow.sync="maintenanceTableBoxSelectedRow"
                  title="Maintenance"
                  :loading="maintenanceTableBoxIsLoading"
                  :dataType="maintenanceTableBoxDataType"
                  :hideColumns="maintenanceTableBoxHiddenColumns"
                  groupBy="_groupBy"
                  hideBottomBlock
                />
              </div>
            </div>
            <div class="tab-container todo-list" slot="Tasks">
              <VehicleTodoList
                mode="vehicle"
                :vehicleNo="inventoryNoInputValue"
                :viewOnly="popupEntryInViewMode || vehicleAction == 'View'"
                :draft="vehicleAction == 'New'"
                :draftItems="vehicleTodoDraftItems"
                hideBottomBlock
                @draftChanged="vehicleTodoDraftItems = $event"
              />
            </div>
            <div class="tab-container tyre-configuration" slot="Tyre Configuration">
              <div class="tyre-grid-container">
                <TyreGrid
                  :tyreLayout="tyreGridTyreLayout"
                  layoutType="LeftRight"
                  :selectedTyre.sync="tyreGridSelectedTyre"
                  :selectedTyreDetails.sync="tyreGridSelectedTyreDetails"
                  :showAll.sync="tyreGridShowAll"
                  :showSpares="tyreGridShowSpares"
                  :displayShowAllCheckbox="tyreGridDisplayShowAllCheckbox"
                  @tyreClicked="tyreGridTyreClicked"
                />
              </div>
              <div class="tyre-list">
                <div class="call-to-action-buttons">
                  <button v-if="vehicleAction != 'View'" @click="addTyreClicked">Add Tyre</button>
                  <button v-if="vehicleAction != 'View'" @click="editTyreClicked">Edit Tyre</button>
                  <button v-if="vehicleAction != 'View'" @click="deleteTyreClicked">Delete Tyre</button>
                  <button v-if="vehicleAction == 'View'" @click="viewTyreClicked">View Tyre</button>
                </div>
                <div class="table-box-container">
                  <TableBox
                    :tableData="tyreConfigurationTableBoxDisplayData"
                    :selectedRow.sync="tyreConfigurationTableBoxSelectedRow"
                    title="Tyres"
                    :loading="tyreConfigurationTableBoxIsLoading"
                    :dataType="tyreConfigurationTableBoxDataType"
                    :hideColumns="tyreConfigurationTableBoxHiddenColumns"
                    hideBottomBlock
                    @rowSelected="tyreConfigurationTableBoxRowSelected"
                  />
                </div>
              </div>
            </div>
            <div class="tab-container attachments" slot="Attachment">
              <div class="call-to-action-buttons">
                <button v-if="vehicleAction != 'View'" @click="addAttachmentClicked">Add Attachment</button>
                <button v-if="vehicleAction != 'View'" @click="editAttachmentClicked">Edit Attachment</button>
                <button v-if="vehicleAction != 'View'" @click="deleteAttachmentClicked">Delete Attachment</button>
                <button v-if="vehicleAction == 'View'" @click="viewAttachmentClicked">View Attachment</button>
              </div>
              <div class="table-box-container">
                <TableBox
                  :tableData="attachmentsTableBoxDataWithoutDeleted"
                  :selectedRow.sync="attachmentsTableBoxSelectedRow"
                  title="Attachments"
                  :loading="attachmentsTableBoxIsLoading"
                  :dataType="attachmentsTableBoxDataType"
                  :hideColumns="attachmentsTableBoxHiddenColumns"
                  hideBottomBlock
                />
              </div>
            </div>
          </Tab>
        </div>
      </div>
    </PopupEntry>

    <PopupEntry
      v-if="showPMToDriverPopupEntry"
      :show.sync="showPMToDriverPopupEntry"
      title="PM to Driver"
      :viewMode="true"
      cancelButton="Close"
    >
      <div class="pm-to-driver-popup-container">
        <div class="pm-to-driver-list">
          <div class="table-box-container">
            <TableBox
              :key="pmToDriverTableBoxKey"
              :tableData="pmToDriverTableBoxData"
              :selectedRow.sync="pmToDriverTableBoxSelectedRow"
              title="PM to Driver"
              :loading="pmToDriverTableBoxIsLoading"
              :maxSelection="1"
            />
          </div>
        </div>

        <BoxContainer class="pm-to-driver-details" title="PM / Driver Details" :isCheckBox="false">
          <div class="pm-to-driver-details-content">
            <InputQuery
              :items="pmToDriverAvailablePMItems"
              itemKey="Code - Description"
              :selectedObject.sync="pmToDriverPMDropdownSelectedItem"
              placeholder="Set PM"
              :isRequired="true"
              :isDisabled="pmToDriverFieldsAreDisabled || pmToDriverAction === 'Edit'"
            />

            <InputQuery
              :items="pmToDriverPMTeamDropdownItems"
              itemKey="Code - Description"
              :selectedObject.sync="pmToDriverPMTeamDropdownSelectedItem"
              placeholder="Team"
              :isDisabled="true"
            />

            <InputQuery
              :items="pmToDriverAvailableDriver1Items"
              itemKey="Code - Description"
              :selectedObject.sync="pmToDriverDriver1DropdownSelectedItem"
              placeholder="To Driver 1"
              :isRequired="true"
              :isDisabled="pmToDriverFieldsAreDisabled"
            />

            <BoxContainer
              class="pm-to-driver-driver2-box"
              title="Driver 2"
              :isCheckBox="true"
              :isChecked.sync="pmToDriverHasDriver2"
              :checkBoxIsDisabled="pmToDriverFieldsAreDisabled"
            >
              <InputQuery
                :items="pmToDriverAvailableDriver2Items"
                itemKey="Code - Description"
                :selectedObject.sync="pmToDriverDriver2DropdownSelectedItem"
                placeholder="To Driver 2"
                :isRequired="pmToDriverHasDriver2"
                :isDisabled="pmToDriverFieldsAreDisabled || !pmToDriverHasDriver2"
              />
            </BoxContainer>

            <button :disabled="pmToDriverAction !== ''" @click="pmToDriverAddClicked"> New </button>

            <template v-if="pmToDriverAction === ''">
              <button :disabled="pmToDriverTableBoxSelectedRow.length !== 1" @click="pmToDriverEditClicked">
                Edit
              </button>

              <button :disabled="pmToDriverTableBoxSelectedRow.length !== 1" @click="pmToDriverDeleteClicked">
                Delete
              </button>
            </template>

            <template v-else>
              <button @click="pmToDriverSaveClicked">
                {{ pmToDriverAction === 'New' ? 'Save New' : 'Save Edit' }}
              </button>

              <button @click="pmToDriverCancelClicked"> Cancel </button>
            </template>
          </div>
        </BoxContainer>
      </div>
    </PopupEntry>

    <PopupEntry
      v-if="showTeamManagementPopupEntry"
      :show.sync="showTeamManagementPopupEntry"
      title="Team Management"
      :successMessages="teamManagementSuccessMessages"
      :errorMessages="teamManagementErrorMessages"
      @save="teamManagementSaveClicked"
    >
      <div class="team-management-popup-container">
        <div class="team-management-toolbar">
          <Dropdown
            :items="teamManagementForDropdownItems"
            itemKey="Description"
            :selectedObject.sync="teamManagementForDropdownSelectedItem"
            placeholder="Team Management For"
            :isRequired="true"
            :isDisabled="showTeamManagementAddTeamPanel"
            @selected="teamManagementForSelected"
          />

          <InputQuery
            class="team-input-query"
            :items="teamManagementTeamItems"
            itemKey="Code - Description"
            :selectedObject.sync="teamManagementSelectedTeam"
            placeholder="Team"
            :isRequired="true"
            :isDisabled="showTeamManagementAddTeamPanel"
            @selected="teamManagementTeamSelected"
          />

          <button :disabled="showTeamManagementAddTeamPanel" @click="teamManagementAddTeamClicked">
            Add New Team
          </button>
        </div>

        <div class="team-management-tables">
          <div class="team-management-table-panel">
            <TableBox
              :key="`available-${teamManagementAvailablePMs.length}`"
              :tableData="teamManagementAvailablePMs"
              :selectedRow.sync="teamManagementAvailableSelectedRows"
              title="PM Without Team"
              :loading="teamManagementIsLoading"
              :maxSelection="-1"
              :hideColumns="['Team']"
            />
          </div>

          <div class="team-management-transfer-buttons">
            <button
              title="Assign selected PMs to Team"
              :disabled="!teamManagementSelectedTeam || teamManagementAvailableSelectedRows.length === 0"
              @click="teamManagementAssignClicked"
            >
              →
            </button>

            <button
              title="Remove selected PMs from Team"
              :disabled="!teamManagementSelectedTeam || teamManagementAssignedSelectedRows.length === 0"
              @click="teamManagementRemoveClicked"
            >
              ←
            </button>
          </div>

          <div class="team-management-table-panel">
            <TableBox
              :key="`assigned-${
                teamManagementSelectedTeam ? teamManagementSelectedTeam['Team'] : ''
              }-${teamManagementAssignedPMs.length}`"
              :tableData="teamManagementAssignedPMs"
              :selectedRow.sync="teamManagementAssignedSelectedRows"
              :title="
                teamManagementSelectedTeam ? `PMs in ${teamManagementSelectedTeam['Team']}` : 'PMs in Selected Team'
              "
              :loading="teamManagementAssignedIsLoading"
              :maxSelection="-1"
              :hideColumns="['Team']"
            />
          </div>

          <BoxContainer v-if="showTeamManagementAddTeamPanel" title="Add New Team" :isCheckBox="false">
            <div class="pm-to-driver-details-content">
              <Input
                ref="newteam"
                :inputValue.sync="newTeamCodeInputValue"
                :errorMessage.sync="newTeamCodeInputErrorMessage"
                placeholder="Team"
                :isRequired="true"
                :isDisabled="teamManagementAddTeamIsSaving"
                validator="masterfilecode,10,uppercase"
              />

              <Input
                ref="newteamdescription"
                :inputValue.sync="newTeamDescriptionInputValue"
                :errorMessage.sync="newTeamDescriptionInputErrorMessage"
                placeholder="Team Description"
                :isDisabled="teamManagementAddTeamIsSaving"
                validator="text,50"
              />

              <button :disabled="teamManagementAddTeamIsSaving" @click="teamManagementActionSaveClicked"> Save </button>

              <button :disabled="teamManagementAddTeamIsSaving" @click="teamManagementActionCancelClicked">
                Cancel
              </button>
            </div>
          </BoxContainer>
        </div>
      </div>
    </PopupEntry>

    <div v-if="showRenewalInfoPopup" class="renewal-info-popup-container">
      <div class="renewal-info-popup">
        <div class="controls">
          <div class="positions-1-to-9">
            <!-- https://dev.azure.com/mydigitalsystems/53bca99b-180e-49c3-b7f5-b8aad6ad1893/_apis/wit/attachments/6857990c-b50a-4561-98ec-2b7dafbf2a1e?fileName=Renewal%20Info%20Popup%20Positions.eml -->
            <div class="positions-1-to-4">
              <!-- explanation on positions -->
              <div class="position-1">
                <Dropdown
                  style="margin-right: 5px; flex: 1"
                  :items="policyTypeDropdownItems"
                  itemKey="PolicyType"
                  :selectedObject.sync="policyTypeDropdownSelectedItem"
                  placeholder="Policy Type"
                  :isRequired="true"
                  :errorMessage.sync="policyTypeDropdownErrorMessage"
                  :isDisabled="policyTypeDropdownIsDisabled"
                />
                <div
                  v-if="
                    policyTypeDropdownSelectedItem &&
                    renewalInfoControls[policyTypeDropdownSelectedItem['PolicyType']] &&
                    getRenewalInfoControlDetails(1)
                  "
                  style="margin-left: 5px; flex: 1"
                >
                  <Input
                    v-if="getRenewalInfoControlDetails(1)['type'] == 'Input'"
                    :inputValue.sync="renewalInfoInputData[1]['value']"
                    :placeholder="getRenewalInfoControlDetails(1)['placeholder']"
                    :isRequired="getRenewalInfoControlDetails(1)['required'] == '1' ? true : false"
                    :errorMessage.sync="renewalInfoInputData[1]['errorMessage']"
                    :isDisabled="renewalInfoInputData[1]['isDisabled']"
                  />
                  <DateTimePicker
                    v-else-if="getRenewalInfoControlDetails(1)['type'] == 'DateTimePicker'"
                    :value.sync="renewalInfoInputData[1]['value']"
                    :showTime="false"
                    :placeholder="getRenewalInfoControlDetails(1)['placeholder']"
                    :isRequired="getRenewalInfoControlDetails(1)['required'] == '1' ? true : false"
                    :errorMessage.sync="renewalInfoInputData[1]['errorMessage']"
                    :isDisabled="renewalInfoInputData[1]['isDisabled']"
                  />
                </div>
                <div v-else style="margin-left: 5px; height: 25px; flex: 1"></div>
                <!-- blank space to position all fields consistent with 4G -->
              </div>
              <div
                v-for="id in Array.from({ length: 3 }, (_, index) => {
                  return index + 2;
                })"
                :key="id"
              >
                <!-- iterate from 2 to 4 -->
                <div
                  v-if="
                    policyTypeDropdownSelectedItem &&
                    renewalInfoControls[policyTypeDropdownSelectedItem['PolicyType']] &&
                    getRenewalInfoControlDetails(id)
                  "
                >
                  <!-- Duration (Months) is just for displaying the difference between effective date and expiry date, it will always be disabled-->
                  <Input
                    v-if="getRenewalInfoControlDetails(id)['placeholder'] == 'Duration (MM)'"
                    :inputValue="effectiveDateExpiryDateDifference"
                    placeholder="Duration (Months)"
                    :isDisabled="true"
                  />
                  <!-- DateTimePicker for Effective Date needs an extra maxDate prop -->
                  <DateTimePicker
                    v-else-if="getRenewalInfoControlDetails(id)['placeholder'] == 'Effective Date'"
                    :value.sync="renewalInfoInputData[id]['value']"
                    :showTime="false"
                    :maxDate="effectiveDateMaxDate"
                    :placeholder="'Effective Date'"
                    :isRequired="getRenewalInfoControlDetails(id)['required'] == '1' ? true : false"
                    :errorMessage.sync="renewalInfoInputData[id]['errorMessage']"
                    :isDisabled="renewalInfoInputData[id]['isDisabled']"
                  />
                  <!-- DateTimePicker for Expiry Date needs an extra minDate prop -->
                  <DateTimePicker
                    v-else-if="getRenewalInfoControlDetails(id)['placeholder'] == 'Expiry Date'"
                    :value.sync="renewalInfoInputData[id]['value']"
                    :showTime="false"
                    :minDate="expiryDateMinDate"
                    :placeholder="'Expiry Date'"
                    :isRequired="getRenewalInfoControlDetails(id)['required'] == '1' ? true : false"
                    :errorMessage.sync="renewalInfoInputData[id]['errorMessage']"
                    :isDisabled="renewalInfoInputData[id]['isDisabled']"
                  />
                  <Input
                    v-else-if="getRenewalInfoControlDetails(id)['type'] == 'Input'"
                    :inputValue.sync="renewalInfoInputData[id]['value']"
                    :placeholder="getRenewalInfoControlDetails(id)['placeholder']"
                    :isRequired="getRenewalInfoControlDetails(id)['required'] == '1' ? true : false"
                    :regex="
                      renewalDecimalInput.includes(getRenewalInfoControlDetails(id)['placeholder']) ? decimalRegex : (
                        null
                      )
                    "
                    :errorMessage.sync="renewalInfoInputData[id]['errorMessage']"
                    :isDisabled="renewalInfoInputData[id]['isDisabled']"
                  />
                  <DateTimePicker
                    v-else-if="getRenewalInfoControlDetails(id)['type'] == 'DateTimePicker'"
                    :value.sync="renewalInfoInputData[id]['value']"
                    :showTime="false"
                    :placeholder="getRenewalInfoControlDetails(id)['placeholder']"
                    :isRequired="getRenewalInfoControlDetails(id)['required'] == '1' ? true : false"
                    :errorMessage.sync="renewalInfoInputData[id]['errorMessage']"
                    :isDisabled="renewalInfoInputData[id]['isDisabled']"
                  />
                </div>
                <div v-else style="height: 25px; width: 100%"></div>
                <!-- blank space to position all fields consistent with 4G -->
              </div>
            </div>
            <div class="positions-5-to-9">
              <div
                v-for="id in Array.from({ length: 5 }, (_, index) => {
                  return index + 5;
                })"
                :key="id"
              >
                <!-- iterate from 5 to 9 -->
                <div
                  v-if="
                    policyTypeDropdownSelectedItem &&
                    renewalInfoControls[policyTypeDropdownSelectedItem['PolicyType']] &&
                    getRenewalInfoControlDetails(id)
                  "
                >
                  <Input
                    v-if="getRenewalInfoControlDetails(id)['type'] == 'Input'"
                    :inputValue.sync="renewalInfoInputData[id]['value']"
                    :placeholder="getRenewalInfoControlDetails(id)['placeholder']"
                    :isRequired="getRenewalInfoControlDetails(id)['required'] == '1' ? true : false"
                    :regex="
                      renewalDecimalInput.includes(getRenewalInfoControlDetails(id)['placeholder']) ? decimalRegex : (
                        null
                      )
                    "
                    :errorMessage.sync="renewalInfoInputData[id]['errorMessage']"
                    :isDisabled="renewalInfoInputData[id]['isDisabled']"
                  />
                  <DateTimePicker
                    v-else-if="getRenewalInfoControlDetails(id)['type'] == 'DateTimePicker'"
                    :value.sync="renewalInfoInputData[id]['value']"
                    :showTime="false"
                    :placeholder="getRenewalInfoControlDetails(id)['placeholder']"
                    :isRequired="getRenewalInfoControlDetails(id)['required'] == '1' ? true : false"
                    :errorMessage.sync="renewalInfoInputData[id]['errorMessage']"
                    :isDisabled="renewalInfoInputData[id]['isDisabled']"
                  />
                </div>
                <div v-else style="height: 29px; width: 100%"></div>
                <!-- blank space to position all fields consistent with 4G -->
              </div>
            </div>
          </div>
          <div class="position-10">
            <div
              v-if="
                policyTypeDropdownSelectedItem &&
                renewalInfoControls[policyTypeDropdownSelectedItem['PolicyType']] &&
                getRenewalInfoControlDetails(10)
              "
            >
              <RemarksBox
                v-if="getRenewalInfoControlDetails(10)['type'] == 'RemarksBox'"
                :text.sync="renewalInfoInputData[10]['value']"
                :isRequired="getRenewalInfoControlDetails(10)['required'] == '1' ? true : false"
                :errorMessage.sync="renewalInfoInputData[10]['errorMessage']"
                :isDisabled="renewalInfoInputData[10]['isDisabled']"
                :maxLength="200"
              />
            </div>
            <div v-else style="height: 29px; width: 100%"></div>
            <!-- blank space -->
          </div>
        </div>
        <div class="call-to-action-buttons">
          <button v-if="renewalInfoAction != 'View'" @click="renewalInfoPopupAffirmativeClicked">{{
            renewalInfoAction
          }}</button>
          <button @click="showRenewalInfoPopup = false">Cancel</button>
        </div>
      </div>
    </div>

    <div v-if="showMaintenancePopup" class="maintenance-popup-container">
      <div class="maintenance-popup">
        <div class="controls">
          <div class="maintenance-info">
            <Dropdown
              :items="servicesDropdownItems"
              itemKey="Description"
              :selectedObject.sync="servicesDropdownSelectedItem"
              placeholder="Services"
              :isRequired="true"
              :errorMessage.sync="servicesDropdownErrorMessage"
              :isDisabled="servicesDropdownIsDisabled"
            />
            <BoxContainer class="interval-box-container" title="Interval" :isCheckBox="false">
              <div class="interval-container">
                <div class="interval-unit">
                  <RadioButton
                    :buttons="intervalUnitRadioButtons"
                    itemKey="label"
                    title=""
                    :showBox="false"
                    :isDisabled="intervalUnitRadioButtonIsDisabled"
                  />
                </div>
                <div class="interval-input">
                  <Input
                    :inputValue.sync="intervalMileageInputValue"
                    :regex="decimalRegex"
                    placeholder="Mileage (KM)"
                    :isRequired="intervalMileageInputIsRequired"
                    :errorMessage.sync="intervalMileageInputErrorMessage"
                    :isDisabled="intervalMileageInputIsDisabled"
                  />
                  <Input
                    :inputValue.sync="intervalDurationInputValue"
                    :regex="decimalRegex"
                    :placeholder="intervalDurationInputPlaceholder"
                    :isRequired="intervalDurationInputIsRequired"
                    :errorMessage.sync="intervalDurationInputErrorMessage"
                    :isDisabled="intervalDurationInputIsDisabled"
                  />
                </div>
              </div>
            </BoxContainer>
            <BoxContainer class="occurrence-box-container" title="Occurrence" :isCheckBox="false">
              <div class="occurrence-container">
                <RadioButton
                  :buttons="occurrenceRadioButtons"
                  itemKey="label"
                  :isHorizontal="true"
                  title=""
                  :showBox="false"
                  :isDisabled="occurrenceRadioButtonIsDisabled"
                />
                <div class="occurrence-date-time-picker-container">
                  <DateTimePicker
                    class="occurrence-date-time-picker"
                    :value.sync="occurrenceDateTimePickerValue"
                    :showTime="false"
                    placeholder=""
                    :isRequired="occurrenceDateTimePickerIsRequired"
                    :errorMessage.sync="occurrenceDateTimePickerErrorMessage"
                    :isDisabled="occurrenceDateTimePickerIsDisabled"
                  />
                </div>
              </div>
            </BoxContainer>
          </div>
          <div class="maintenance-task-list-container">
            <BoxContainer class="task-list-box-container" title="Task List" :isCheckBox="false">
              <div class="call-to-action-buttons">
                <button v-if="maintenanceAction != 'View'" @click="addTaskClicked">Add Task</button>
                <button v-if="maintenanceAction != 'View'" @click="editTaskClicked">Edit Task</button>
                <button v-if="maintenanceAction != 'View'" @click="deleteTaskClicked">Delete Task</button>
                <button v-if="maintenanceAction == 'View'" @click="viewTaskClicked">View Task</button>
              </div>
              <TableBox
                :tableData="taskListTableBoxDataWithoutDeleted"
                :selectedRow.sync="taskListTableBoxSelectedRow"
                title=""
                :loading="taskListTableBoxIsLoading"
                :dataType="taskListTableBoxDataType"
                :hideColumns="taskListTableBoxHiddenColumns"
                style="min-height: 0"
                hideBottomBlock
                fitContentWidth
              />
            </BoxContainer>
          </div>
        </div>
        <div class="call-to-action-buttons">
          <button v-if="maintenanceAction != 'View'" @click="maintenancePopupAffirmativeClicked">{{
            maintenanceAction
          }}</button>
          <button @click="showMaintenancePopup = false">Cancel</button>
        </div>
      </div>
      <PopupV2
        class="task-popup"
        :show="showTaskPopup"
        :title="taskPopupTitle"
        :buttons="taskPopupButtons"
        @buttonClicked="taskPopupButtonClicked"
      >
        <div class="task-popup-content">
          <Dropdown
            :items="taskServicesDropdownItems"
            itemKey="Description"
            :selectedObject.sync="taskServicesDropdownSelectedItem"
            placeholder="Services"
            :isRequired="true"
            :errorMessage.sync="taskServicesDropdownErrorMessage"
            :isDisabled="taskServicesDropdownIsDisabled"
          />
          <RemarksBox
            :text.sync="taskServicesRemarksBoxValue"
            :errorMessage.sync="taskServicesRemarksBoxErrorMessage"
            :isDisabled="taskServicesRemarksBoxIsDisabled"
            :maxLength="200"
          />
          <Input
            v-if="taskAction != 'View'"
            textType="file"
            placeholder="Browse"
            @fileChanged="taskPopupFilesChanged"
            :isDisabled="taskServicesFileInputIsDisabled"
          />
          <div v-if="taskAction == 'Edit' || taskAction == 'View'" class="attached-file">
            <strong>Attached file: </strong>
            <span
              class="file-link"
              title="Click to view"
              @click="
                projectAttachment(
                  taskListTableBoxSelectedRow[0]['_file'] ?
                    taskListTableBoxData.find((task) => {
                      // need to get from TableBoxData, cant just pass value in TableBoxSelectedRow
                      return task['_rowid'] == taskListTableBoxSelectedRow[0]['_rowid']; // because SelectedRow is a copy of the object in TableBoxData made with JSON.parse(JSON.stringify())
                    })['_file'] // the file object loses its data after JSON.stringify(), so you need to get it from TableBoxData directly
                  : {
                      // display file retrieved from API
                      'File Name': taskListTableBoxSelectedRow[0]['Attachment'],
                      'MIME Type': null, // MIME type isn't stored in the database, pass null to the function and it will infer the MIME type from file extension
                      'File Data': taskListTableBoxSelectedRow[0]['_AttImage'],
                    },
                )
              "
            >
              {{ taskServicesAttachedFileName }}
            </span>
          </div>
        </div>
      </PopupV2>
      <PopupV2
        class="duplicate-task-popup"
        :show="showDuplicateTaskPopup"
        type="error"
        :title="`Duplicate Task${duplicateTaskPopupTaskList.length > 1 ? 's' : ''}`"
        :buttons="duplicateTaskPopupButtons"
        @buttonClicked="duplicateTaskPopupButtonClicked"
      >
        <div class="duplicate-task-popup-content">
          <div class="warning-message">
            {{
              `The following task${duplicateTaskPopupTaskList.length > 1 ? 's' : ''} already exist for this service:`
            }}
          </div>
          <div class="duplicate-tasks-list-container">
            <ol>
              <li v-for="task in duplicateTaskPopupTaskList" :key="task['ServiceCode']">
                {{ task['ServiceCode'] }} - {{ task['Description'] }}
              </li>
            </ol>
          </div>
          <div class="prompt">
            {{
              `Please delete the duplicate task${duplicateTaskPopupTaskList.length > 1 ? 's' : ''} or edit them before proceeding.`
            }}
          </div>
        </div>
      </PopupV2>
    </div>

    <div v-if="showTyreConfigurationPopup" class="tyre-configuration-popup-container">
      <div class="tyre-configuration-popup">
        <div class="tyre-position-label">
          <strong>{{ tyreGridSelectedTyreDetails['description'] }}</strong>
        </div>
        <div class="controls">
          <div>
            <InputQuery
              placeholder="Tyre Type"
              :isRequired="true"
              :isDisabled="tyreTypeIsDisabled"
              :selectedObject.sync="tyreTypeSelectedItem"
              :items="tyreTypeListActive"
              itemKey="label"
              :tableBoxHideColumns="[
                'label',
                'Expense Code',
                'Unique Code',
                'IsExpenses',
                'isExpense',
                'Unit Price',
                'SuppCode',
                'Spp_Name',
                'ProdTxnID',
                'Active',
              ]"
              :errorMessage.sync="tyreTypeErrorMessage"
              @selected="tyreTypeSelected"
            />
            <Input
              :inputValue.sync="serialNoInputValue"
              placeholder="Serial No"
              :isRequired="true"
              :errorMessage.sync="serialNoInputErrorMessage"
              :isDisabled="serialNoInputIsDisabled"
            />
            <Input
              :inputValue.sync="sizeInputValue"
              placeholder="Size"
              :errorMessage.sync="sizeInputErrorMessage"
              :isDisabled="sizeInputIsDisabled"
            />
            <DateTimePicker
              :value.sync="mfgDateDateTimePickerValue"
              :maxDate="new Date()"
              :showTime="false"
              placeholder="Mfg Date"
              :isDisabled="mfgDateDateTimePickerIsDisabled"
            />
            <DateTimePicker
              :value.sync="dateChangeDateTimePickerValue"
              :maxDate="new Date()"
              :showTime="false"
              placeholder="Date Change"
              :isDisabled="dateChangeDateTimePickerIsDisabled"
            />
          </div>
          <div>
            <Input
              :inputValue.sync="tyreModelInputValue"
              placeholder="Model"
              :errorMessage.sync="tyreModelInputErrorMessage"
              :isDisabled="tyreModelInputIsDisabled"
            />
            <Dropdown
              :items="manufacturerDropdownItems"
              itemKey="MFGName"
              :selectedObject.sync="manufacturerDropdownSelectedItem"
              placeholder="Manufacturer"
              :isDisabled="manufacturerDropdownIsDisabled"
            />
            <Dropdown
              :items="tyreMadeInDropdownItems"
              itemKey="Country"
              :selectedObject.sync="tyreMadeInDropdownSelectedItem"
              placeholder="Made In"
              :isDisabled="tyreMadeInDropdownIsDisabled"
            />
            <div>
              <RemarksBox
                :text.sync="tyreConfigurationRemarksBoxValue"
                :errorMessage.sync="tyreConfigurationRemarksBoxErrorMessage"
                :isDisabled="tyreConfigurationRemarksBoxIsDisabled"
                :maxLength="200"
              />
            </div>
          </div>
        </div>
        <div class="call-to-action-buttons">
          <button v-if="tyreConfigurationAction != 'View'" @click="tyreConfigurationPopupAffirmativeClicked">{{
            tyreConfigurationAction
          }}</button>
          <button @click="showTyreConfigurationPopup = false">Cancel</button>
        </div>
      </div>
    </div>

    <div class="attachments-popup-container">
      <PopupV2
        class="attachments-popup"
        :show="showAttachmentsPopup"
        :title="attachmentsPopupTitle"
        :buttons="attachmentsPopupButtons"
        @buttonClicked="attachmentsPopupButtonClicked"
      >
        <div class="attachments-popup-content">
          <Input
            textType="file"
            placeholder="Browse"
            :isRequired="attachmentsInputIsRequired"
            :errorMessage.sync="attachmentsInputErrorMessage"
            :multiFile="attachmentsInputMultiFile"
            @fileChanged="attachmentsPopupFilesChanged"
          />
          <div v-if="attachmentsAction == 'Edit'" class="attached-file">
            <strong>Attached file: </strong>
            <span
              class="file-link"
              title="Click to view"
              @click="
                projectAttachment(
                  attachmentsTableBoxSelectedRow[0]['_file'] ?
                    attachmentsTableBoxData.find((attachment) => {
                      // need to get from TableBoxData, cant just pass value in TableBoxSelectedRow
                      return attachment['ID'] == attachmentsTableBoxSelectedRow[0]['ID']; // because SelectedRow is a copy of the object in TableBoxData made with JSON.parse(JSON.stringify())
                    })['_file'] // the file object loses its data after JSON.stringify(), so you need to get it from TableBoxData directly
                  : {
                      // display file retrieved from API
                      'File Name': attachmentsTableBoxSelectedRow[0]['File Name'],
                      'MIME Type': null, // MIME type isn't stored in the database, pass null to the function and it will infer the MIME type from file extension
                      'File Data': attachmentsTableBoxSelectedRow[0]['Attachment'],
                    },
                )
              "
            >
              {{ attachmentsAttachedFileName }}
            </span>
          </div>
          <RemarksBox
            :text.sync="attachmentsRemarksBoxValue"
            :errorMessage.sync="attachmentsRemarksBoxErrorMessage"
            :maxLength="200"
          />
        </div>
      </PopupV2>
    </div>

    <div v-if="showAttachmentProjector" class="attachment-projector">
      <div class="attachment-projection">
        <object
          v-if="attachmentIsProjectable"
          :type="attachmentProjectorType"
          :data="attachmentProjectorData"
          draggable="false"
        >
        </object>
        <div v-else class="attachment-not-projectable-container">
          <span>
            {{
              ((filename) => {
                const split = filename.split('.');
                return split[split.length - 1].toUpperCase();
              })(attachmentsAttachedFileName.trim())
            }}
            files cannot be previewed.<br />
            Please download the file and view it instead.
          </span>
          <button @click="downloadAttachment">
            <img src="https://dsmlib.logistics.systems/Icons/Download.png" alt="Download icon" />
            Download
          </button>
        </div>
      </div>
      <div class="close-button" @click="closeProjector"> &times; </div>
    </div>

    <PopupEntry
      v-if="vehicleTodoPopupShow"
      :show.sync="vehicleTodoPopupShow"
      title="Vehicle Tasks"
      :viewMode="true"
      cancelButton="Close"
    >
      <VehicleTodoList mode="fleet" class="vehicle-todo-popup-list" />
    </PopupEntry>

    <PopupEntry
      v-if="batchAvailabilityPopupShow"
      :show="batchAvailabilityPopupShow"
      @update:show="closeBatchAvailabilityPopup"
      title="Mark Vehicle Availability"
      :viewMode="true"
      cancelButton="Cancel"
    >
        <div class="batch-availability-popup-body">
          <div class="left-menu-container">
            <Menus
              :menu="batchAvailabilityMenuButtons"
              icon="https://dsmlib.logistics.systems/Icons/BlackAndWhiteSteeringWheel.png"
              @menuClicked="batchAvailabilityMenuClicked"
            />
          </div>
          <div class="right-tablebox-container">
            <TableBox
              :selectedRow.sync="batchAvailabilityTableBoxSelectedRow"
              title="Vehicle"
              :loading="vehicleTableBoxIsLoading"
              :tableData="batchAvailabilityTableData"
              :dataType="batchAvailabilityTableDataType"
              :maxSelection="-1"
              :showClearSelected="true"
            />
          </div>
        </div>
    </PopupEntry>

    <PopupV2
      :show="batchAvailabilityActionPopupShow"
      :title="batchAvailabilityPopupMode === 'A' ? 'Mark Available' : 'Mark Unavailable'"
      width="620px"
      :buttons="batchAvailabilityActionPopupButtons"
      @buttonClicked="batchAvailabilityActionPopupButtonClicked"
    >
      <div class="batch-availability-action-popup-content">
        <div>
          {{ batchAvailabilityPopupMode === 'A' ? 'Are you sure you want to mark the selected vehicles as available?' : 'Please enter a remark to mark the selected vehicles as unavailable.' }}
        </div>
        <ul class="batch-availability-selected-vehicles">
          <li
            v-for="vehicle in batchAvailabilityTableBoxSelectedRow"
            :key="vehicle['Inventory #']"
          >
            {{ vehicle['Inventory #'] }}{{ vehicle['Inventory Code'] ? ` - ${vehicle['Inventory Code']}` : '' }}
          </li>
        </ul>
        <Input
          v-if="batchAvailabilityPopupMode === 'U'"
          placeholder="Remarks"
          :isRequired="true"
          :inputValue.sync="batchAvailabilityRemarkInputValue"
          :errorMessage.sync="batchAvailabilityRemarkInputErrorMessage"
          validator="text,150"
        />
      </div>
    </PopupV2>
  </div>
</template>

<script>
/* eslint-disable */
import TyreGrid from '@/components/TyreGrid.vue';
import VehicleTodoList from '@/components/VehicleTodo/VehicleTodoList.vue';
export default {
  components: {
    TyreGrid,
    VehicleTodoList,
  },
  computed: {
    canResetVehicleMileage() {
      return this.mileageAccess !== null && !this.mileageAccess.some((row) => row.Results != null || String(row.Control || '').trim().toLowerCase() === 'cmdreset');
    },
    mileageHasChanges() {
      return Number(this.currentMileageInputValue) !== Number(this.vehicleDetails.RawMeterReading ?? this.vehicleDetails.MeterReading ?? 0);
    },
    renewalInfoTableBoxDataFiltered: function () {
      //remove deleted items
      let filteredAndSorted = this.renewalInfoTableBoxData.filter((renewal) => {
        return renewal['_policyAction'] != 'Delete';
      });

      // Group by Type
      const groupedByType = {};
      for (const item of filteredAndSorted) {
        if (!groupedByType[item.Type]) {
          groupedByType[item.Type] = [];
        }
        groupedByType[item.Type].push(item);
      }

      // For each type, sort by "Date Updated" (descending, latest first)
      Object.keys(groupedByType).forEach((type) => {
        groupedByType[type].sort((a, b) => {
          const dateA = a['Date Updated'] ? new Date(a['Date Updated']) : new Date(0);
          const dateB = b['Date Updated'] ? new Date(b['Date Updated']) : new Date(0);
          return dateB - dateA;
        });
      });

      Object.keys(groupedByType).forEach((type) => {
        groupedByType[type].forEach((item, index) => {
          if (index > 0) {
            item.isOld = true;
          }
        });
      });

      // Flatten the grouped array, preserving the order of types
      let result = [];
      Object.keys(groupedByType)
        .sort()
        .forEach((type) => {
          if (!this.renewalInfoTableShowHistoryCheckbox) {
            for (let i = 0; i < groupedByType[type].length; i++) {
              const row = groupedByType[type][i];
              if (row['Date Updated'] === '') {
                result.push(row); // push all
              } else {
                result.push(row); // only push the first one;
                break;
              }
            }
          } else {
            result.push(...groupedByType[type]);
          }
        });
      if (this.renewalInfoTableShowHistoryCheckbox)
        result = result.map((item, index) => {
          return {
            ...item,
            Type:
              item.isOld ?
                `${item.Type} <img width="12px" src="https://dsmlib.logistics.systems/Icons/History_record.svg" style="margin-left: auto;" />`
              : item.Type,
          };
        });

      result = result.map((item, index) => {
        return { ...item, '#': index + 1 };
      });

      return [...result];
    },
    eventTableDataFiltered() {
      const eventArray = Array.isArray(this.eventTableData) ? this.eventTableData : [this.eventTableData];

      const filter = eventArray.map((item, index) => {
        const {
          DateCreated,
          Status,
          StatusDescription,
          Destination,
          Area,
          ConsignmentNo,
          DriverCode,
          MovementType,
          Remarks,
          Detained,
          UserID,
          DateUpdated,
          TimeUpdated,
        } = item;
        let dateUpdated = '';
        if (DateUpdated && TimeUpdated) {
          const datePart = DateUpdated.substring(0, 10);
          const timePart = `${TimeUpdated.substring(0, 2)}:${TimeUpdated.substring(2, 4)}`;
          dateUpdated = new Date(`${datePart}T${timePart}:00`);
        }
        return {
          '#': index + 1,
          'Event Date': DateCreated ? new Date(DateCreated) : null,
          'Event Type': Status ? `[${Status}] ${StatusDescription}` : '',
          Destination: Destination ?? '',
          Area: Area ?? '',
          'Consignment No': ConsignmentNo ?? '',
          Driver: DriverCode ?? '',
          'Movement Type': MovementType ?? '',
          Remarks: Remarks ?? '',
          'TR Detained?': Detained == '1' ? 'Yes' : 'No',
          'Updated By': UserID ?? '',
          'Date Updated': dateUpdated,
        };
      });
      return filter;
    },
    effectiveDateMaxDate: function () {
      // Effective Date cannot be later than Expiry Date
      for (const control of this.renewalInfoControls[this.policyTypeDropdownSelectedItem['PolicyType']]) {
        if (control['placeholder'] == 'Expiry Date') {
          // find the field position for Expiry Date
          return this.renewalInfoInputData[control['#']]['value'] || null; // return Expiry Date to be used as maxDate for Effective Date
        }
      }

      return null;
    },
    expiryDateMinDate: function () {
      // Expiry Date cannot be earlier than Expiry date
      for (const control of this.renewalInfoControls[this.policyTypeDropdownSelectedItem['PolicyType']]) {
        if (control['placeholder'] == 'Effective Date') {
          // find the field position for Effective Date
          return this.renewalInfoInputData[control['#']]['value'] || null; // return Effective Date to be used as minDate for Expiry Date
        }
      }

      return null;
    },
    effectiveDateExpiryDateDifference: function () {
      // there is an input field in the renewal info popup called Duration (Months)
      let effectiveDatePosition = null; // this field should always be disabled
      let expiryDatePosition = null; // and should always show the difference between effective date and expiry date in months
      // it is not for user input, but rather just to display the difference

      for (const control of this.renewalInfoControls[this.policyTypeDropdownSelectedItem['PolicyType']]) {
        switch (
          control['placeholder'] // find the field positions for Effective Date and Expiry Date
        ) {
          case 'Effective Date':
            effectiveDatePosition = control['#'];
            break;

          case 'Expiry Date':
            expiryDatePosition = control['#'];
            break;
        }
      }

      if (
        !effectiveDatePosition || // there is no Effective Date control
        !(this.renewalInfoInputData[effectiveDatePosition]['value'] instanceof Date) || // value in Effective Date value is not a Date
        !expiryDatePosition || // there is no Expiry Date control
        !(this.renewalInfoInputData[expiryDatePosition]['value'] instanceof Date) // value in Expiry Date control field is not a date
      ) {
        return '';
      }

      return this.calculateEffectiveDateExpiryDateDifference(
        this.renewalInfoInputData[effectiveDatePosition]['value'],
        this.renewalInfoInputData[expiryDatePosition]['value'],
      );

      if (
        this.renewalInfoInputData[effectiveDatePosition]['value'].getFullYear() == // same year
        this.renewalInfoInputData[expiryDatePosition]['value'].getFullYear()
      ) {
        return (
          this.renewalInfoInputData[expiryDatePosition]['value'].getMonth() -
          this.renewalInfoInputData[effectiveDatePosition]['value'].getMonth() +
          1
        ).toString();
      } else if (
        this.renewalInfoInputData[expiryDatePosition]['value'].getFullYear() -
          this.renewalInfoInputData[effectiveDatePosition]['value'].getFullYear() ==
        1 // 1 year gap
      ) {
        return (
          12 -
          this.renewalInfoInputData[effectiveDatePosition]['value'].getMonth() +
          1 +
          this.renewalInfoInputData[expiryDatePosition]['value'].getMonth()
        ).toString();
      } else {
        // more than 1 year difference
        return (
          12 -
          this.renewalInfoInputData[effectiveDatePosition]['value'].getMonth() +
          1 +
          (this.renewalInfoInputData[expiryDatePosition]['value'].getFullYear() -
            this.renewalInfoInputData[effectiveDatePosition]['value'].getFullYear() -
            1) *
            12 +
          this.renewalInfoInputData[expiryDatePosition]['value'].getMonth()
        ).toString();
      }
    },
    maintenanceTableBoxDataWithoutDeleted: function () {
      return this.maintenanceTableBoxData.filter((service) => {
        return service['_serviceAction'] != 'Delete';
      });
    },
    tyreConfigurationTableBoxDataWithoutDeleted: function () {
      return this.tyreConfigurationTableBoxData.filter((tyre) => {
        return tyre['_tyreAction'] != 'Delete';
      });
    },
    tyreConfigurationTableBoxDisplayData: function () {
      return [...this.tyreConfigurationTableBoxDataWithoutDeleted]
        .map((tyre, index) => {
          const displayTyre = { ...tyre };
          if (String(tyre['Position'] ?? '').trim()) return displayTyre;
          const layoutTyre = this.tyreGridTyreLayout.find((item) => String(item.index) == String(tyre.TyreIndex));
          return layoutTyre?.description ? { ...displayTyre, Position: layoutTyre.description } : displayTyre;
        })        
        .sort((left, right) => Number(left.Position) - Number(right.Position))
        .map((tyre, index) => {
          return{ ...tyre, '#': index + 1 };
        })        

;
    },
    tyreTypeListByGroup: function () {
      return this.tyreTypeList.filter((item) => {
        return item?.['Part Group']?.toLowerCase().includes('tyre');
      });
    },
    tyreTypeListActive: function () {
      const activeTyres = this.tyreTypeListByGroup.filter((item) => item.Active == '1');

      if (
        this.tyreTypeSelectedItem &&
        !activeTyres.some((item) => item['Part Type'] == this.tyreTypeSelectedItem['Part Type'])
      ) {
        return [this.tyreTypeSelectedItem, ...activeTyres];
      }

      return activeTyres;
    },
    attachmentsTableBoxDataWithoutDeleted: function () {
      return this.attachmentsTableBoxData.filter((attachment) => {
        return attachment['_attachmentAction'] != 'Delete';
      });
    },
    taskListTableBoxDataWithoutDeleted: function () {
      return this.taskListTableBoxData.filter((task) => {
        return task['_taskAction'] != 'Delete';
      });
    },
    attachmentIsProjectable: function () {
      if (
        !this.attachmentProjectorFileName ||
        !this.attachmentProjectorType // if either of these are blank
      ) {
        return false;
      }

      const filenameSplit = this.attachmentProjectorFileName.trim().split('.');
      const fileExtension = filenameSplit[filenameSplit.length - 1];
      const projectableExtensions = ['pdf', 'txt', 'json', 'mp3', 'wav', 'mp4', 'webm', 'ogg'];

      return this.attachmentProjectorType.startsWith('image/') || projectableExtensions.includes(fileExtension);
    },
    batchAvailabilityTableData: function () {
      return (Array.isArray(this.vehicleTableBoxData) ? this.vehicleTableBoxData : []).map((vehicle, index) => {
        const availabilityValue = vehicle['Availability'];
        return {
          '#': index + 1,
          'Inventory #': vehicle['Inventory #'] ?? '',
          'Inventory Code': vehicle['Inventory Code'] ?? '',
          'Vehicle Category': vehicle['Category'] ?? '',
          Team: vehicle['Team'] ?? '',
          Availability:
            availabilityValue === '1' || availabilityValue === 1 || availabilityValue === 'Y' || availabilityValue === true ? 'Yes'
            : availabilityValue === '0' || availabilityValue === 0 || availabilityValue === 'N' || availabilityValue === false ? 'No'
            : availabilityValue || '',
          Remark: vehicle['Remark1'] ?? '',
        };
      });
    },
    batchAvailabilityTableDataType: function () {
      return {
        '#': 'number',
        'Inventory #': 'string',
        'Inventory Code': 'string',
        'Vehicle Category': 'string',
        Team: 'string',
        Availability: 'string',
        Remark: 'string',
      };
    },
    batchAvailabilityActionPopupButtons: function () {
      return [
        {
          text: this.batchAvailabilityPopupMode === 'A' ? 'Mark Available' : 'Mark Unavailable',
          color: this.batchAvailabilityPopupMode === 'A' ? '' : 'red',
          textColor: this.batchAvailabilityPopupMode === 'A' ? 'black' : 'white',
        },
        { text: 'Cancel', color: 'lightgray' },
      ];
    },
    pmToDriverAvailablePMItems: function () {
      const selectedRow = this.pmToDriverTableBoxSelectedRow[0] || {};
      const editingVehicle = this.pmToDriverAction === 'Edit' ? String(selectedRow['Vehicle'] || '').trim().toUpperCase() : '';
      const assignedVehicles = new Set(
        (this.pmToDriverTableBoxData || [])
          .map((row) =>
            String(row['Vehicle'] || '').trim().toUpperCase(),
          )
          .filter((vehicleCode) => {
            return vehicleCode && vehicleCode !== editingVehicle;
          }),
      );

      return (this.pmToDriverPMDropdownItems || []).filter((vehicle) => {
        const vehicleCode = String(vehicle['VehicleCode'] || '').trim().toUpperCase();
        return vehicleCode && !assignedVehicles.has(vehicleCode);
      });
    },
    pmToDriverAvailableDriver1Items: function () {
      const selectedRow = this.pmToDriverTableBoxSelectedRow[0] || {};
      const editingVehicle = this.pmToDriverAction === 'Edit' ? String(selectedRow['Vehicle'] || '').trim().toUpperCase() : '';
      const assignedDrivers = new Set();

      (this.pmToDriverTableBoxData || []).forEach((row) => {
        const rowVehicle = String(row['Vehicle'] || '').trim().toUpperCase();

        if (editingVehicle && rowVehicle === editingVehicle) {
          return;
        }

        const driver1 = String(row['Driver Code'] || '').trim().toUpperCase();
        const driver2 = String(row['Driver 2 Code'] || '').trim().toUpperCase();

        if (driver1) assignedDrivers.add(driver1);
        if (driver2) assignedDrivers.add(driver2);
      });

      const selectedDriver2 = String(this.pmToDriverDriver2DropdownSelectedItem?.['Staff_No'] || '').trim().toUpperCase();

      return (this.pmToDriverDriverDropdownItems || []).filter((driver) => {
        const driverCode = String(driver['Staff_No'] || '').trim().toUpperCase();
        return driverCode && !assignedDrivers.has(driverCode) && driverCode !== selectedDriver2;
      });
    },
    pmToDriverAvailableDriver2Items: function () {
      const selectedRow = this.pmToDriverTableBoxSelectedRow[0] || {};
      const editingVehicle = this.pmToDriverAction === 'Edit' ? String(selectedRow['Vehicle'] || '').trim().toUpperCase() : '';
      const assignedDrivers = new Set();

      (this.pmToDriverTableBoxData || []).forEach((row) => {
        const rowVehicle = String(row['Vehicle'] || '').trim().toUpperCase();

        if (editingVehicle && rowVehicle === editingVehicle) {
          return;
        }

        const driver1 = String(row['Driver Code'] || '').trim().toUpperCase();
        const driver2 = String(row['Driver 2 Code'] || '').trim().toUpperCase();

        if (driver1) assignedDrivers.add(driver1);
        if (driver2) assignedDrivers.add(driver2);
      });

      const selectedDriver1 = String(this.pmToDriverDriver1DropdownSelectedItem?.['Staff_No'] || '').trim().toUpperCase();

      return (this.pmToDriverDriverDropdownItems || []).filter((driver) => {
        const driverCode = String(driver['Staff_No'] || '').trim().toUpperCase();
        return driverCode && !assignedDrivers.has(driverCode) && driverCode !== selectedDriver1;
      });
    },
  },
  data: function () {
    return {
      userDetails: null,
      mileageAccess: null,
      currentMileageInputValue: '',
      currentMileageInputErrorMessage: '',
      mileageIsSaving: false,
      menuButtons: [
        { title: 'New Vehicle', icon: 'https://dsmlib.logistics.systems/Icons/icons8-add-new-50.png' },
        { title: 'Edit Vehicle', icon: 'https://dsmlib.logistics.systems/Icons/icons8-edit-64%20(1).png' },
        { title: 'Delete Vehicle', icon: 'https://dsmlib.logistics.systems/Icons/icons8-reduce-50.png' },
        { title: 'View Vehicle', icon: 'https://dsmlib.logistics.systems/Icons/icons8-view-64.png' },
        { title: 'Tasks', icon: 'https://dsmlib.logistics.systems/Icons/icons8-todo-list-64.png' },
        { title: 'Mark Availability', icon: 'https://dsmlib.logistics.systems/Icons/BlackAndWhiteSteeringWheel.png' },
        { title: 'PM to Driver', icon: 'https://dsmlib.logistics.systems/Icons/byroad.png' },
        { title: 'Team Management', icon: 'https://dsmlib.logistics.systems/Icons/team.png' },
      ],

      vehicleTableBoxData: [],
      vehicleTableBoxSelectedRow: [],
      vehicleTableBoxIsLoading: false,
      vehicleTableBoxDataType: {
        Year: 'number',
        'Purchase Date': 'date',
        'Weight(KG)': 'number',
        'Width(Ft)': 'number',
        'Length(Ft)': 'number',
        'Height(Ft)': 'number',
        'Max Capacity(M3)': 'number',
        'Winch Capacity(M3)': 'number',
        'Ground Clearance': 'number',
        Price: 'number', // convert to currency
        'Opening Mileage': 'number',
      },

      vehicleDetails: {}, // stores all vehicle details (populated by getVehicleDetails when edit or view vehicle is clicked)
      vehicleTodoDraftItems: [],
      vehicleTodoPopupShow: false,

      vehicleAction: '', // New | Edit | Delete | View (to indicate what action the user has chosen)
      showPopupEntry: false,
      popupEntryTitle: '',
      popupEntryCreatedBy: '',
      popupEntryUpdatedBy: '',
      popupEntryRequiredError: [],
      popupEntrySuccessMessages: [],
      popupEntryErrorMessages: [],
      popupEntryInViewMode: false,

      availabilityRadioButtons: [
        { label: 'Yes', value: '1' },
        { label: 'No', value: '0' },
      ],
      availabilityRadioValue: null,
      availabilityRadioErrorMessage: '',
      availabilityRadioIsDisabled: false,

      unavailabilityRemarkInputValue: '',
      unavailabilityRemarkInputErrorMessage: '',
      unavailabilityRemarkInputIsDisabled: true,

      unavailabilityRemarkLabelValue: '',

      inventoryNoInputValue: '',
      inventoryNoInputErrorMessage: '',
      inventoryNoInputIsDisabled: false,
      inventoryCodeInputValue: '',
      inventoryCodeInputErrorMessage: '',
      inventoryCodeInputIsDisabled: false,
      activeCheckBoxIsChecked: false,
      activeCheckBoxIsDisabled: false,

      tabItems: [
        { title: 'General' },
        { title: 'Engine Information' },
        { title: 'Event' },
        { title: 'Renewal Info' },
        { title: 'Maintenance' },
        { title: 'Tasks' },
        { title: 'Tyre Configuration' },
        { title: 'Attachment' },
      ],
      selectedTabIndex: 0,

      errorMapping: [
        { key: 'vehiclecategory', tab: 0 },
        { key: 'year', tab: 0 },
        { key: 'colour', tab: 0 },
        { key: 'netweight', tab: 0 },
        { key: 'lorry_width', tab: 0 },
        { key: 'lorry_length', tab: 0 },
        { key: 'height', tab: 0 },
        { key: 'maxcapacity', tab: 0 },
        { key: 'winchcapacity', tab: 0 },
        { key: 'groundclearance', tab: 0 },
        { key: 'lorry_model', tab: 0 },
        { key: 'company_code', tab: 0 },
        { key: 'price', tab: 0 },
        { key: 'financeby', tab: 0 },
        { key: 'regcardno', tab: 0 },
        { key: 'regplace', tab: 0 },
        { key: 'openingmeter', tab: 0 },

        { key: 'engineno', tab: 1 },
        { key: 'chassisno', tab: 1 },
        { key: 'turbochargerno', tab: 1 },
        { key: 'fuel', tab: 1 },
        { key: 'horsepower', tab: 1 },
        { key: 'enginemodel', tab: 1 },
        { key: 'bdm', tab: 1 },
        { key: 'btt', tab: 1 },
        { key: 'kerbweight', tab: 1 },
        { key: 'btm', tab: 1 },
        { key: 'bg1', tab: 1 },
        { key: 'bg2', tab: 1 },
        { key: 'bg3', tab: 1 },
        { key: 'bg4', tab: 1 },
        { key: 'bg5', tab: 1 },
        { key: 'bg6', tab: 1 },
        { key: 'bg7', tab: 1 },
        { key: 'bg8', tab: 1 },
        { key: 'bg9', tab: 1 },
        { key: 'bg10', tab: 1 },
        { key: 'bg11', tab: 1 },
        { key: 'bg12', tab: 1 },
      ],

      //regex format
      integerRegex: {
        regex: /\d/g,
        live: true,
        autoClear: false,
      },

      decimalRegex: {
        regex: /\d*\.?\d*/g,
        live: true,
        autoClear: false,
      },

      //#region general tab data fields
      categoryDropdownItems: [],
      categoryDropdownSelectedItem: null,
      categoryDropdownErrorMessage: '',
      categoryDropdownIsDisabled: false,

      yearInputValue: '',
      yearInputErrorMessage: '',
      yearInputIsDisabled: false,

      datePurchaseDateTimePickerValue: null,
      datePurchaseDateTimePickerErrorMessage: '',
      datePurchaseDateTimePickerIsDisabled: false,

      colourInputValue: '',
      colourInputErrorMessage: '',
      colourInputIsDisabled: false,

      weightInputValue: '',
      weightInputErrorMessage: '',
      weightInputIsDisabled: false,

      widthInputValue: '',
      widthInputErrorMessage: '',
      widthInputIsDisabled: false,

      lengthInputValue: '',
      lengthInputErrorMessage: '',
      lengthInputIsDisabled: false,

      heightInputValue: '',
      heightInputErrorMessage: '',
      heightInputIsDisabled: false,

      maxCapacityInputValue: '',
      maxCapacityInputErrorMessage: '',
      maxCapacityInputIsDisabled: false,

      winchCapacityInputValue: '',
      winchCapacityInputErrorMessage: '',
      winchCapacityInputIsDisabled: false,

      groundClearanceInputValue: '',
      groundClearanceInputErrorMessage: '',
      groundClearanceInputIsDisabled: false,

      depreciationTypeDropdownItems: [],
      depreciationTypeDropdownSelectedItem: null,
      depreciationTypeDropdownIsDisabled: false,

      vehicleModelInputValue: '',
      vehicleModelInputErrorMessage: '',
      vehicleModelInputIsDisabled: false,

      trailerInputQueryItems: [],
      trailerInputQuerySelectedObject: null,
      trailerInputQueryIsDisabled: false,

      vehicleMadeInDropdownItems: [],
      vehicleMadeInDropdownSelectedItem: null,
      vehicleMadeInDropdownIsDisabled: false,

      companyDropdownItems: [],
      companyDropdownSelectedItem: null,
      companyDropdownErrorMessage: '',
      companyDropdownIsDisabled: false,

      ownershipDropdownItems: [],
      ownershipDropdownSelectedItem: null,
      ownershipDropdownIsDisabled: false,

      supplierInputQueryItems: [],
      supplierInputQuerySelectedObject: null,
      supplierInputQueryIsDisabled: false,

      priceInputValue: '',
      priceInputErrorMessage: '',
      priceInputIsDisabled: false,

      loanFromInputValue: '',
      loanFromInputErrorMessage: '',
      loanFromInputIsDisabled: false,

      regCardNoInputValue: '',
      regCardNoInputErrorMessage: '',
      regCardNoInputIsDisabled: false,

      regPlaceInputValue: '',
      regPlaceInputErrorMessage: '',
      regPlaceInputIsDisabled: false,

      openingMileageInputValue: '',
      openingMileageInputErrorMessage: '',
      openingMileageInputIsDisabled: false,

      teamDropdownItems: [],
      teamDropdownSelectedItem: null,
      teamDropdownIsDisabled: false,

      sideloaderCheckBoxIsChecked: false,
      sideloaderCheckBoxIsDisabled: false,
      //#endregion

      //#region engine information tab data fields
      engineNoInputValue: '',
      engineNoInputErrorMessage: '',
      engineNoInputIsDisabled: false,

      chassisNoInputValue: '',
      chassisNoInputErrorMessage: '',
      chassisNoInputIsDisabled: false,

      turboChargerNoInputValue: '',
      turboChargerNoInputErrorMessage: '',
      turboChargerNoInputIsDisabled: false,

      fuelDropdownItems: [],
      fuelDropdownSelectedItem: null,
      fuelDropdownIsDisabled: false,

      horsePowerInputValue: '',
      horsePowerInputErrorMessage: '',
      horsePowerInputIsDisabled: false,

      engineModelInputValue: '',
      engineModelInputErrorMessage: '',
      engineModelInputIsDisabled: false,

      BDMInputValue: '',
      BDMInputErrorMessage: '',
      BDMInputIsDisabled: false,

      BTTInputValue: '',
      BTTInputErrorMessage: '',
      BTTInputIsDisabled: false,

      kerbWeightInputValue: '',
      kerbWeightInputErrorMessage: '',
      kerbWeightInputIsDisabled: false,

      BTMInputValue: '',
      BTMInputErrorMessage: '',
      BTMInputIsDisabled: false,

      BG1InputValue: '',
      BG1InputErrorMessage: '',
      BG1InputIsDisabled: false,

      BG2InputValue: '',
      BG2InputErrorMessage: '',
      BG2InputIsDisabled: false,

      BG3InputValue: '',
      BG3InputErrorMessage: '',
      BG3InputIsDisabled: false,

      BG4InputValue: '',
      BG4InputErrorMessage: '',
      BG4InputIsDisabled: false,

      BG5InputValue: '',
      BG5InputErrorMessage: '',
      BG5InputIsDisabled: false,

      BG6InputValue: '',
      BG6InputErrorMessage: '',
      BG6InputIsDisabled: false,

      BG7InputValue: '',
      BG7InputErrorMessage: '',
      BG7InputIsDisabled: false,

      BG8InputValue: '',
      BG8InputErrorMessage: '',
      BG8InputIsDisabled: false,

      BG9InputValue: '',
      BG9InputErrorMessage: '',
      BG9InputIsDisabled: false,

      BG10InputValue: '',
      BG10InputErrorMessage: '',
      BG10InputIsDisabled: false,

      BG11InputValue: '',
      BG11InputErrorMessage: '',
      BG11InputIsDisabled: false,

      BG12InputValue: '',
      BG12InputErrorMessage: '',
      BG12InputIsDisabled: false,
      //#endregion

      //#region renewal info tab data fields
      renewalInfoTableBoxData: [],
      renewalInfoTableBoxSelectedRow: [],
      renewalInfoTableBoxIsLoading: false,
      renewalInfoTableBoxDataType: {
        'Effective Date': 'date',
        ExpiryDate: 'date',
        Duration: 'number',
        Amount: 'currency',
        NCB: 'number',
        'Sum Insured': 'currency',
        'Date Updated': 'date',
      },
      renewalInfoTableBoxHiddenColumns: [
        'User ID',
        'Inventory No',
        'SeqNo',
        'InventoryNo',
        '_policyAction',
        '_policyType',
        '_renewalInfoInputData',
        '_requestBody', //what is this for?
        'isOld',
      ],

      renewalInfoTableShowHistoryCheckbox: false,
      //#endregion

      //#region maintenance tab data fields
      maintenanceTableBoxData: [],
      maintenanceTableBoxSelectedRow: [],
      maintenanceTableBoxIsLoading: false,
      maintenanceTableBoxDataType: {
        Mileage: 'number',
        Duration: 'number',
        ExpireDate: 'date',
      },
      maintenanceTableBoxHiddenColumns: [
        '#',
        'AttImage',
        'SeqNo',
        'ServiceCode',
        'Code Description',
        'Mileage',
        'Duration',
        'ExpireDate',
        'Interval',
        'Occurence',
        'InventoryNo',
        '_groupBy',
        '_serviceAction',
        '_file',
      ],
      //#endregion

      //#region tyre configuration tab data fields
      tyreGridTyreLayout: [],
      tyreGridLayoutType: 'LeftRight',
      tyreGridSelectedTyre: null,
      tyreGridSelectedTyreDetails: null,
      tyreGridShowAll: false,
      tyreGridShowSpares: true,
      tyreGridDisplayShowAllCheckbox: true,

      tyreConfigurationTableBoxData: [],
      tyreConfigurationTableBoxSelectedRow: [],
      tyreConfigurationTableBoxIsLoading: false,
      tyreConfigurationTableBoxDataType: {
        '#': 'number',
        Size: 'number',
        'Mfg Date': 'date',
        'Date Change': 'date',
        TyreIndex: 'number',
        'In Use': 'number',
      },
      tyreConfigurationTableBoxHiddenColumns: [
        'InventoryNo',
        'SeqNo',
        'TyreGUID',
        '_tyreAction',
        '_rowNo',
        '_colNo',
      ],

      defaultTyreConfigurations: {}, // cache for results returned from GetVehicleTyreConfiguration API call
      // format:
      // { `${category_code}_${type}`: [DEFAULT_TYRE_CONFIGURATION] }
      //#endregion

      //#region attachments tab data fields
      attachmentsTableBoxData: [],
      attachmentsTableBoxSelectedRow: [],
      attachmentsTableBoxIsLoading: false,
      attachmentsTableBoxDataType: {
        'Updated on': 'date',
      },
      attachmentsTableBoxHiddenColumns: ['Attachment', 'Updated by', 'ID', '_attachmentAction', '_file'],
      //#endregion

      //#region renewal info popup data fields
      showRenewalInfoPopup: false,
      renewalInfoAction: '', // "Add" | "Edit" | "View"

      policyTypeDropdownItems: [
        { type: 'Road Tax' },
        { type: 'Insurance' },
        { type: 'Permit' },
        { type: 'Inspection' },
        { type: 'Plant & Machinery' },
        { type: 'Custom Bonded' },
        { type: 'Special Inspection' },
        { type: 'Usahawan' },
      ],
      policyTypeDropdownSelectedItem: null,
      policyTypeDropdownErrorMessage: '',
      policyTypeDropdownIsDisabled: false,

      renewalInfoControls: {}, // cache for results returned from GetVehicleRenewalOption API call
      // format:
      // { "POLICY_TYPE": [CONTROLS_ARRAY] }

      renewalInfoInputData: {
        // needs to be in a JS object, because you need to parameterise assignment
        1: { value: null, errorMessage: '', isDisabled: false }, // reactivity *SHOULDN'T* be an issue
        2: { value: null, errorMessage: '', isDisabled: false }, // if it is then use this.$set
        3: { value: null, errorMessage: '', isDisabled: false },
        4: { value: null, errorMessage: '', isDisabled: false },
        5: { value: null, errorMessage: '', isDisabled: false },
        6: { value: null, errorMessage: '', isDisabled: false },
        7: { value: null, errorMessage: '', isDisabled: false },
        8: { value: null, errorMessage: '', isDisabled: false },
        9: { value: null, errorMessage: '', isDisabled: false },
        10: { value: null, errorMessage: '', isDisabled: false }, // errorMessage property not used currently, as RemarksBox doesn't have that prop
      },
      //#endregion

      //#region maintenance popup data fields
      showMaintenancePopup: false,
      maintenanceAction: '', // "Add" | "Edit" | "View"

      servicesDropdownItems: [],
      servicesDropdownSelectedItem: null,
      servicesDropdownErrorMessage: '',
      servicesDropdownIsDisabled: false,

      intervalUnitRadioButtons: [
        { label: 'Mileage', checked: false },
        { label: 'Months', checked: false },
        { label: 'Days', checked: false },
        { label: 'Whichever Comes First', checked: true },
      ],
      intervalUnitRadioButtonIsDisabled: false,
      intervalMileageInputValue: '',
      intervalMileageInputIsRequired: true,
      intervalMileageInputErrorMessage: '',
      intervalMileageInputIsDisabled: false,
      intervalDurationInputValue: '',
      intervalDurationInputPlaceholder: 'Duration (Months)',
      intervalDurationInputIsRequired: true,
      intervalDurationInputErrorMessage: '',
      intervalDurationInputIsDisabled: false,

      occurrenceRadioButtons: [
        { label: 'Once Only', checked: true },
        { label: 'Routine until', checked: false },
      ],
      occurrenceRadioButtonIsDisabled: false,
      occurrenceDateTimePickerValue: null,
      occurrenceDateTimePickerIsRequired: false,
      occurrenceDateTimePickerErrorMessage: '',
      occurrenceDateTimePickerIsDisabled: true,

      taskListTableBoxData: [],
      taskListTableBoxSelectedRow: [],
      taskListTableBoxIsLoading: false,
      taskListTableBoxDataType: {}, // all columns are of type string
      taskListTableBoxHiddenColumns: ['_AttImage', '_taskAction', '_file'],

      //#region task popup data fields
      showTaskPopup: false,
      taskAction: '', // "Add" | "Edit"
      taskPopupTitle: '',
      taskPopupButtons: [],

      taskServicesDropdownItems: [],
      taskServicesDropdownSelectedItem: null,
      taskServicesDropdownErrorMessage: '',
      taskServicesDropdownIsDisabled: false,
      taskServicesRemarksBoxValue: '', // moving remarks box
      taskServicesRemarksBoxErrorMessage: '',
      taskServicesRemarksBoxIsDisabled: false,
      taskServicesFileInputIsDisabled: false,
      taskServicesAttachments: [],
      taskServicesAttachedFileName: '',
      //#endregion task popup data fields

      //#region duplicate task popup
      showDuplicateTaskPopup: false,
      duplicateTaskPopupButtons: [{ text: 'OK' }],
      duplicateTaskPopupTaskList: [],
      //#endregion duplicate task popup

      //#endregion maintenance popup data fields

      //#region tyre configuration popup data fields
      showTyreConfigurationPopup: false,
      tyreConfigurationAction: '', // "Add" | "Edit" | "View"

      tyreTypeList: [],
      tyreTypeSelectedItem: null,
      tyreTypeErrorMessage: '',
      tyreTypeIsDisabled: false,

      serialNoInputValue: '',
      serialNoInputErrorMessage: '',
      serialNoInputIsDisabled: false,

      sizeInputValue: '',
      sizeInputErrorMessage: '',
      sizeInputIsDisabled: false,

      mfgDateDateTimePickerValue: null,
      mfgDateDateTimePickerIsDisabled: false,

      dateChangeDateTimePickerValue: null,
      dateChangeDateTimePickerIsDisabled: false,

      tyreModelInputValue: '',
      tyreModelInputErrorMessage: '',
      tyreModelInputIsDisabled: false,

      manufacturerDropdownItems: [],
      manufacturerDropdownSelectedItem: null,
      manufacturerDropdownIsDisabled: false,

      tyreMadeInDropdownItems: [],
      tyreMadeInDropdownSelectedItem: null,
      tyreMadeInDropdownIsDisabled: false,

      tyreConfigurationRemarksBoxValue: '',
      tyreConfigurationRemarksBoxErrorMessage: '',
      tyreConfigurationRemarksBoxIsDisabled: false,
      //#endregion

      //#region attachments popup data fields
      showAttachmentsPopup: false,
      attachmentsAction: '', // "Add" | "Edit" | "View"

      attachmentsPopupTitle: '',
      attachmentsPopupButtons: [],

      attachmentsInputIsRequired: false,
      attachmentsInputErrorMessage: '',
      attachmentsInputMultiFile: false,
      attachmentsAttachedFileName: '',
      attachmentsRemarksBoxValue: '',
      attachmentsRemarksBoxErrorMessage: '',

      attachmentsPopupAttachments: [], // used to store the FileList returned by the file input
      //#endregion

      //#region attachment projector data fields
      showAttachmentProjector: false,
      attachmentProjectorFileName: '',
      attachmentProjectorType: '',
      attachmentProjectorData: '',
      //#endregion

      lengthMapping: [
        { placeholder: 'Reference #', length: 25 },
        { placeholder: 'Issue By', length: 60 },
        { placeholder: 'Agent', length: 60 },
        { placeholder: 'Remark', length: 200 },
        { placeholder: 'File Ref #', length: 25 },
        { placeholder: 'License Ref #', length: 25 },
        { placeholder: 'Registration #', length: 25 },
        { placeholder: 'Manufacturer', length: 60 },
        { placeholder: 'Mfg Ref #', length: 25 },
        { placeholder: 'Bon Am #', length: 25 },
      ],

      renewalDecimalInput: ['NCB (%)', 'Amount (RM)', 'Sum Insured'],

      eventTableData: [],
      eventTableDataType: {
        '#': 'number',
        'Event Date': 'datetime',
        'Event Type': 'string',
        destination: 'string',
        Area: 'string',
        'Consignment No': 'string',
        Driver: 'string',
        'Movement Type': 'string',
        Remarks: 'string',
        'TR Detained?': 'string',
        'Updated By': 'string',
        'Date Updated': 'datetime',
      },
      //#region batch availability popup data fields
      batchAvailabilityPopupShow: false,
      batchAvailabilityPopupMode: 'A',
      batchAvailabilityRemarkInputValue: '',
      batchAvailabilityRemarkInputErrorMessage: '',
      batchAvailabilityTableBoxSelectedRow: [],
      batchAvailabilityActionPopupShow: false,
      batchAvailabilityMenuButtons: [
        { title: 'Mark Available', icon: 'https://dsmlib.logistics.systems/Icons/icons8-add-48.png' },
        { title: 'Mark Unavailable', icon: 'https://dsmlib.logistics.systems/Icons/error.png' },
      ],
      //#endregion

      //#region PM to Driver
      showPMToDriverPopupEntry: false,
      pmToDriverAction: '',
      pmToDriverFieldsAreDisabled: true,

      pmToDriverTableBoxData: [],
      pmToDriverTableBoxSelectedRow: [],
      pmToDriverTableBoxIsLoading: false,

      pmToDriverPMDropdownItems: [],
      pmToDriverPMTeamDropdownItems: [],
      pmToDriverPMDropdownSelectedItem: null,
      pmToDriverPMTeamDropdownSelectedItem: null,

      pmToDriverDriverDropdownItems: [],
      pmToDriverDriver1DropdownSelectedItem: null,
      pmToDriverDriver2DropdownSelectedItem: null,
      pmToDriverHasDriver2: false,
      //#endregion

      //#region Team Management
      pmToDriverTableBoxKey: 0,
      teamManagementAction: '',
      teamManagementEditedTeams: [],
      teamManagementDeletedTeams: [],
      teamManagementForDropdownItems: [
        {
          Description: 'All',
          Module: '',
        },
        {
          Description: 'Prime Movers',
          Module: 'HMS',
        },
        {
          Description: 'Trucks',
          Module: 'TMS',
        },
      ],

      teamManagementForDropdownSelectedItem: null,
      teamManagementAssignedIsLoading: false,

      showTeamManagementPopupEntry: false,
      teamManagementIsLoading: false,

      teamManagementTeamItems: [],
      teamManagementSelectedTeam: null,

      teamManagementAllPMs: [],
      teamManagementAvailablePMs: [],
      teamManagementAssignedPMs: [],

      teamManagementAvailableSelectedRows: [],
      teamManagementAssignedSelectedRows: [],

      teamManagementOriginalTeams: {},

      teamManagementSuccessMessages: [],
      teamManagementErrorMessages: [],

      showTeamManagementAddTeamPanel: false,
      teamManagementAddTeamIsSaving: false,

      newTeamCodeInputValue: '',
      newTeamCodeInputErrorMessage: '',

      newTeamDescriptionInputValue: '',
      newTeamDescriptionInputErrorMessage: '',
      //#endregion
    };
  },
  props: {},
  methods: {
    refreshVehicleData: async function () {
      this.vehicleTableBoxIsLoading = true;
      this.vehicleTableBoxData = [];
      this.vehicleTableBoxSelectedRow = [];
      this.$nextTick(async () => {
        this.vehicleTableBoxData = await this.$apiCallV2('/Master/GetListMaster', { masterfile: 'VEHICLE' });
        this.vehicleTableBoxIsLoading = false;
      });
    },
    getDropdownData: async function () {
      this.categoryDropdownItems = (
        await this.$apiCallV2('/Master/GetListMaster', { masterfile: 'VEHICLECATEGORY' })
      ).map((category) => {
        return {
          ...category,
          'Full Description': `${category['Description'].trim()} - ${category['Type Description'].trim()}`,
        };
      });

      this.depreciationTypeDropdownItems = await this.$apiCallV2('/Master/GetListMaster', {
        masterfile: 'DEPRECIATIONTYPE',
      });

      this.trailerInputQueryItems = await this.$apiCallV2('/Master/GetListMaster', { masterfile: 'TRAILER' });

      this.vehicleMadeInDropdownItems = await this.$apiCallV2('/Master/GetListMaster', { masterfile: 'COUNTRY' });
      this.tyreMadeInDropdownItems = this.vehicleMadeInDropdownItems; // both dropdowns use the same masterfile

      this.companyDropdownItems = await this.$apiCallV2('/Master/GetListMaster', {
        masterfile: 'COMPANY',
        module: 'VEHICLE',
      });

      this.ownershipDropdownItems = await this.$apiCallV2('/Master/GetListMaster', { masterfile: 'VEHICLEOWNERSHIP' });

      this.supplierInputQueryItems = await this.$apiCallV2('/Master/GetListMaster', { masterfile: 'SUPPLIER' });

      this.teamDropdownItems = await this.$apiCallV2('/Master/GetListMaster', { masterfile: 'TEAM' });

      this.fuelDropdownItems = await this.$apiCallV2('/Master/GetListMaster', { masterfile: 'FUELTYPE' });

      this.policyTypeDropdownItems = await this.$apiCallV2('/Master/GetListMaster', { masterfile: 'POLICYTYPE' });

      this.servicesDropdownItems = await this.$apiCallV2('/Master/GetListMaster', { masterfile: 'SERVICES' });

      this.taskServicesDropdownItems = await this.$apiCallV2('/Master/GetListMaster', { masterfile: 'SERVICESTASK' });

      this.manufacturerDropdownItems = await this.$apiCallV2('/Master/GetListMaster', { masterfile: 'MANUFACTURER' });

      const partTypes = await this.$apiCallV2('/Master/GetListMaster', {
        masterfile: 'PARTTYPEWITHPRODUCTINFO',
      });
      this.tyreTypeList = partTypes.map((item) => {
        const { 'Part Type': PartType, Description, 'Part Group': PartGroup, ProdName, Size, Shade } = item;

        const partTypeLabel = `${PartType}${PartGroup ? ` [${PartGroup}]` : ''}`;
        const infoArray = [];
        if (ProdName && ProdName != Description) infoArray.push(ProdName);
        if (Size && Size != '-') infoArray.push(Size);
        if (Shade && Shade != '-') infoArray.push(Shade);
        const infoLabel = infoArray.length > 0 ? `[${infoArray.join('|')}]` : '';
        const descriptionLabel = `${Description} ${infoLabel}`;

        return {
          ...item,
          isExpense: item.IsExpenses,
          'Is Expense': ['Y', 'YES', '1', 'TRUE'].includes(String(item.IsExpenses ?? '').trim().toUpperCase())
            ? 'Yes'
            : 'No',
          label: `${partTypeLabel} - ${descriptionLabel}`,
        };
      });
    },
    getVehicleDetails: async function (inventoryNo) {
      // gets all details for a particular vehicle and formats them accordingly
      this.vehicleDetails = (await this.$apiCallV2(`/Vehicle/GetDetailsMaster?recordid=${inventoryNo}`))['Vehicle'];

      if (this.vehicleDetails['Policy']) {
        // if there exists renewals for this vehicle
        if (!Array.isArray(this.vehicleDetails['Policy'])) {
          // if there is only one renewal for this vehicle
          this.vehicleDetails['Policy'] = [this.vehicleDetails['Policy']]; // wrap it in an array
        }

        this.vehicleDetails['Policy'] = this.vehicleDetails['Policy'].map((policy) => {
          // calculate duration for each policy
          if (!policy['Effective Date'] || !policy['ExpiryDate']) {
            // if either one is empty
            return policy; // don't modify policy
          } else {
            // if both of them have values
            return {
              ...policy, // calculate duration and return
              Duration: this.calculateEffectiveDateExpiryDateDifference(
                new Date(policy['Effective Date']),
                new Date(policy['ExpiryDate']),
              ),
            };
          }
        });
      }

      if (this.vehicleDetails['Service']) {
        // if there is at least one service for this vehicle
        if (!Array.isArray(this.vehicleDetails['Service'])) {
          // if there is only one service
          this.vehicleDetails['Service'] = [this.vehicleDetails['Service']]; // wrap it in an array
        }

        this.vehicleDetails['Service'] = this.vehicleDetails['Service'].map((service) => {
          service['_groupBy'] = this.generateMaintenanceGroupByField(service); // generate groupby string for every service
          return service;
        });
      }

      if (this.vehicleDetails['Tyre'] && !Array.isArray(this.vehicleDetails['Tyre'])) {
        // if there is only one saved tyre for this vehicle
        this.vehicleDetails['Tyre'] = [this.vehicleDetails['Tyre']]; // wrap it in an array
      }

      const attachment = await this.$apiCallV2(`/Vehicle/GetMasterFileAttachments?refno=${inventoryNo}`);
      if (attachment.length > 0) {
        // if there are attachments for this vehicle
        this.vehicleDetails['Attachment'] = attachment;
      }

      this.eventTableData = this.vehicleDetails?.Event ?? [];
    },
    async getMileageAccess() {
      try {
        const access = await this.$apiCallV2('/SASControl/GetSASControlAccess?system=EMS');
        this.mileageAccess = Array.isArray(access) ? access : null;
      } catch (error) { this.mileageAccess = null; }
    },
    async resetVehicleMileage() {
      if (!this.canResetVehicleMileage || this.vehicleAction === 'New' || !this.vehicleDetails.Lorry_No || this.mileageIsSaving) return;
      this.currentMileageInputErrorMessage = '';
      const mileage = String(this.currentMileageInputValue ?? '').trim();
      if (!/^[0-9]{1,7}$/.test(mileage)) {
        this.currentMileageInputErrorMessage = 'Mileage must be a whole number from 0 to 9999999.';
        return;
      }
      if (!this.mileageHasChanges) return;
      const inventoryNo = this.vehicleDetails.Lorry_No;
      this.mileageIsSaving = true;
      try {
        const result = (await this.$apiCallV2('/Vehicle/ResetVehicleMileage', {
          inventoryno: inventoryNo,
          meterreading: mileage,
          expectedmeterreading: this.vehicleDetails.RawMeterReading ?? (this.vehicleDetails.MeterReading || 0),
        }))?.[0];
        if (this.vehicleAction === 'New' || this.vehicleDetails.Lorry_No !== inventoryNo) return;
        if (!result || String(result.Results) !== '0') {
          this.currentMileageInputErrorMessage = result?.ErrMsg || 'Unable to update vehicle mileage.';
          return;
        }
        this.$set(this.vehicleDetails, 'MeterReading', String(result.MeterReading));
        this.$set(this.vehicleDetails, 'RawMeterReading', result.MeterReading);
        this.currentMileageInputValue = String(result.MeterReading);
        this.popupEntrySuccessMessages = ['Mileage updated.'];
      } catch (error) {
        if (this.vehicleDetails.Lorry_No === inventoryNo) this.currentMileageInputErrorMessage = error.message || 'Unable to update vehicle mileage.';
      } finally { this.mileageIsSaving = false; }
    },
    populatePopupEntryFields: async function () {
      // only populates fields in the PopupEntry, does not populate fields in additional popups
      // additional popups are populated and cleared by their respective call to action buttons

      this.popupEntryCreatedBy = this.vehicleDetails['UserCreate'];
      this.popupEntryUpdatedBy = this.vehicleDetails['DateCreated'];

      this.availabilityRadioValue =
        this.availabilityRadioButtons.find((radio) => radio['value'] === this.vehicleDetails['Availability']) ?? null;

      this.unavailabilityRemarkInputIsDisabled = this.availabilityRadioValue?.['value'] !== '1'; // if the vehicle is available, disable the unavailability remark input
      this.unavailabilityRemarkLabelValue = this.vehicleDetails['Remark1'];

      this.inventoryNoInputValue = this.vehicleDetails['Lorry_No'];
      this.inventoryCodeInputValue = this.vehicleDetails['VehicleCode'];
      this.currentMileageInputValue = String(this.vehicleDetails.RawMeterReading ?? (this.vehicleDetails.MeterReading || 0));
      this.currentMileageInputErrorMessage = '';
      this.activeCheckBoxIsChecked = this.vehicleDetails['active'] == '1';

      //#region general tab
      const normalizeCategoryValue = (value) => String(value ?? '').trim().toUpperCase();

      this.categoryDropdownSelectedItem =
        this.categoryDropdownItems.find(
          (category) =>
            [category['Category Code'], category['Description']].some(
              (value) => normalizeCategoryValue(value) == normalizeCategoryValue(this.vehicleDetails['VehicleCategory']),
            ) &&
            [category['Type'], category['Type Description']].some(
              (value) => normalizeCategoryValue(value) == normalizeCategoryValue(this.vehicleDetails['Type']),
            ),
        ) ??
        this.categoryDropdownItems.find(
          (category) =>
            normalizeCategoryValue(category['Description']) ==
              normalizeCategoryValue(this.vehicleTableBoxSelectedRow[0]?.['Category']) &&
            normalizeCategoryValue(category['Type Description']) ==
              normalizeCategoryValue(this.vehicleTableBoxSelectedRow[0]?.['Type']),
        ) ?? null; // create a new object to trigger watcher
      this.yearInputValue = this.vehicleDetails['Year'];
      this.datePurchaseDateTimePickerValue =
        this.vehicleDetails['DatePurchase'] ? new Date(this.vehicleDetails['DatePurchase']) : null;
      this.colourInputValue = this.vehicleDetails['Color'];
      this.weightInputValue = this.vehicleDetails['NetWeight'];
      this.widthInputValue = this.vehicleDetails['Lorry_width'];
      this.lengthInputValue = this.vehicleDetails['Lorry_Length'];
      this.heightInputValue = this.vehicleDetails['Height'];
      this.maxCapacityInputValue = this.vehicleDetails['MaxCapacity'];
      this.winchCapacityInputValue = this.vehicleDetails['WinchCapacity'];
      this.groundClearanceInputValue = this.vehicleDetails['GroundClearance'];
      this.depreciationTypeDropdownSelectedItem =
        this.depreciationTypeDropdownItems.find((depreciationType) => {
          return depreciationType['Type'] == this.vehicleDetails['DepType'];
        }) ?? null;

      this.vehicleModelInputValue = this.vehicleDetails['Lorry_Model'];
      this.trailerInputQuerySelectedObject =
        this.trailerInputQueryItems.find((trailer) => {
          return trailer['Trailer No'] == this.vehicleDetails['Trailer_No'];
        }) ?? null;
      this.vehicleMadeInDropdownSelectedItem =
        this.vehicleMadeInDropdownItems.find((country) => {
          return country['Country Code'] == this.vehicleDetails['Made'];
        }) ?? null;
      this.companyDropdownSelectedItem =
        this.companyDropdownItems.find((company) => {
          return company['Company Code'] == this.vehicleDetails['Company_code'];
        }) ?? null;
      this.ownershipDropdownSelectedItem =
        this.ownershipDropdownItems.find((ownership) => {
          return ownership['Ownership'] == this.vehicleDetails['OwnerShip'];
        }) ?? null;
      this.supplierInputQuerySelectedObject =
        this.supplierInputQueryItems.find((supplier) => {
          return supplier['Supplier Code'] == this.vehicleDetails['Supplier'];
        }) ?? null;
      this.teamDropdownSelectedItem =
        this.teamDropdownItems.find((team) => {
          return team['Team'] == this.vehicleDetails['Team'];
        }) ?? null;
      this.priceInputValue = this.vehicleDetails['Price'];
      this.loanFromInputValue = this.vehicleDetails['FinanceBy'];
      this.regCardNoInputValue = this.vehicleDetails['RegCardNo'];
      this.regPlaceInputValue = this.vehicleDetails['RegPlace'];
      this.openingMileageInputValue = this.vehicleDetails['OpeningMeter'];
      this.sideloaderCheckBoxIsChecked = this.vehicleDetails['SideLoader'] === '1';
      //#endregion

      //#region engine information tab
      this.engineNoInputValue = this.vehicleDetails['EngineNo'];
      this.chassisNoInputValue = this.vehicleDetails['ChassisNo'];
      this.turboChargerNoInputValue = this.vehicleDetails['TurboChargerNo'];
      this.fuelDropdownSelectedItem =
        this.fuelDropdownItems.find((fuel) => {
          return fuel['Type'] == this.vehicleDetails['Fuel'];
        }) ?? null;
      this.horsePowerInputValue = this.vehicleDetails['HorsePower'];
      this.engineModelInputValue = this.vehicleDetails['EngineModel'];
      this.BDMInputValue = this.vehicleDetails['BDM'];
      this.BTTInputValue = this.vehicleDetails['BTT'];
      this.kerbWeightInputValue = this.vehicleDetails['KerbWeight'];
      this.BTMInputValue = this.vehicleDetails['BTM'];
      this.BG1InputValue = this.vehicleDetails['BG1'];
      this.BG2InputValue = this.vehicleDetails['BG2'];
      this.BG3InputValue = this.vehicleDetails['BG3'];
      this.BG4InputValue = this.vehicleDetails['BG4'];
      this.BG5InputValue = this.vehicleDetails['BG5'];
      this.BG6InputValue = this.vehicleDetails['BG6'];
      this.BG7InputValue = this.vehicleDetails['BG7'];
      this.BG8InputValue = this.vehicleDetails['BG8'];
      this.BG9InputValue = this.vehicleDetails['BG9'];
      this.BG10InputValue = this.vehicleDetails['BG10'];
      this.BG11InputValue = this.vehicleDetails['BG11'];
      this.BG12InputValue = this.vehicleDetails['BG12'];
      //#endregion

      //#region renewal into tab
      this.renewalInfoTableBoxData = this.vehicleDetails['Policy'] || [];
      this.renewalInfoTableBoxSelectedRow = [];
      //#endregion

      //#region maintenance tab
      this.maintenanceTableBoxData = this.vehicleDetails['Service'] || [];
      this.maintenanceTableBoxSelectedRow = [];
      //#endregion

      this.vehicleTodoDraftItems = [];

      //#region tyre configuration tab
      this.tyreConfigurationTableBoxData = this.vehicleDetails['Tyre'] || [];
      this.tyreConfigurationTableBoxSelectedRow = [];
      this.tyreGridShowAll = false;
      this.tyreGridSelectedTyre = {};
      await this.updateTyreGridTyreLayout(this.categoryDropdownSelectedItem);
      //#endregion

      //#region attachments tab
      this.attachmentsTableBoxData = this.vehicleDetails['Attachment'] || [];
      //assign temporary id to keep track of selected index for tablebox
      let i = 0;
      for (const a of this.attachmentsTableBoxData) {
        this.attachmentsTableBoxData[i]['ID'] = ++i;
      }
      //#endregion
    },
    clearPopupEntryFields: async function () {
      // clears all PopupEntry fields, does not clear fields in additional popups
      // those are cleared by their respective call to action buttons

      this.popupEntryCreatedBy = '';
      this.popupEntryUpdatedBy = '';
      this.currentMileageInputValue = '';
      this.currentMileageInputErrorMessage = '';

      this.availabilityRadioValue = null;
      this.unavailabilityRemarkInputValue = '';
      this.unavailabilityRemarkLabelValue = '';

      this.unavailabilityRemarkInputIsDisabled = true;
      this.unavailabilityRemarkInputValue = '';
      this.unavailabilityRemarkInputErrorMessage = '';

      this.inventoryNoInputValue = '';
      this.inventoryCodeInputValue = '';

      this.inventoryNoInputErrorMessage = '';
      this.inventoryCodeInputErrorMessage = '';

      //#region general tab
      this.categoryDropdownSelectedItem = null;
      this.yearInputValue = '';
      this.datePurchaseDateTimePickerValue = null;
      this.colourInputValue = '';
      this.weightInputValue = '';
      this.widthInputValue = '';
      this.lengthInputValue = '';
      this.heightInputValue = '';
      this.maxCapacityInputValue = '';
      this.winchCapacityInputValue = '';
      this.groundClearanceInputValue = '';
      this.depreciationTypeDropdownSelectedItem = null;

      this.categoryDropdownErrorMessage = '';
      this.yearInputErrorMessage = '';
      this.datePurchaseDateTimePickerErrorMessage = '';
      this.colourInputErrorMessage = '';
      this.weightInputErrorMessage = '';
      this.widthInputErrorMessage = '';
      this.lengthInputErrorMessage = '';
      this.heightInputErrorMessage = '';
      this.maxCapacityInputErrorMessage = '';
      this.winchCapacityInputErrorMessage = '';
      this.groundClearanceInputErrorMessage = '';

      this.vehicleModelInputValue = '';
      this.trailerInputQuerySelectedObject = null;
      this.vehicleMadeInDropdownSelectedItem = null;
      this.companyDropdownSelectedItem = null;
      this.ownershipDropdownSelectedItem = null;
      this.supplierInputQuerySelectedObject = null;
      this.priceInputValue = '';
      this.loanFromInputValue = '';
      this.regCardNoInputValue = '';
      this.regPlaceInputValue = '';
      this.openingMileageInputValue = '';
      this.teamDropdownSelectedItem = null;
      this.sideloaderCheckBoxIsChecked = false;

      this.vehicleModelInputErrorMessage = '';
      this.companyDropdownErrorMessage = '';
      this.priceInputErrorMessage = '';
      this.loanFromInputErrorMessage = '';
      this.regCardNoInputErrorMessage = '';
      this.regPlaceInputErrorMessage = '';
      this.openingMileageInputErrorMessage = '';
      //#endregion

      //#region engine information
      this.engineNoInputValue = '';
      this.chassisNoInputValue = '';
      this.turboChargerNoInputValue = '';
      this.fuelDropdownSelectedItem = null;
      this.horsePowerInputValue = '';
      this.engineModelInputValue = '';

      this.engineNoInputErrorMessage = '';
      this.chassisNoInputErrorMessage = '';
      this.turboChargerNoInputErrorMessage = '';
      this.horsePowerInputErrorMessage = '';
      this.engineModelInputErrorMessage = '';

      this.BDMInputValue = '';
      this.BTTInputValue = '';
      this.kerbWeightInputValue = '';
      this.BTMInputValue = '';
      this.BG1InputValue = '';
      this.BG2InputValue = '';
      this.BG3InputValue = '';
      this.BG4InputValue = '';
      this.BG5InputValue = '';
      this.BG6InputValue = '';
      this.BG7InputValue = '';
      this.BG8InputValue = '';
      this.BG9InputValue = '';
      this.BG10InputValue = '';
      this.BG11InputValue = '';
      this.BG12InputValue = '';

      this.BDMInputErrorMessage = '';
      this.BTTInputErrorMessage = '';
      this.kerbWeightInputErrorMessage = '';
      this.BTMInputErrorMessage = '';
      this.BG1InputErrorMessage = '';
      this.BG2InputErrorMessage = '';
      this.BG3InputErrorMessage = '';
      this.BG4InputErrorMessage = '';
      this.BG5InputErrorMessage = '';
      this.BG6InputErrorMessage = '';
      this.BG7InputErrorMessage = '';
      this.BG8InputErrorMessage = '';
      this.BG9InputErrorMessage = '';
      this.BG10InputErrorMessage = '';
      this.BG11InputErrorMessage = '';
      this.BG12InputErrorMessage = '';
      //#endregion

      //#region renewal into tab
      this.renewalInfoTableBoxData = [];
      this.renewalInfoTableBoxSelectedRow = [];
      //#endregion

      //#region maintenance tab
      this.maintenanceTableBoxData = [];
      this.maintenanceTableBoxSelectedRow = [];
      //#endregion

      this.vehicleTodoDraftItems = [];

      //#region tyre configuration tab
      this.tyreConfigurationTableBoxData = [];
      this.tyreConfigurationTableBoxSelectedRow = [];
      this.tyreGridShowAll = false;
      this.tyreGridSelectedTyre = null;
      await this.updateTyreGridTyreLayout(this.categoryDropdownSelectedItem);
      //#endregion

      //#region attachments tab
      this.attachmentsTableBoxData = [];
      this.attachmentsTableBoxSelectedRow = [];
      //#endregion

      //#region PopupEntry message box
      this.popupEntryRequiredError = [];
      this.popupEntrySuccessMessages = [];
      this.popupEntryErrorMessages = [];
      //#endregion
    },
    enablePopupEntryFields: function () {
      // enable all PopupEntry fields, does not enable fields in additional popups
      // those are enabled by their respective call to action buttons

      this.availabilityRadioIsDisabled = false;

      this.inventoryNoInputIsDisabled = false;
      this.inventoryCodeInputIsDisabled = false;
      this.activeCheckBoxIsDisabled = false;

      //#region general tab
      this.categoryDropdownIsDisabled = false;
      this.yearInputIsDisabled = false;
      this.datePurchaseDateTimePickerIsDisabled = false;
      this.colourInputIsDisabled = false;
      this.weightInputIsDisabled = false;
      this.widthInputIsDisabled = false;
      this.lengthInputIsDisabled = false;
      this.heightInputIsDisabled = false;
      this.maxCapacityInputIsDisabled = false;
      this.winchCapacityInputIsDisabled = false;
      this.groundClearanceInputIsDisabled = false;
      this.depreciationTypeDropdownIsDisabled = false;

      this.vehicleModelInputIsDisabled = false;
      this.trailerInputQueryIsDisabled = false;
      this.vehicleMadeInDropdownIsDisabled = false;
      this.companyDropdownIsDisabled = false;
      this.ownershipDropdownIsDisabled = false;
      this.supplierInputQueryIsDisabled = false;
      this.priceInputIsDisabled = false;
      this.loanFromInputIsDisabled = false;
      this.regCardNoInputIsDisabled = false;
      this.regPlaceInputIsDisabled = false;
      this.openingMileageInputIsDisabled = false;
      this.teamDropdownIsDisabled = false;
      this.sideloaderCheckBoxIsDisabled = false;
      //#endregion

      //#region engine information tab
      this.engineNoInputIsDisabled = false;
      this.chassisNoInputIsDisabled = false;
      this.turboChargerNoInputIsDisabled = false;
      this.fuelDropdownIsDisabled = false;
      this.horsePowerInputIsDisabled = false;
      this.engineModelInputIsDisabled = false;

      this.BDMInputIsDisabled = false;
      this.BTTInputIsDisabled = false;
      this.kerbWeightInputIsDisabled = false;
      this.BTMInputIsDisabled = false;
      this.BG1InputIsDisabled = false;
      this.BG2InputIsDisabled = false;
      this.BG3InputIsDisabled = false;
      this.BG4InputIsDisabled = false;
      this.BG5InputIsDisabled = false;
      this.BG6InputIsDisabled = false;
      this.BG7InputIsDisabled = false;
      this.BG8InputIsDisabled = false;
      this.BG9InputIsDisabled = false;
      this.BG10InputIsDisabled = false;
      this.BG11InputIsDisabled = false;
      this.BG12InputIsDisabled = false;
      //#endregion

      //#region tyre configuration tab
      this.tyreGridDisplayShowAllCheckbox = true;
      this.tyreGridShowSpares = true;
      //#endregion
    },
    disablePopupEntryFields: function () {
      // disable all PopupEntry fields, does not disable fields in additional popups
      // those are disabled by their respective call to action buttons

      this.availabilityRadioIsDisabled = true;
      this.unavailabilityRemarkInputIsDisabled = true;
      this.unavailabilityRemarkLabelValue = '';

      this.inventoryNoInputIsDisabled = true;
      this.inventoryCodeInputIsDisabled = true;
      this.activeCheckBoxIsDisabled = true;

      //#region general tab
      this.categoryDropdownIsDisabled = true;
      this.yearInputIsDisabled = true;
      this.datePurchaseDateTimePickerIsDisabled = true;
      this.colourInputIsDisabled = true;
      this.weightInputIsDisabled = true;
      this.widthInputIsDisabled = true;
      this.lengthInputIsDisabled = true;
      this.heightInputIsDisabled = true;
      this.maxCapacityInputIsDisabled = true;
      this.winchCapacityInputIsDisabled = true;
      this.groundClearanceInputIsDisabled = true;
      this.depreciationTypeDropdownIsDisabled = true;

      this.vehicleModelInputIsDisabled = true;
      this.trailerInputQueryIsDisabled = true;
      this.vehicleMadeInDropdownIsDisabled = true;
      this.companyDropdownIsDisabled = true;
      this.ownershipDropdownIsDisabled = true;
      this.supplierInputQueryIsDisabled = true;
      this.priceInputIsDisabled = true;
      this.loanFromInputIsDisabled = true;
      this.regCardNoInputIsDisabled = true;
      this.regPlaceInputIsDisabled = true;
      this.openingMileageInputIsDisabled = true;
      this.teamDropdownIsDisabled = true;
      this.sideloaderCheckBoxIsDisabled = true;
      //#endregion

      //#region engine information tab
      this.engineNoInputIsDisabled = true;
      this.chassisNoInputIsDisabled = true;
      this.turboChargerNoInputIsDisabled = true;
      this.fuelDropdownIsDisabled = true;
      this.horsePowerInputIsDisabled = true;
      this.engineModelInputIsDisabled = true;

      this.BDMInputIsDisabled = true;
      this.BTTInputIsDisabled = true;
      this.kerbWeightInputIsDisabled = true;
      this.BTMInputIsDisabled = true;
      this.BG1InputIsDisabled = true;
      this.BG2InputIsDisabled = true;
      this.BG3InputIsDisabled = true;
      this.BG4InputIsDisabled = true;
      this.BG5InputIsDisabled = true;
      this.BG6InputIsDisabled = true;
      this.BG7InputIsDisabled = true;
      this.BG8InputIsDisabled = true;
      this.BG9InputIsDisabled = true;
      this.BG10InputIsDisabled = true;
      this.BG11InputIsDisabled = true;
      this.BG12InputIsDisabled = true;
      //#endregion

      //#region tyre configuration tab
      this.tyreGridDisplayShowAllCheckbox = false;
      this.tyreGridShowSpares = false;
      //#endregion
    },
    menuClicked: async function (menuButton) {
      switch (menuButton['title']) {
        case 'New Vehicle':
          this.clearPopupEntryFields();
          this.enablePopupEntryFields();
          this.selectedTabIndex = 0; // reset to general tab
          this.vehicleAction = 'New';
          this.availabilityRadioValue = this.availabilityRadioButtons.find((radio) => radio['value'] === '1') ?? null; // set to Available
          this.availabilityRadioIsDisabled = true; // disable availability radio buttons
          this.popupEntryTitle = 'New Vehicle';
          this.popupEntryInViewMode = false;
          this.showPopupEntry = true;
          break;

        case 'Edit Vehicle':
          if (this.vehicleTableBoxSelectedRow.length > 1) {
            this.$showAlert('error', 'Multiple Vehicles Selected', 'Please select only 1 vehicle for this action', [
              { text: 'OK' },
            ]);
            break;
          }
          this.clearPopupEntryFields();
          if (this.vehicleTableBoxSelectedRow.length == 0) {
            this.$showAlert('error', 'No Vehicle Selected', 'Please select a vehicle to edit', [{ text: 'OK' }]);
            return;
          }

          this.$fullscreenLoading('Getting Vehicle Details');
          await this.getVehicleDetails(this.vehicleTableBoxSelectedRow[0]['Inventory #'].trim());

          this.$fullscreenLoading('Populating Fields');
          this.populatePopupEntryFields();

          this.enablePopupEntryFields();
          this.selectedTabIndex = 0; // reset to general tab
          this.vehicleAction = 'Edit';
          this.popupEntryTitle = `Edit Vehicle ${this.vehicleTableBoxSelectedRow[0]['Inventory #']}`;
          this.popupEntryInViewMode = false;
          this.showPopupEntry = true;
          this.$fullscreenLoading();
          break;

        case 'Delete Vehicle':
          if (this.vehicleTableBoxSelectedRow.length > 1) {
            this.$showAlert('error', 'Multiple Vehicles Selected', 'Please select only 1 vehicle for this action', [
              { text: 'OK' },
            ]);
            break;
          }
          if (this.vehicleTableBoxSelectedRow.length == 0) {
            this.$showAlert('error', 'No Vehicle Selected', 'Please select a vehicle to delete', [{ text: 'OK' }]);
            return;
          }

          if (
            (await this.$showAlert(
              'warning',
              'Are you sure you want to delete this vehicle?',
              `Inventory #: ${this.vehicleTableBoxSelectedRow[0]['Inventory #']}<br>` +
                `Inventory Code: ${this.vehicleTableBoxSelectedRow[0]['Inventory Code']}`,
              [
                { text: 'Delete', color: 'red', textColor: 'white' },
                { text: 'Cancel', color: 'lightgray' },
              ],
            )) == 'Cancel' // user clicked Cancel
          ) {
            return; // exit function
          }

          this.$fullscreenLoading('Getting Vehicle Details');
          await this.getVehicleDetails(this.vehicleTableBoxSelectedRow[0]['Inventory #'].trim());

          const requestBody = {
            action: 'D',
            lorry_no: this.vehicleDetails['Lorry_No'],
            vehiclecode: this.vehicleDetails['VehicleCode'],
            vehiclecategory: this.vehicleDetails['VehicleCategory'],
            type: this.vehicleDetails['Type'],
            company_code: this.vehicleDetails['Company_code'],
          };

          this.$fullscreenLoading('Deleting Vehicle');
          const result = (await this.$apiCallV2('/Vehicle/SaveVehicle', requestBody))[0];
          if (result['Results'] == 0) {
            // successfully deleted
            this.$showAlert(
              'success',
              'Vehicle Deleted',
              `Vehicle No. ${requestBody['lorry_no']} has been successfully deleted`,
              [{ text: 'OK' }],
            );
            this.refreshVehicleData();
          } else {
            // an error occurred
            this.$showAlert('error', 'Vehicle Could Not Be Deleted', result['ErrMsg'], [{ text: 'OK' }]);
          }
          this.$fullscreenLoading();
          break;

        case 'View Vehicle':
          if (this.vehicleTableBoxSelectedRow.length > 1) {
            this.$showAlert('error', 'Multiple Vehicles Selected', 'Please select only 1 vehicle for this action', [
              { text: 'OK' },
            ]);
            break;
          }
          if (this.vehicleTableBoxSelectedRow.length == 0) {
            this.$showAlert('error', 'No Vehicle Selected', 'Please select a vehicle to view', [{ text: 'OK' }]);
            return;
          }

          this.$fullscreenLoading('Getting Vehicle Details');
          await this.getVehicleDetails(this.vehicleTableBoxSelectedRow[0]['Inventory #'].trim());

          this.clearPopupEntryFields();
          this.disablePopupEntryFields();

          this.$fullscreenLoading('Populating Fields');
          this.populatePopupEntryFields();

          this.selectedTabIndex = 0; // reset to general tab
          this.vehicleAction = 'View';
          this.popupEntryTitle = 'View Vehicle';
          this.popupEntryInViewMode = true;
          this.showPopupEntry = true;
          this.$fullscreenLoading();
          break;
        case 'Tasks':
          this.vehicleTodoPopupShow = true;
          break;
        case 'Mark Availability':
          this.batchAvailabilityPopupShow = true;
          this.batchAvailabilityActionPopupShow = false;
          this.batchAvailabilityTableBoxSelectedRow = [];
          break;
        case 'PM to Driver':
          await this.openPMToDriverPopup();
          break;
        case 'Team Management':
          await this.openTeamManagementPopup();
          break;
      }
    },
    addRenewalClicked: function (pointerEvent) {
      this.clearRenewalInfoPopupFields();
      this.enableRenewalInfoPopupFields();

      this.renewalInfoAction = 'Add';
      this.showRenewalInfoPopup = true;
    },
    editRenewalClicked: function (pointerEvent) {
      if (this.renewalInfoTableBoxSelectedRow.length == 0) {
        this.$showAlert('error', 'No Renewal Selected', 'Please select a renewal to edit', [{ text: 'OK' }]);
        return;
      }

      this.clearRenewalInfoPopupFields();
      this.populateRenewalInfoPopupFields();
      this.enableRenewalInfoPopupFields();
      this.renewalInfoAction = 'Edit';
      this.showRenewalInfoPopup = true;
    },
    deleteRenewalClicked: async function (pointerEvent) {
      if (this.renewalInfoTableBoxSelectedRow.length == 0) {
        this.$showAlert('error', 'No Renewal Selected', 'Please select a renewal to delete', [{ text: 'OK' }]);
        return;
      }

      if (
        (await this.$showAlert(
          'warning',
          'Are you sure you want to delete this renewal?',
          `Type: ${this.renewalInfoTableBoxSelectedRow[0]['Type']}<br>Policy No: ${this.renewalInfoTableBoxSelectedRow[0]['Ref #']}`,
          [
            { text: 'Delete', color: 'red', textColor: 'white' },
            { text: 'Cancel', color: 'lightgray' },
          ],
        )) == 'Cancel' // user clicked Cancel
      ) {
        return; // exit function
      }

      let tableBoxData;
      switch (this.renewalInfoTableBoxSelectedRow[0]['_policyAction']) {
        case 'New': // newly added by user, not saved yet
          tableBoxData = this.renewalInfoTableBoxData.filter((renewal) => {
            return renewal['SeqNo'] != this.renewalInfoTableBoxSelectedRow[0]['SeqNo']; // just remove the row from the TableBox
          });
          break;

        case 'Edit':
        default: // user edited the saved policy to be deleted OR the saved policy is without any changes
          tableBoxData = [...this.renewalInfoTableBoxData];
          let deleteIndex = tableBoxData.findIndex((renewal) => {
            return renewal['SeqNo'] == this.renewalInfoTableBoxSelectedRow[0]['SeqNo']; // find the policy to be deleted
          });
          tableBoxData[deleteIndex]['_policyAction'] = 'Delete';
          let dropdownIndex = this.policyTypeDropdownItems.findIndex((i) => {
            return i['PolicyType'] == tableBoxData[deleteIndex]['Type'].toUpperCase();
          });
          tableBoxData[deleteIndex]['_policyType'] = this.policyTypeDropdownItems[dropdownIndex]['Type'];
          break;
      }

      this.renewalInfoTableBoxData = [];
      this.renewalInfoTableBoxSelectedRow = [];
      this.$nextTick(() => {
        this.renewalInfoTableBoxData = tableBoxData;
      });
    },
    viewRenewalClicked: function (pointerEvent) {
      if (this.renewalInfoTableBoxSelectedRow.length == 0) {
        this.$showAlert('error', 'No Renewal Selected', 'Please select a renewal to view', [{ text: 'OK' }]);
        return;
      }

      this.populateRenewalInfoPopupFields();
      this.disableRenewalInfoPopupFields();

      this.renewalInfoAction = 'View';
      this.showRenewalInfoPopup = true;
    },
    populateRenewalInfoPopupFields: async function () {
      this.policyTypeDropdownSelectedItem = this.policyTypeDropdownItems.find((policy) => {
        return policy['PolicyType'] == this.renewalInfoTableBoxSelectedRow[0]['Type'].toUpperCase();
      });
      await this.getRenewalInfoControls(this.policyTypeDropdownSelectedItem['PolicyType']);

      const APIFieldNameToTableBoxColumn = {
        policyno: 'Ref #',
        effdate: 'Effective Date',
        expdate: 'ExpiryDate',
        amount: 'Amount',
        filerefno: 'File Ref #',
        licenserefno: 'License Ref #',
        issueby: 'Issue By',
        ncb: 'NCB',
        agent: 'Agent',
        nnsamount: 'InsAmount',
        remark: 'Remark',
        slregno: 'Registration #',
        slmfg: 'Manufacturer',
        slmfgno: 'Mgf #',
        nnsamount: 'Sum Insured',
      };

      for (const control of this.renewalInfoControls[this.policyTypeDropdownSelectedItem['PolicyType']]) {
        if (!control['sendkey']) {
          // some field values do not need to be sent to the API, e.g. Duration (Months)
          continue;
        }

        if (control['type'] == 'DateTimePicker') {
          this.renewalInfoInputData[control['#']]['value'] =
            this.renewalInfoTableBoxSelectedRow[0][APIFieldNameToTableBoxColumn[control['sendkey']]] ?
              new Date(this.renewalInfoTableBoxSelectedRow[0][APIFieldNameToTableBoxColumn[control['sendkey']]])
            : null;
        } else {
          this.renewalInfoInputData[control['#']]['value'] =
            this.renewalInfoTableBoxSelectedRow[0][APIFieldNameToTableBoxColumn[control['sendkey']]] ?
              this.renewalInfoTableBoxSelectedRow[0][APIFieldNameToTableBoxColumn[control['sendkey']]]
            : '';
        }
      }
    },
    clearRenewalInfoPopupFields: function () {
      this.policyTypeDropdownSelectedItem = this.policyTypeDropdownItems[0];
      for (const index in this.renewalInfoInputData) {
        // reset all input fields
        this.renewalInfoInputData[index] = { ...this.renewalInfoInputData[index], value: null, errorMessage: '' };
      }
    },
    enableRenewalInfoPopupFields: function () {
      this.policyTypeDropdownIsDisabled = false;
      for (const index in this.renewalInfoInputData) {
        // enable all input fields
        this.renewalInfoInputData[index] = { ...this.renewalInfoInputData[index], isDisabled: false };
      }
    },
    disableRenewalInfoPopupFields: function () {
      this.policyTypeDropdownIsDisabled = true;
      for (const index in this.renewalInfoInputData) {
        // disable all input fields
        this.renewalInfoInputData[index] = { ...this.renewalInfoInputData[index], isDisabled: true };
      }
    },
    getRenewalInfoControls: async function (policyType) {
      if (this.renewalInfoControls[policyType]) {
        // if controls for passed policy type already exists
        return; // exit function
      }

      this.$fullscreenLoading('Getting Input Fields'); // set loading screen and get controls for passed policy type
      const renewalInfoControls = { ...this.renewalInfoControls }; // create a new JSON object and update renewalInfoControls
      renewalInfoControls[policyType] = (
        await this.$apiCallV2('/Vehicle/GetVehicleRenewalOption', {
          option: policyType,
        })
      )['control'];
      this.renewalInfoControls = renewalInfoControls; // create a new object so that Vue will detect that this.renewalInfoControls has changed
      this.$fullscreenLoading(); // otherwise, you would have to use this.$set()
    },
    getRenewalInfoControlDetails: function (id) {
      // only used in <template>, get control details for selected policy type and given id
      if (!this.policyTypeDropdownSelectedItem) {
        // if no policy selected
        return false;
      }
      return (
        this.renewalInfoControls[this.policyTypeDropdownSelectedItem['PolicyType']].find((control) => {
          return control['#'] == id.toString();
        }) || false
      );
    },
    calculateEffectiveDateExpiryDateDifference: function (effectiveDate, expiryDate) {
      if (
        !(effectiveDate instanceof Date) ||
        !(expiryDate instanceof Date) ||
        isNaN(effectiveDate.getTime()) ||
        isNaN(expiryDate.getTime())
      ) {
        return '';
      }

      const start = new Date(effectiveDate.getFullYear(), effectiveDate.getMonth(), effectiveDate.getDate());
      const end = new Date(expiryDate.getFullYear(), expiryDate.getMonth(), expiryDate.getDate());
      if (end < start) {
        return '';
      }
      // Expiry is inclusive; count completed calendar months, clamping month-end anniversaries.
      end.setDate(end.getDate() + 1);
      let months = (end.getFullYear() - start.getFullYear()) * 12 + end.getMonth() - start.getMonth();
      const anniversaryDay = Math.min(start.getDate(), new Date(end.getFullYear(), end.getMonth() + 1, 0).getDate());
      const anniversary = new Date(end.getFullYear(), end.getMonth(), anniversaryDay);
      if (end < anniversary) {
        months--;
      }
      return Math.max(0, months).toString();
    },
    renewalInfoPopupAffirmativeClicked: async function (pointerEvent) {
      if (!this.policyTypeDropdownSelectedItem || Object.keys(this.policyTypeDropdownSelectedItem).length == 0) {
        // if policy type isn't selected
        this.policyTypeDropdownErrorMessage = 'Select a policy type';
        return;
      }

      let error = false;

      for (let e of Object.keys(this.renewalInfoInputData)) {
        //remove all error message first
        this.renewalInfoInputData[e]['errorMessage'] = '';
      }

      const requiredFields = this.renewalInfoControls[this.policyTypeDropdownSelectedItem['PolicyType']].filter(
        (control) => {
          return control['required'] == '1';
        }, // find required fields from controls returned by API
      );
      for (const field of requiredFields) {
        if (!this.renewalInfoInputData[field['#']]['value']) {
          // for any required field that's missing a value
          this.renewalInfoInputData[field['#']]['errorMessage'] = `${field['placeholder']} is required!`;
          error = true;
        }
      }

      const lengthCheckFieldList = [
        'Reference #',
        'Issue By',
        'Agent',
        'Remark',
        'File Ref #',
        'License Ref #',
        'Registration #',
        'Manufacturer',
        'Mfg Ref #',
        'Bon Am #',
      ];
      const lengthCheckFields = this.renewalInfoControls[this.policyTypeDropdownSelectedItem['PolicyType']].filter(
        (control) => {
          return lengthCheckFieldList.includes(control['placeholder']);
        }, // find required fields from controls returned by API
      );
      for (const field of lengthCheckFields) {
        let length = this.lengthMapping.find((l) => l['placeholder'] == field['placeholder'])['length'];
        if (
          this.renewalInfoInputData[field['#']]['value'] &&
          this.renewalInfoInputData[field['#']]['value'].length > length
        ) {
          this.renewalInfoInputData[field['#']]['errorMessage'] =
            `${field['placeholder']} cannot exceed ${length} characters!`;
          error = true;
        }
      }

      const typeCheckFieldList = ['NCB (%)', 'Amount (RM)', 'Sum Insured'];
      const typeCheckFields = this.renewalInfoControls[this.policyTypeDropdownSelectedItem['PolicyType']].filter(
        (control) => {
          return typeCheckFieldList.includes(control['placeholder']);
        },
      );
      for (const field of typeCheckFields) {
        if (
          this.renewalInfoInputData[field['#']]['value'] &&
          !this.renewalInfoInputData[field['#']]['value'].match(/^\d*\.?\d*$/g)
        ) {
          this.renewalInfoInputData[field['#']]['errorMessage'] = `${field['placeholder']} format error!`;
          error = true;
        } else if (+this.renewalInfoInputData[field['#']]['value'] > 9999999) {
          this.renewalInfoInputData[field['#']]['errorMessage'] = `${field['placeholder']} cannot exceed 9999999!`;
          error = true;
        }
      }

      let effectiveD = this.renewalInfoControls[this.policyTypeDropdownSelectedItem['PolicyType']].find(
        (f) => f['placeholder'] == 'Effective Date',
      )['#'];
      let expiryD = this.renewalInfoControls[this.policyTypeDropdownSelectedItem['PolicyType']].find(
        (f) => f['placeholder'] == 'Expiry Date',
      )['#'];

      if (this.effectiveDateMaxDate && this.renewalInfoInputData[effectiveD]['value']) {
        if (this.renewalInfoInputData[effectiveD]['value'] > this.effectiveDateMaxDate) {
          error = true;
          this.renewalInfoInputData[effectiveD]['errorMessage'] = 'Effective Date cannot be later than Expiry Date!';
        }
      }
      if (this.expiryDateMinDate && this.renewalInfoInputData[expiryD]['value']) {
        if (this.renewalInfoInputData[expiryD]['value'] <= this.expiryDateMinDate) {
          error = true;
          this.renewalInfoInputData[expiryD]['errorMessage'] =
            'Expiry Date cannot be earlier than or the same as Effective Date!';
        }
      }

      if (error) {
        return;
      }

      const duplicateRenewal = this.renewalInfoTableBoxData.find((renewal) => {
        // attempt to find a renewal with the same type and Policy No
        return (
          renewal['Type'] == this.policyTypeDropdownSelectedItem['PolicyType'] &&
          renewal['PolicyNo'] ==
            this.renewalInfoInputData[
              this.renewalInfoControls[ // list of controls returned by API
                this.policyTypeDropdownSelectedItem['PolicyType'] // get controls for selected policy type
              ]
                .find((control) => {
                  return control['sendkey'] == 'policyno'; // find the position of the PolicyNo control
                })['#']
            ]['value'] // get user inputted PolicyNo value
        );
      });

      if (duplicateRenewal) {
        const response = await this.$showAlert(
          'warning',
          'Duplicate Renewal',
          'A renewal of the same Type with the same Policy No already exists.<br>Do you wish to proceed?',
          [{ text: 'Add New Renewal' }, { text: 'Cancel', color: 'lightgray' }],
        );

        if (response == 'Cancel') {
          return;
        }
      }

      const policy = {
        '#': '*',
        Type: this.policyTypeDropdownSelectedItem['PolicyType'],
        'Ref #': '',
        'Effective Date': '',
        ExpiryDate: '',
        Duration: '', // fill in later
        Amount: '',
        'File Ref #': '',
        'License Ref #': '',
        'Issue By': '',
        NCB: '0',
        Agent: '',
        'Registration #': '',
        Manufacturer: '',
        'Mgf #': '',
        Remark: '',
        'Sum Insured': '',
        'Date Updated': '', // leave blank
        'User ID': '', // leave blank
        SeqNo: '', // hidden column, fill in later
        _policyAction: '', // hidden column, fill in later
        _policyType: this.policyTypeDropdownSelectedItem['Type'], // hidden column
        _renewalInfoInputData: { ...this.renewalInfoInputData }, // hidden column
      };

      const APIFieldNameToTableBoxColumn = {
        policyno: 'Ref #',
        effdate: 'Effective Date',
        expdate: 'ExpiryDate',
        amount: 'Amount',
        filerefno: 'File Ref #',
        licenserefno: 'License Ref #',
        issueby: 'Issue By',
        ncb: 'NCB',
        agent: 'Agent',
        nnsamount: 'InsAmount',
        remark: 'Remark',
        slregno: 'Registration #',
        slmfg: 'Manufacturer',
        slmfgno: 'Mgf #',
        nnsamount: 'Sum Insured',
      };

      for (const control of this.renewalInfoControls[this.policyTypeDropdownSelectedItem['PolicyType']]) {
        if (!control['sendkey']) {
          // some field values do not need to be sent to the API, e.g. Duration (Months)
          continue;
        }

        if (control['type'] == 'DateTimePicker') {
          policy[APIFieldNameToTableBoxColumn[control['sendkey']]] =
            this.renewalInfoInputData[control['#']]['value'] ?
              this.renewalInfoInputData[control['#']]['value'].formatString('yyyy-MM-dd')
            : '';
        } else {
          policy[APIFieldNameToTableBoxColumn[control['sendkey']]] =
            this.renewalInfoInputData[control['#']]['value'] ? this.renewalInfoInputData[control['#']]['value'] : '';
        }
      }

      if (policy['Effective Date'] && policy['ExpiryDate']) {
        policy['Duration'] = this.calculateEffectiveDateExpiryDateDifference(
          new Date(policy['Effective Date']),
          new Date(policy['ExpiryDate']),
        );
      }

      if (!policy['SeqNo']) {
        let highestSeqNo = 0;
        for (let p of this.renewalInfoTableBoxData) {
          if (p['SeqNo'] > highestSeqNo) {
            highestSeqNo = p['SeqNo'];
          }
        }
        policy['SeqNo'] = parseInt(highestSeqNo) + 1;
      }

      let tableBoxData;
      switch (this.renewalInfoAction) {
        case 'Add':
          policy['_policyAction'] = 'New';
          tableBoxData = [...this.renewalInfoTableBoxData, policy]; // store current TableBoxData in temp variable
          break;

        case 'Edit':
          policy['_policyAction'] = this.renewalInfoTableBoxSelectedRow[0]['_policyAction'] == 'New' ? 'New' : 'Edit';
          tableBoxData = [...this.renewalInfoTableBoxData]; // store current TableBoxData in temp variable
          const editedRenewalIndex = tableBoxData.findIndex((p) => {
            return p['SeqNo'] == this.renewalInfoTableBoxSelectedRow[0]['SeqNo'];
          });
          policy['SeqNo'] = tableBoxData[editedRenewalIndex]['SeqNo'];
          tableBoxData[editedRenewalIndex] = policy;
          break;

        default:
          if (process.env['NODE_ENV'] == 'development') {
            console.error(
              '[VehiclePageV2] Invalid Renewal Action. Make sure the action is set when the call to action button is clicked',
            );
          }
      }

      this.renewalInfoTableBoxData = []; // clear TableBoxData
      this.renewalInfoTableBoxSelectedRow = [];
      this.$nextTick(() => {
        this.renewalInfoTableBoxData = this.reassignSequentialNumbers(tableBoxData); // repopulate TableBoxData
        this.showRenewalInfoPopup = false;
      });
    },
    addMaintenanceClicked: function (pointerEvent) {
      // reset all fields to default
      this.clearMaintenancePopupFields();
      this.enableMaintenancePopupFields();

      this.maintenanceAction = 'Add';
      this.showMaintenancePopup = true;
    },
    editMaintenanceClicked: function (pointerEvent) {
      if (this.maintenanceTableBoxSelectedRow.length == 0) {
        this.$showAlert('error', 'No Maintenance Selected', 'Please select a maintenance to edit', [{ text: 'OK' }]);
        return;
      }

      this.clearMaintenancePopupFields();
      this.populateMaintenancePopupFields();
      this.enableMaintenancePopupFields();
      this.maintenanceAction = 'Edit';
      this.showMaintenancePopup = true;
    },
    deleteMaintenanceClicked: async function (pointerEvent) {
      if (this.maintenanceTableBoxSelectedRow.length == 0) {
        this.$showAlert('error', 'No Maintenance Selected', 'Please select a maintenance to delete', [{ text: 'OK' }]);
        return;
      }

      if (
        (await this.$showAlert(
          'warning',
          'Are you sure you want to delete this task?',
          this.maintenanceTableBoxSelectedRow[0]['_groupBy']
            .split('|')
            .map((part, index) => {
              if (index == 0) {
                return `${part.trim()} - ${this.maintenanceTableBoxSelectedRow[0]['Task Description']} (${this.maintenanceTableBoxSelectedRow[0]['Service Task']})`;
              } else {
                return part.trim();
              }
            })
            .join('<br>'), // formulate the popup message using _groupBy of the selected row
          [
            { text: 'Delete', color: 'red', textColor: 'white' },
            { text: 'Cancel', color: 'lightgray' },
          ],
        )) == 'Cancel' // user clicked Cancel
      ) {
        return; // exit function
      }

      let tableBoxData = [...this.maintenanceTableBoxData];
      switch (this.maintenanceTableBoxSelectedRow[0]['_serviceAction']) {
        case 'New': // newly added by user, not saved yet
          let index = this.maintenanceTableBoxData.findIndex((service) => {
            return (
              // just remove the row from the TableBox
              service['_groupBy'] == this.maintenanceTableBoxSelectedRow[0]['_groupBy'] &&
              service['Service Task'] == this.maintenanceTableBoxSelectedRow[0]['Service Task'] &&
              service['_serviceAction'] == 'New'
            );
          });
          tableBoxData.splice(index, 1);
          break;

        case 'Edit':
        default: // user edited the saved policy to be deleted OR the saved policy is without any changes
          // tableBoxData = [...this.maintenanceTableBoxData];
          tableBoxData[
            tableBoxData.findIndex((service) => {
              return (
                service['_groupBy'] == this.maintenanceTableBoxSelectedRow[0]['_groupBy'] &&
                service['Service Task'] == this.maintenanceTableBoxSelectedRow[0]['Service Task'] &&
                service['_serviceAction'] != 'Delete'
              );
            })
          ]['_serviceAction'] = 'Delete';
          break;
      }
      this.maintenanceTableBoxData = [];
      this.maintenanceTableBoxSelectedRow = [];
      this.$nextTick(() => {
        this.maintenanceTableBoxData = tableBoxData;
      });
    },
    viewMaintenanceClicked: function (pointerEvent) {
      if (this.maintenanceTableBoxSelectedRow.length == 0) {
        this.$showAlert('error', 'No Maintenance Selected', 'Please select a maintenance to view', [{ text: 'OK' }]);
        return;
      }

      this.populateMaintenancePopupFields();
      this.disableMaintenancePopupFields();

      this.maintenanceAction = 'View';
      this.showMaintenancePopup = true;
    },
    populateMaintenancePopupFields: function () {
      this.servicesDropdownSelectedItem = this.servicesDropdownItems.find((service) => {
        return service['ServiceCode'] == this.maintenanceTableBoxSelectedRow[0]['ServiceCode'];
      });

      switch (this.maintenanceTableBoxSelectedRow[0]['Interval']) {
        case '0':
          this.intervalUnitRadioButtons = this.intervalUnitRadioButtons.map((radioButton) => {
            radioButton['checked'] = radioButton['label'] == 'Mileage';
            return radioButton;
          });
          this.intervalMileageInputValue = this.maintenanceTableBoxSelectedRow[0]['Mileage'];
          this.intervalDurationInputValue = '';
          break;

        case '1':
          this.intervalUnitRadioButtons = this.intervalUnitRadioButtons.map((radioButton) => {
            radioButton['checked'] = radioButton['label'] == 'Months';
            return radioButton;
          });
          this.intervalMileageInputValue = '';
          this.intervalDurationInputValue = this.maintenanceTableBoxSelectedRow[0]['Duration'];
          break;

        case '2':
          this.intervalUnitRadioButtons = this.intervalUnitRadioButtons.map((radioButton) => {
            radioButton['checked'] = radioButton['label'] == 'Days';
            return radioButton;
          });
          this.intervalMileageInputValue = '';
          this.intervalDurationInputValue = this.maintenanceTableBoxSelectedRow[0]['Duration'];
          break;

        case '3':
          this.intervalUnitRadioButtons = this.intervalUnitRadioButtons.map((radioButton) => {
            radioButton['checked'] = radioButton['label'] == 'Whichever Comes First';
            return radioButton;
          });
          this.intervalMileageInputValue = this.maintenanceTableBoxSelectedRow[0]['Mileage'];
          this.intervalDurationInputValue = this.maintenanceTableBoxSelectedRow[0]['Duration'];
          break;

        default:
          if (process.env['NODE_ENV'] == 'development') {
            console.error(
              '[VehiclePageV2] Unidentified Interval type, Maintenance popup may not be populated correctly',
            );
          }
      }

      switch (this.maintenanceTableBoxSelectedRow[0]['Occurence']) {
        case '0':
          this.occurrenceRadioButtons = this.occurrenceRadioButtons.map((radioButton) => {
            radioButton['checked'] = radioButton['label'] == 'Once Only';
            return radioButton;
          });
          this.occurrenceDateTimePickerValue = null;
          break;

        case '1':
          this.occurrenceRadioButtons = this.occurrenceRadioButtons.map((radioButton) => {
            radioButton['checked'] = radioButton['label'] == 'Routine until';
            return radioButton;
          });
          this.occurrenceDateTimePickerValue = new Date(this.maintenanceTableBoxSelectedRow[0]['ExpireDate']);
          break;

        default:
          if (process.env['NODE_ENV'] == 'development') {
            console.error(
              '[VehiclePageV2] Unidentified Occurrence type, Maintenance popup may not be populated correctly',
            );
          }
      }

      this.taskListTableBoxData = this.maintenanceTableBoxDataWithoutDeleted
        .filter((service) => {
          return service['_groupBy'] == this.maintenanceTableBoxSelectedRow[0]['_groupBy'];
        })
        .map((service) => {
          return {
            ServiceCode: service['Service Task'],
            Description: service['Task Description'],
            Remarks: service['Remark'], // moving remarks box
            Attachment: service['Attachment'],
            _AttImage: service['AttImage'],
          };
        });
      this.$nextTick(() => {
        this.taskListTableBoxSelectedRow = [
          this.taskListTableBoxData.find((task) => {
            return task['ServiceCode'] == this.maintenanceTableBoxSelectedRow[0]['Service Task'];
          }),
        ];
      });
    },
    clearMaintenancePopupFields: function () {
      this.servicesDropdownSelectedItem = {};
      this.servicesDropdownErrorMessage = '';
      this.intervalUnitRadioButtons = this.intervalUnitRadioButtons.map((radioButton) => {
        radioButton['checked'] = radioButton['label'] == 'Whichever Comes First';
        return radioButton;
      });
      this.intervalMileageInputValue = '';
      this.intervalMileageInputErrorMessage = '';
      this.intervalDurationInputValue = '';
      this.intervalDurationInputErrorMessage = '';
      this.occurrenceRadioButtons = this.occurrenceRadioButtons.map((radioButton) => {
        radioButton['checked'] = radioButton['label'] == 'Once Only';
        return radioButton;
      });
      this.occurrenceDateTimePickerValue = null;
      this.occurrenceDateTimePickerErrorMessage = '';
      this.taskListTableBoxData = [];
      this.taskListTableBoxSelectedRow = [];
    },
    enableMaintenancePopupFields: function () {
      this.servicesDropdownIsDisabled = false;
      this.intervalUnitRadioButtonIsDisabled = false;
      this.intervalMileageInputIsDisabled = false;
      this.intervalDurationInputIsDisabled = false;
      this.occurrenceRadioButtonIsDisabled = false;
      this.occurrenceDateTimePickerIsDisabled = false;
    },
    disableMaintenancePopupFields: function () {
      this.servicesDropdownIsDisabled = true;
      this.intervalUnitRadioButtonIsDisabled = true;
      this.intervalMileageInputIsDisabled = true;
      this.intervalDurationInputIsDisabled = true;
      this.occurrenceRadioButtonIsDisabled = true;
      this.occurrenceDateTimePickerIsDisabled = true;
    },
    generateMaintenanceGroupByField: function (service) {
      let groupBy = `${service['Code Description']} (${service['ServiceCode']})  |  `;

      switch (service['Interval']) {
        case '0': // mileage
          groupBy += `Every ${service['Mileage']} KM  |  `;
          break;

        case '1': // months
          groupBy += `Every ${service['Duration']} Month${service['Duration'] == '1' ? '' : 's'}  |  `;
          break;

        case '2': // days
          groupBy += `Every ${service['Duration']} Day${service['Duration'] == '1' ? '' : 's'}  |  `;
          break;

        case '3': // whichever comes first
          groupBy += `Every ${service['Mileage']} KM or ${service['Duration']} Month${service['Duration'] == '1' ? '' : 's'} (Whichever Comes First)  |  `;
          break;

        default:
          if (process.env['NODE_ENV'] == 'development') {
            console.error('[VehiclePageV2] Unidentified Interval type, _groupBy may not be created properly');
          }
      }

      switch (
        service['Occurence'] // yes, occurrence is misspelt in the API response, do not correct it
      ) {
        case '0':
          groupBy += 'Once Only';
          break;

        case '1':
          groupBy += `Routine until ${service['ExpireDate']}`;
          break;

        default:
          if (process.env['NODE_ENV'] == 'development') {
            console.error('[VehiclePageV2] Unidentified Occurence type, _groupBy may not be created properly');
          }
      }

      return groupBy;
    },
    maintenancePopupAffirmativeClicked: async function (pointerEvent) {
      let error = false;

      // check if missing required values
      if (!this.servicesDropdownSelectedItem || Object.keys(this.servicesDropdownSelectedItem).length == 0) {
        // no selected service
        this.servicesDropdownErrorMessage = 'Please select a service!';
        error = true;
      }

      if (this.intervalMileageInputIsRequired && !this.intervalMileageInputValue) {
        this.intervalMileageInputErrorMessage = 'Please enter a mileage!';
        error = true;
      } else if (this.intervalMileageInputValue && !this.intervalMileageInputValue.match(/^\d*\.?\d*$/g)) {
        this.intervalMileageInputErrorMessage = 'Mileage format error!';
        error = true;
      } else if (+this.intervalMileageInputValue > 9999999) {
        this.intervalMileageInputErrorMessage = 'Mileage cannot exceed 9999999!';
        error = true;
      }
      if (this.intervalDurationInputIsRequired && !this.intervalDurationInputValue) {
        this.intervalDurationInputErrorMessage = 'Please enter a duration!';
        error = true;
      } else if (this.intervalDurationInputValue && !this.intervalDurationInputValue.match(/^\d*\.?\d*$/g)) {
        this.intervalDurationInputErrorMessage = 'Duration format error!';
        error = true;
      } else if (
        +this.intervalDurationInputValue > 120 &&
        this.intervalUnitRadioButtons.some(
          (button) => button.checked && (button.label == 'Months' || button.label == 'Whichever Comes First'),
        )
      ) {
        this.intervalDurationInputErrorMessage = 'Duration cannot exceed 120 months!';
        error = true;
      }
      if (this.occurrenceDateTimePickerIsRequired && !this.occurrenceDateTimePickerValue) {
        this.occurrenceDateTimePickerErrorMessage = 'Please select a date!';
        error = true;
      }

      if (error) {
        return;
      }

      if (this.taskListTableBoxData.length == 0) {
        this.$showAlert('error', 'No Tasks Added', 'Please add at least one task before proceeding', [{ text: 'OK' }]);
        return;
      }

      const serviceTemplate = {
        '#': '*',
        ServiceCode: this.servicesDropdownSelectedItem['ServiceCode'],
        'Code Description': this.servicesDropdownSelectedItem['Description'],
        'Service Task': '', // fill in later
        'Task Description': '', // fill in later
        Mileage: this.intervalMileageInputValue,
        Duration: this.intervalDurationInputValue,
        Attachment: '', // fill in later
        AttImage: '', // hidden column, fill in later
        ExpireDate: '', // fill in later
        Remark: '', // fill in later, moving remarks box
        SeqNo: '', // hidden column
        Interval: '', // hidden column
        Occurence: '', // hidden column
        _groupBy: '', // hidden column
        _serviceAction: '', // hidden column
        _file: null, // hidden column
      };

      switch (
        this.intervalUnitRadioButtons.find((radioButton) => {
          return radioButton['checked'];
        })['label']
      ) {
        case 'Mileage':
          serviceTemplate['Interval'] = '0';
          break;

        case 'Months':
          serviceTemplate['Interval'] = '1';
          break;

        case 'Days':
          serviceTemplate['Interval'] = '2';
          break;

        case 'Whichever Comes First':
          serviceTemplate['Interval'] = '3';
          break;

        default:
          if (process.env['NODE_ENV'] == 'development') {
            console.error('[VehiclePageV2] Unidentified Interval type, Interval field may not be populated correctly');
          }
          break;
      }

      switch (
        this.occurrenceRadioButtons.find((radioButton) => {
          return radioButton['checked'];
        })['label']
      ) {
        case 'Once Only':
          serviceTemplate['Occurence'] = '0';
          break;

        case 'Routine until':
          serviceTemplate['Occurence'] = '1';
          serviceTemplate['ExpireDate'] = this.occurrenceDateTimePickerValue.formatString('yyyy-MM-dd');
          break;

        default:
          if (process.env['NODE_ENV'] == 'development') {
            console.error(
              '[VehiclePageV2] Unidentified Occurrence type, Occurrence field may not be populated correctly',
            );
          }
          break;
      }

      serviceTemplate['_groupBy'] = this.generateMaintenanceGroupByField(serviceTemplate);

      let tableBoxData;
      switch (this.maintenanceAction) {
        case 'Add': // add maintenance clicked
          tableBoxData = [...this.maintenanceTableBoxData]; // make a copy of table box data
          this.duplicateTaskPopupTaskList = []; // check for duplicate tasks
          for (const task of this.taskListTableBoxData) {
            const duplicateTask = tableBoxData.find((service) => {
              return (
                service['_serviceAction'] != 'Delete' &&
                service['_groupBy'] == serviceTemplate['_groupBy'] &&
                service['Service Task'] == task['ServiceCode']
              );
            });
            if (duplicateTask) {
              this.duplicateTaskPopupTaskList.push(task);
            }
          }

          if (this.duplicateTaskPopupTaskList.length > 0) {
            // if there are duplicate tasks for this service
            this.showDuplicateTaskPopup = true;
            return;
          }

          const services = this.taskListTableBoxData.map((task) => {
            const service = { ...serviceTemplate };
            service['Service Task'] = task['ServiceCode'];
            service['Task Description'] = task['Description'];
            service['Remark'] = task['Remarks']; // moving remarks box
            service['Attachment'] = task['Attachment'];
            service['AttImage'] = task['_AttImage'];
            service['_serviceAction'] = 'New'; // if maintenanceAction is "Add", _taskAction of each task should already all be "New", no need to check
            service['_file'] = task['_file'];
            return service;
          });

          tableBoxData = [...this.maintenanceTableBoxData, ...services];
          break;

        case 'Edit':
          if (serviceTemplate['_groupBy'] != this.maintenanceTableBoxSelectedRow[0]['_groupBy']) {
            // changes to service, interval and occurrence, need to change every single task with the same service, interval and occurrence
            const originalGroupBy = this.maintenanceTableBoxSelectedRow[0]['_groupBy'];
            tableBoxData = this.maintenanceTableBoxData.map((service) => {
              if (service['_groupBy'] != originalGroupBy) {
                return service;
              }
              const newService = { ...serviceTemplate };
              newService['Service Task'] = service['Service Task'];
              newService['Task Description'] = service['Task Description'];
              newService['Remark'] = service['Remark']; // moving remarks box
              newService['Attachment'] = service['Attachment'];
              newService['SeqNo'] = service['SeqNo'];
              newService['_serviceAction'] = service['_serviceAction'] == 'Delete' ? 'Delete' : 'Edit'; // if the service has already been deleted, remain deleted, else change to edited
              newService['_file'] = service['_file'];
              return newService;
            });
          } else {
            // no changes to service, interval and occurrence
            tableBoxData = [...this.maintenanceTableBoxData]; // make a copy of maintenanceTableBoxData with deletes
          }

          for (const task of this.taskListTableBoxData) {
            if (task['_taskAction'] != 'Delete') {
              // handle task deletion first
              continue;
            }

            // delete unwanted tasks from maintenanceTableBoxData
            const serviceToDeleteIndex = tableBoxData.findIndex((service) => {
              return (
                service['_serviceAction'] != 'Delete' &&
                service['_groupBy'] == serviceTemplate['_groupBy'] &&
                service['Service Task'] == task['ServiceCode']
              );
            });

            if (serviceToDeleteIndex == -1 && process.env['NODE_ENV'] == 'development') {
              console.error('[VehiclePageV2] No matching service found. Check for logic errors.');
              console.error(tableBoxData);
              console.error(task);
              continue;
            }

            if (tableBoxData[serviceToDeleteIndex]['_serviceAction'] == 'New') {
              // unsaved service
              tableBoxData.splice(serviceToDeleteIndex, 1); // just remove the row entirely
            } else {
              // saved
              tableBoxData[serviceToDeleteIndex]['_serviceAction'] = 'Delete'; // change action to delete
            }
          }

          for (const task of this.taskListTableBoxData) {
            // handle new and edited tasks
            const service = { ...serviceTemplate };
            service['Service Task'] = task['ServiceCode'];
            service['Task Description'] = task['Description'];
            service['Remark'] = task['Remarks']; // moving remarks box
            service['Attachment'] = task['Attachment'];
            service['_file'] = task['_file'];

            switch (task['_taskAction']) {
              case 'New':
                service['_serviceAction'] = 'New';
                tableBoxData.push(service);
                break;

              case 'Edit':
                const editedServiceIndex = tableBoxData.findIndex((s) => {
                  return (
                    s['_serviceAction'] != 'Delete' &&
                    s['ServiceCode'] == service['ServiceCode'] &&
                    s['Service Task'] == task['_originalTaskCode']
                  );
                });
                service['AttImage'] = tableBoxData[editedServiceIndex]['AttImage'];
                service['SeqNo'] = tableBoxData[editedServiceIndex]['SeqNo'];
                service['_serviceAction'] =
                  tableBoxData[editedServiceIndex]['_serviceAction'] == 'New' ? 'New' : 'Edit';
                tableBoxData[editedServiceIndex] = service;
                break;
            }
          }
          break;

        default:
          if (process.env['NODE_ENV'] == 'development') {
            console.error(
              '[VehiclePageV2] Invalid Maintenance Action. Make sure the action is set when the call to action button is clicked',
            );
          }
      }
      this.maintenanceTableBoxData = [];
      this.maintenanceTableBoxSelectedRow = [];
      this.$nextTick(() => {
        this.maintenanceTableBoxData = tableBoxData;
        this.showMaintenancePopup = false;
      });
    },
    addTaskClicked: function () {
      this.clearTaskPopupFields();
      this.enableTaskPopupFields();

      this.taskAction = 'Add';
      this.taskPopupTitle = 'Add Task';
      this.taskPopupButtons = [{ text: 'Add' }, { text: 'Cancel', color: 'lightgray' }];
      this.showTaskPopup = true;
    },
    editTaskClicked: function () {
      if (this.taskListTableBoxSelectedRow.length == 0) {
        this.$showAlert('error', 'No Task Selected', 'Please select a task to edit', [{ text: 'OK' }]);
        return;
      }

      this.clearTaskPopupFields();
      this.populateTaskPopupFields();
      this.enableTaskPopupFields();

      this.taskAction = 'Edit';
      this.taskPopupTitle = 'Edit Task';
      this.taskPopupButtons = [{ text: 'Edit' }, { text: 'Cancel', color: 'lightgray' }];
      this.showTaskPopup = true;
    },
    deleteTaskClicked: async function () {
      if (this.taskListTableBoxSelectedRow.length == 0) {
        this.$showAlert('error', 'No Task Selected', 'Please select a task to delete', [{ text: 'OK' }]);
        return;
      }

      const response = await this.$showAlert(
        'warning',
        'Are you sure you want to delete this task?',
        `Service Code: ${this.taskListTableBoxSelectedRow[0]['ServiceCode']}<br>` +
          `Description: ${this.taskListTableBoxSelectedRow[0]['Description']}`,
        [
          { text: 'Delete', color: 'red', textColor: 'white' },
          { text: 'Cancel', color: 'lightgray' },
        ],
      );
      if (response == 'Cancel') {
        // if user clicked cancel
        return; // exit function
      }

      let tableBoxData;
      if (this.taskListTableBoxSelectedRow[0]['_taskAction'] == 'New') {
        // not in maintenanceTableBoxData yet
        tableBoxData = this.taskListTableBoxData.filter((task) => {
          // just remove the row entirely
          return (
            task['_taskAction'] != 'Delete' && task['ServiceCode'] != this.taskListTableBoxSelectedRow[0]['ServiceCode']
          );
        });
      } else {
        // already in maintenanceTableBoxData, may or may not be saved in database already, depends on _serviceAction
        tableBoxData = [...this.taskListTableBoxData]; // make a copy of table box data
        const taskToDeleteIndex = tableBoxData.findIndex((task) => {
          return (
            task['_taskAction'] != 'Delete' && task['ServiceCode'] == this.taskListTableBoxSelectedRow[0]['ServiceCode']
          );
        });
        tableBoxData[taskToDeleteIndex]['_taskAction'] = 'Delete'; // change _taskAction of selected row to "Delete"
        if (tableBoxData[taskToDeleteIndex]['_originalTaskCode']) {
          // if the task was edited before being deleted
          tableBoxData[taskToDeleteIndex]['ServiceCode'] = tableBoxData[taskToDeleteIndex]['_originalTaskCode']; // change ServiceCode back to before the task was edited
        } // so that you don't need to check for _originalTaskCode inside maintenancePopupAffirmativeClicked
      }

      this.taskListTableBoxData = []; // clear table box
      this.taskListTableBoxSelectedRow = [];

      this.$nextTick(() => {
        this.taskListTableBoxData = tableBoxData;
      });
    },
    viewTaskClicked: function () {
      if (this.taskListTableBoxSelectedRow.length == 0) {
        this.$showAlert('error', 'No Task Selected', 'Please select a task to view', [{ text: 'OK' }]);
        return;
      }

      this.populateTaskPopupFields();
      this.disableTaskPopupFields();

      this.taskAction = 'View';
      this.taskPopupTitle = 'View Task';
      this.taskPopupButtons = [{ text: 'Cancel' }];
      this.showTaskPopup = true;
    },
    taskPopupFilesChanged: async function (files, placeholder) {
      this.taskServicesAttachments = files;
    },
    populateTaskPopupFields: function () {
      this.taskServicesDropdownSelectedItem = this.taskServicesDropdownItems.find((task) => {
        return task['ServiceCode'] == this.taskListTableBoxSelectedRow[0]['ServiceCode'];
      });
      this.taskServicesRemarksBoxValue = this.taskListTableBoxSelectedRow[0]['Remarks'];
      this.taskServicesAttachedFileName = this.taskListTableBoxSelectedRow[0]['Attachment'];

      this.taskServicesAttachments = [];
    },
    clearTaskPopupFields: function () {
      this.taskServicesDropdownSelectedItem = {};
      this.taskServicesDropdownErrorMessage = '';
      this.taskServicesRemarksBoxValue = '';
      this.taskServicesRemarksBoxErrorMessage = '';
      this.taskServicesAttachedFileName = '';

      this.taskServicesAttachments = [];
    },
    enableTaskPopupFields: function () {
      this.taskServicesDropdownIsDisabled = false;
      this.taskServicesRemarksBoxIsDisabled = false;
      this.taskServicesFileInputIsDisabled = false; // the file input is disabled even though it is hidden, so just reenable it

      this.taskServicesAttachments = [];
    },
    disableTaskPopupFields: function () {
      this.taskServicesDropdownIsDisabled = true;
      this.taskServicesRemarksBoxIsDisabled = true;
      this.taskServicesFileInputIsDisabled = true; // the file input should be hidden if action is "View", but just disable it anyways

      this.taskServicesAttachments = [];
    },
    taskPopupButtonClicked: function (buttonText) {
      let tableBoxData;

      switch (buttonText) {
        case 'Add':
          let addError = false;
          if (
            !this.taskServicesDropdownSelectedItem ||
            Object.keys(this.taskServicesDropdownSelectedItem).length == 0
          ) {
            // if no service is selected
            this.taskServicesDropdownErrorMessage = 'Please select a service to add!';
            addError = true;
          }

          if (this.taskServicesRemarksBoxValue.length > 200) {
            this.taskServicesRemarksBoxErrorMessage = 'Remarks cannot exceed 200 characters!';
            addError = true;
          }

          if (
            this.taskListTableBoxData.find((task) => {
              return (
                task['_taskAction'] != 'Delete' &&
                task['ServiceCode'] == this.taskServicesDropdownSelectedItem['ServiceCode']
              );
            })
          ) {
            this.$showAlert(
              'error',
              'Task Already Exists',
              'The selected task already exists for this service.<br>Please choose another task to add.',
              [{ text: 'OK' }],
            );
            return;
          }

          if (addError) return;

          tableBoxData = [
            ...this.taskListTableBoxData,
            {
              ServiceCode: this.taskServicesDropdownSelectedItem['ServiceCode'],
              Description: this.taskServicesDropdownSelectedItem['Description'],
              Remarks: this.taskServicesRemarksBoxValue, // moving remarks box
              Attachment: this.taskServicesAttachments[0] ? this.taskServicesAttachments[0]['name'] : '',
              _taskAction: 'New', // to indicate that this task is not in maintenanceTableBoxData yet
              _file: this.taskServicesAttachments[0],
            },
          ];
          this.taskListTableBoxData = [];
          this.$nextTick(() => {
            this.taskListTableBoxData = tableBoxData;
            this.showTaskPopup = false;
          });
          break;

        case 'Edit':
          let editError = false;
          if (
            !this.taskServicesDropdownSelectedItem ||
            Object.keys(this.taskServicesDropdownSelectedItem).length == 0
          ) {
            // if no service is selected
            this.taskServicesDropdownErrorMessage = 'Please select a service to add';
            editError = true;
          }

          if (this.taskServicesRemarksBoxValue.length > 200) {
            this.taskServicesRemarksBoxErrorMessage = 'Remarks cannot exceed 200 characters!';
            editError = true;
          }

          if (
            this.taskListTableBoxSelectedRow[0]['ServiceCode'] !=
              this.taskServicesDropdownSelectedItem['ServiceCode'] && // if a different task is selected
            this.taskListTableBoxData.find((task) => {
              // and the newly selected task alerady exists
              return task['ServiceCode'] == this.taskServicesDropdownSelectedItem['ServiceCode'];
            })
          ) {
            this.$showAlert(
              'error',
              'Task Already Exists',
              'The selected task already exists for this service.<br>Please choose another task to add.',
              [{ text: 'OK' }],
            );
            return;
          }

          if (editError) return;

          tableBoxData = [...this.taskListTableBoxData];
          const editedTaskIndex = tableBoxData.findIndex((task) => {
            return (
              task['_taskAction'] != 'Delete' &&
              task['ServiceCode'] == this.taskListTableBoxSelectedRow[0]['ServiceCode']
            );
          });
          tableBoxData[editedTaskIndex] = {
            ServiceCode: this.taskServicesDropdownSelectedItem['ServiceCode'],
            Description: this.taskServicesDropdownSelectedItem['Description'],
            Remarks: this.taskServicesRemarksBoxValue, // moving remarks box
            Attachment:
              this.taskServicesAttachments[0] ?
                this.taskServicesAttachments[0]['name']
              : tableBoxData[editedTaskIndex]['Attachment'],
            _taskAction: tableBoxData[editedTaskIndex]['_taskAction'] == 'New' ? 'New' : 'Edit',
            _originalTaskCode: tableBoxData[editedTaskIndex]['ServiceCode'], // used to indicate what the task code was before the edit, needed for maintenancePopupAffirmativeClicked
            _file:
              this.taskServicesAttachments[0] ?
                this.taskServicesAttachments[0]
              : tableBoxData[editedTaskIndex]['_file'],
          };

          this.taskListTableBoxData = [];
          this.$nextTick(() => {
            this.taskListTableBoxData = tableBoxData;
            this.showTaskPopup = false;
          });
          break;

        case 'Cancel':
          this.showTaskPopup = false;
          break;
      }
    },
    duplicateTaskPopupButtonClicked: function (buttonText) {
      this.showDuplicateTaskPopup = false;
    },
    updateTyreGridTyreLayout: async function (selectedCategory) {
      if (!selectedCategory || Object.keys(selectedCategory).length == 0) {
        // category is null or no category is selected
        this.tyreGridTyreLayout = this.tyreConfigurationTableBoxDataWithoutDeleted.map((tyre) => {
          // form tyrelayout based on TableBox data only
          const tyreToRender = { index: tyre['TyreIndex'] };

          if (tyre['Align'] == 'C') {
            tyreToRender['layoutType'] = 'FrontRear';
          }

          switch (tyre['_tyreAction']) {
            case 'New':
            case 'Edit':
              tyreToRender['state'] = 'changes';
              break;

            default:
              tyreToRender['state'] = 'saved';
              break;
          }

          return tyreToRender;
        });
        return; // exit function
      }

      let tyreConfiguration = [];
      if (
        this.defaultTyreConfigurations[
          `${selectedCategory['Category Code']}_${selectedCategory['Type']}` // if tyre config exists in the cache
        ]
      ) {
        tyreConfiguration = JSON.parse(
          JSON.stringify(
            // make a deep copy, because tyreConfiguration will be modified later on
            this.defaultTyreConfigurations[
              `${selectedCategory['Category Code']}_${selectedCategory['Type']}` // get from cache
            ],
          ),
        );
      } else {
        this.$fullscreenLoading('Getting Tyre Configuration');

        tyreConfiguration = (
          await this.$apiCallV2(
            // get from API
            `/Vehicle/GetVehicleTyreConfiguration?category=${selectedCategory['Category Code']}&type=${selectedCategory['Type']}`,
          )
        ).map((tyre) => {
          return {
            index: tyre['tyreindex'],
            description: tyre['tyrenaming'],
          };
        });
        this.defaultTyreConfigurations[
          `${selectedCategory['Category Code']}_${selectedCategory['Type']}` // store into cache
        ] = JSON.parse(JSON.stringify(tyreConfiguration)); // make a deep copy, because tyreConfiguration will be modified later on

        this.$fullscreenLoading();
      }

      for (const tyre of this.tyreConfigurationTableBoxDataWithoutDeleted) {
        const matchingTyre = tyreConfiguration.find((t) => {
          return t['index'].toString() === tyre['TyreIndex'].toString();
        });

        if (matchingTyre) {
          // if a match is found
          switch (tyre['_tyreAction']) {
            case 'New':
            case 'Edit':
              matchingTyre['state'] = 'changes';
              break;

            default:
              matchingTyre['state'] = 'saved';
              break;
          }
        } else {
          // no match is found
          const outlierTyre = { index: tyre['TyreIndex'] };

          if (tyre['Align'] == 'C') {
            outlierTyre['layoutType'] = 'FrontRear';
          }

          switch (tyre['_tyreAction']) {
            case 'New':
            case 'Edit':
              outlierTyre['state'] = 'changes';
              break;

            default:
              outlierTyre['state'] = 'saved';
              break;
          }

          tyreConfiguration.push(outlierTyre);
        }
      }

      this.tyreGridTyreLayout = [...tyreConfiguration];
    },
    tyreGridTyreClicked: function (selectedTyreDetails) {
      if (!selectedTyreDetails) {
        // if tyre was deselected
        this.tyreConfigurationTableBoxSelectedRow = []; // deselect TableBox row
      } else {
        if (parseInt(selectedTyreDetails['index']) <= 1) {
          // check both index and layout type
          const associatedRow = this.tyreConfigurationTableBoxDisplayData.find((tyre) => {
            return (
              tyre['TyreIndex'].toString() === selectedTyreDetails['index'].toString() &&
              tyre['Align'] == (selectedTyreDetails['layoutType'] == 'FrontRear' ? 'C' : 'L')
            );
          });

          this.tyreConfigurationTableBoxSelectedRow = associatedRow ? [associatedRow] : [];
        } else {
          // checking index is sufficient
          const associatedRow = this.tyreConfigurationTableBoxDisplayData.find((tyre) => {
            return tyre['TyreIndex'].toString() === selectedTyreDetails['index'].toString();
          });

          this.tyreConfigurationTableBoxSelectedRow = associatedRow ? [associatedRow] : [];
        }
      }
    },
    addTyreClicked: async function (pointerEvent) {
      if (!this.tyreGridSelectedTyre || Object.keys(this.tyreGridSelectedTyre).length == 0) {
        this.$showAlert('error', 'No Tyre Selected', 'Please select a tyre position before adding a tyre', [
          { text: 'OK' },
        ]);
        return;
      }

      if (
        this.tyreGridSelectedTyre &&
        Object.keys(this.tyreGridSelectedTyre).length > 0 && // tyre selected on tyre grid
        this.tyreConfigurationTableBoxSelectedRow &&
        this.tyreConfigurationTableBoxSelectedRow.length > 0 // tyre selected on tablebox
      ) {
        const response = await this.$showAlert(
          'warning',
          'Saved Tyre Exists',
          'The selected position already has a saved tyre attached to it.<br>Would you like to edit it instead?',
          [{ text: 'Edit' }, { text: 'Cancel', color: 'lightgray' }],
        );

        switch (response) {
          case 'Edit':
            this.clearTyreConfigurationPopupFields();
            this.populateTyreConfigurationPopupFields();
            this.enableTyreConfigurationPopupFields();
            this.tyreConfigurationAction = 'Edit';
            this.showTyreConfigurationPopup = true;
            return;

          case 'Cancel':
            return;
        }
      }

      this.clearTyreConfigurationPopupFields();
      this.enableTyreConfigurationPopupFields();
      this.tyreConfigurationAction = 'Add';
      this.showTyreConfigurationPopup = true;
    },
    editTyreClicked: function (pointerEvent) {
      if (
        (!this.tyreGridSelectedTyre || Object.keys(this.tyreGridSelectedTyre).length == 0) && // no tyre selected on tyre grid
        // AND
        (!this.tyreConfigurationTableBoxSelectedRow || this.tyreConfigurationTableBoxSelectedRow.length == 0) // no tyre selected on tablebox
      ) {
        this.$showAlert('error', 'No Tyre Selected', 'Please select a tyre to edit', [{ text: 'OK' }]);
        return;
      }

      if (
        this.tyreGridSelectedTyre &&
        Object.keys(this.tyreGridSelectedTyre).length > 0 && // tyre selected on tyre grid
        (!this.tyreConfigurationTableBoxSelectedRow || this.tyreConfigurationTableBoxSelectedRow.length == 0) // but no tyre selected on tablebox
      ) {
        this.$showAlert(
          'error',
          'No Saved Tyre',
          'The selected position does not have a saved tyre.<br>Please select a position with a saved tyre (any of the green/orange slots).',
          [{ text: 'OK' }],
        );
        return;
      }

      this.clearTyreConfigurationPopupFields();
      this.populateTyreConfigurationPopupFields();
      this.enableTyreConfigurationPopupFields();

      this.tyreConfigurationAction = 'Edit';
      this.showTyreConfigurationPopup = true;
    },
    deleteTyreClicked: async function (pointerEvent) {
      if (
        (!this.tyreGridSelectedTyre || Object.keys(this.tyreGridSelectedTyre).length == 0) && // no tyre selected on tyre grid
        // AND
        (!this.tyreConfigurationTableBoxSelectedRow || this.tyreConfigurationTableBoxSelectedRow.length == 0) // no tyre selected on tablebox
      ) {
        this.$showAlert('error', 'No Tyre Selected', 'Please select a tyre to delete', [{ text: 'OK' }]);
        return;
      }

      if (
        this.tyreGridSelectedTyre &&
        Object.keys(this.tyreGridSelectedTyre).length > 0 && // tyre selected on tyre grid
        (!this.tyreConfigurationTableBoxSelectedRow || this.tyreConfigurationTableBoxSelectedRow.length == 0) // but no tyre selected on tablebox
      ) {
        this.$showAlert(
          'error',
          'No Saved Tyre',
          'The selected position does not have a saved tyre.<br>Please select a position with a saved tyre (any of the green/orange slots).',
          [{ text: 'OK' }],
        );
        return;
      }

      if (
        (await this.$showAlert(
          'warning',
          'Are you sure you want to delete this tyre?',
          `<strong>Name:</strong> ${this.tyreGridSelectedTyreDetails['description']}<br>` +
            `<strong>Position:</strong> Column ${this.tyreGridSelectedTyreDetails['colNo']}, Row ${this.tyreGridSelectedTyreDetails['rowNo']}<br>` +
            `<strong>Model:</strong> ${this.tyreConfigurationTableBoxSelectedRow[0]['Model'] == '' ? '-' : this.tyreConfigurationTableBoxSelectedRow[0]['Model']}<br>` +
            `<strong>Manufacturer:</strong> ${this.tyreConfigurationTableBoxSelectedRow[0]['Manufacturer'] == '' ? '-' : this.tyreConfigurationTableBoxSelectedRow[0]['Manufacturer']}<br>` +
            `<strong>Serial No:</strong> ${this.tyreConfigurationTableBoxSelectedRow[0]['Serial No'] ?? '-'}<br>`,
          [
            { text: 'Delete', color: 'red', textColor: 'white' },
            { text: 'Cancel', color: 'lightgray' },
          ],
        )) == 'Cancel' // user clicked Cancel
      ) {
        return; // exit function
      }

      let tableBoxData;
      switch (this.tyreConfigurationTableBoxSelectedRow[0]['_tyreAction']) {
        case 'New': // newly added by user, not saved yet
          tableBoxData = this.tyreConfigurationTableBoxData.filter((tyre) => {
            // just remove the row from the TableBox
            return tyre['TyreIndex'].toString() != this.tyreConfigurationTableBoxSelectedRow[0]['TyreIndex'].toString();
          });
          break;

        case 'Edit':
        default: // user edited the saved policy to be deleted OR the saved policy is without any changes
          tableBoxData = [...this.tyreConfigurationTableBoxData];
          tableBoxData[
            tableBoxData.findIndex((tyre) => {
              return (
                tyre['TyreIndex'].toString() === this.tyreConfigurationTableBoxSelectedRow[0]['TyreIndex'].toString()
              );
            })
          ]['_tyreAction'] = 'Delete';
          break;
      }
      this.tyreConfigurationTableBoxData = [];
      this.tyreConfigurationTableBoxSelectedRow = [];
      this.tyreGridSelectedTyre = {};
      await this.$nextTick(() => {
        this.tyreConfigurationTableBoxData = tableBoxData;
        this.updateTyreGridTyreLayout(this.categoryDropdownSelectedItem);
      });
    },
    viewTyreClicked: function (pointerEvent) {
      if (
        (!this.tyreGridSelectedTyre || Object.keys(this.tyreGridSelectedTyre).length == 0) && // no tyre selected on tyre grid
        // AND
        (!this.tyreConfigurationTableBoxSelectedRow || this.tyreConfigurationTableBoxSelectedRow.length == 0) // no tyre selected on tablebox
      ) {
        this.$showAlert('error', 'No Tyre Selected', 'Please select a tyre to view', [{ text: 'OK' }]);
        return;
      }

      if (
        this.tyreGridSelectedTyre &&
        Object.keys(this.tyreGridSelectedTyre).length > 0 && // tyre selected on tyre grid
        (!this.tyreConfigurationTableBoxSelectedRow || this.tyreConfigurationTableBoxSelectedRow.length == 0) // but no tyre selected on tablebox
      ) {
        this.$showAlert(
          'error',
          'No Saved Tyre',
          'The selected position does not have a saved tyre.<br>Please select a position with a saved tyre (any of the green/orange slots).',
          [{ text: 'OK' }],
        );
        return;
      }

      this.populateTyreConfigurationPopupFields();
      this.disableTyreConfigurationPopupFields();

      this.tyreConfigurationAction = 'View';
      this.showTyreConfigurationPopup = true;
    },
    populateTyreConfigurationPopupFields: function () {
      const tyreType = String(
        this.tyreConfigurationTableBoxSelectedRow[0]['Tyre Type'] ??
          this.tyreConfigurationTableBoxSelectedRow[0]['TyreType'] ??
          '',
      ).trim();
      this.tyreTypeSelectedItem =
        this.tyreTypeListByGroup.find(
          (item) =>
            String(item['Part Type'] || '').trim() == tyreType ||
            String(item['Product Code'] || '').trim() == tyreType,
        ) || null;
      this.serialNoInputValue = this.tyreConfigurationTableBoxSelectedRow[0]['Serial No'];
      this.sizeInputValue = this.tyreConfigurationTableBoxSelectedRow[0]['Size'];
      const mfgDate = String(this.tyreConfigurationTableBoxSelectedRow[0]['Mfg Date'] || '').trim();
      const dateChange = String(this.tyreConfigurationTableBoxSelectedRow[0]['Date Change'] || '').trim();
      this.mfgDateDateTimePickerValue = mfgDate ? new Date(mfgDate) : null;
      this.dateChangeDateTimePickerValue = dateChange ? new Date(dateChange) : null;

      this.tyreModelInputValue = this.tyreConfigurationTableBoxSelectedRow[0]['Model'];
      this.manufacturerDropdownSelectedItem = this.manufacturerDropdownItems.find((manufacturer) => {
        return manufacturer['MFGCode'] == this.tyreConfigurationTableBoxSelectedRow[0]['Manufacturer'];
      });
      this.tyreMadeInDropdownSelectedItem = this.tyreMadeInDropdownItems.find((country) => {
        return country['Country'] == this.tyreConfigurationTableBoxSelectedRow[0]['MadeIn'];
      });
      this.tyreConfigurationRemarksBoxValue = this.tyreConfigurationTableBoxSelectedRow[0]['Remark'];
    },
    clearTyreConfigurationPopupFields: function () {
      this.tyreTypeSelectedItem = null;
      this.tyreTypeErrorMessage = '';
      this.serialNoInputValue = '';
      this.serialNoInputErrorMessage = '';
      this.sizeInputValue = '';
      this.sizeInputErrorMessage = '';
      this.mfgDateDateTimePickerValue = null;
      this.dateChangeDateTimePickerValue = null;

      this.tyreModelInputValue = '';
      this.tyreModelInputErrorMessage = '';
      this.manufacturerDropdownSelectedItem = null;
      this.tyreMadeInDropdownSelectedItem = null;
      this.tyreConfigurationRemarksBoxValue = '';
      this.tyreConfigurationRemarksBoxErrorMessage = '';
    },
    enableTyreConfigurationPopupFields: function () {
      this.tyreTypeIsDisabled = false;
      this.serialNoInputIsDisabled = false;
      this.sizeInputIsDisabled = false;
      this.mfgDateDateTimePickerIsDisabled = false;
      this.dateChangeDateTimePickerIsDisabled = false;
      this.tyreModelInputIsDisabled = false;
      this.manufacturerDropdownIsDisabled = false;
      this.tyreMadeInDropdownIsDisabled = false;
      this.tyreConfigurationRemarksBoxIsDisabled = false;
    },
    disableTyreConfigurationPopupFields: function () {
      this.tyreTypeIsDisabled = true;
      this.serialNoInputIsDisabled = true;
      this.sizeInputIsDisabled = true;
      this.mfgDateDateTimePickerIsDisabled = true;
      this.dateChangeDateTimePickerIsDisabled = true;
      this.tyreModelInputIsDisabled = true;
      this.manufacturerDropdownIsDisabled = true;
      this.tyreMadeInDropdownIsDisabled = true;
      this.tyreConfigurationRemarksBoxIsDisabled = true;
    },
    tyreConfigurationTableBoxRowSelected: function (row, selected) {
      if (!selected) {
        // if a row was deselected
        this.tyreGridSelectedTyre = {}; // deselect any selected tyre
        return;
      }

      this.tyreGridSelectedTyre = { index: row['TyreIndex'] };
      if (row['Align'] == 'C' && parseInt(row['TyreIndex']) < 100) {
        // if it is a front or rear tyre
        this.tyreGridSelectedTyre['layoutType'] = 'FrontRear';
      }
    },
    tyreTypeSelected: function () {
      this.tyreTypeErrorMessage = '';
    },
    tyreConfigurationPopupAffirmativeClicked: async function (pointerEvent) {
      let error = false;

      if (!this.tyreTypeSelectedItem) {
        this.tyreTypeErrorMessage = 'Tyre Type is required!';
        error = true;
      } else if (String(this.tyreTypeSelectedItem['Part Type'] || '').trim().length > 10) {
        this.tyreTypeErrorMessage = 'Tyre Type cannot exceed 10 characters!';
        error = true;
      }
      if (!this.serialNoInputValue) {
        this.serialNoInputErrorMessage = 'Serial No is required!';
        error = true;
      } else if (this.serialNoInputValue.length > 25) {
        this.serialNoInputErrorMessage = 'Serial No cannot exceed 25 characters!';
        error = true;
      }
      if (this.sizeInputValue && this.sizeInputValue.length > 25) {
        this.sizeInputErrorMessage = 'Size cannot exceed 25 characters!';
        error = true;
      }
      if (this.tyreModelInputValue && this.tyreModelInputValue.length > 60) {
        this.tyreModelInputErrorMessage = 'Model cannot exceed 60 characters!';
        error = true;
      }
      if (this.tyreConfigurationRemarksBoxValue.length > 200) {
        this.tyreConfigurationRemarksBoxErrorMessage = 'Remarks cannot exceed 200 characters!';
        error = true;
      }

      if (error) return;

      const tyre = {
        '#': '*',
        'Tyre Type': this.tyreTypeSelectedItem ? this.tyreTypeSelectedItem['Part Type'] : '',
        'Serial No': this.serialNoInputValue,
        Model: this.tyreModelInputValue,
        Manufacturer: this.manufacturerDropdownSelectedItem ? this.manufacturerDropdownSelectedItem['MFGCode'] : '',
        MadeIn: this.tyreMadeInDropdownSelectedItem ? this.tyreMadeInDropdownSelectedItem['Country'] : '',
        Size: this.sizeInputValue,
        Position: '', // fill in later
        'Mfg Date': this.mfgDateDateTimePickerValue ? this.mfgDateDateTimePickerValue.formatString('yyyy-MM-dd') : '',
        'Date Change':
          this.dateChangeDateTimePickerValue ? this.dateChangeDateTimePickerValue.formatString('yyyy-MM-dd') : '',
        Align: '', // fill in later
        TyreIndex: this.tyreGridSelectedTyreDetails['index'].toString(),
        '# of Retread': '', // leave blank
        'In Use': parseInt(this.tyreGridSelectedTyreDetails['index']) < 100 ? '1' : '2', // "1" if not spare, "2" if spare
        Remark: this.tyreConfigurationRemarksBoxValue,
        InventoryNo: '', // hidden column, leave blank
        SeqNo: '', // hidden column, fill in later
        TyreGUID: '', // hidden column, fill in later
        _tyreAction: '', // hidden column, fill in later
        _rowNo: this.tyreGridSelectedTyreDetails['rowNo'], // hidden column
        _colNo: this.tyreGridSelectedTyreDetails['colNo'], // hidden column
      };

      switch (this.tyreGridSelectedTyreDetails['layoutType']) {
        case 'LeftRight':
          if (this.tyreGridSelectedTyreDetails['index'] >= 100) {
            // spare tyre
            tyre['Align'] = '';
            tyre['Position'] = '';
          } else if (this.tyreGridSelectedTyreDetails['colNo'] <= 5) {
            // left side
            tyre['Align'] = 'L';
            if (this.tyreGridSelectedTyreDetails['rowNo'] <= 5) {
              tyre['Position'] = 'FL';
            } else {
              tyre['Position'] = 'RL';
            }
          } else {
            // right side
            tyre['Align'] = 'R';
            if (this.tyreGridSelectedTyreDetails['rowNo'] <= 5) {
              tyre['Position'] = 'FR';
            } else {
              tyre['Position'] = 'RR';
            }
          }
          break;

        case 'FrontRear':
          tyre['Align'] = 'C';
          tyre['Position'] = 'RC';
          break;
      }

      let tableBoxData;
      switch (this.tyreConfigurationAction) {
        case 'Add':
          tyre['_tyreAction'] = 'New';
          tableBoxData = [...this.tyreConfigurationTableBoxData, tyre];
          break;

        case 'Edit':
          tyre['_tyreAction'] = this.tyreConfigurationTableBoxSelectedRow[0]['_tyreAction'] == 'New' ? 'New' : 'Edit';
          tableBoxData = [...this.tyreConfigurationTableBoxData];
          const editedTyreIndex = tableBoxData.findIndex((t) => {
            return t['TyreIndex'].toString() === this.tyreConfigurationTableBoxSelectedRow[0]['TyreIndex'].toString();
          });
          tyre['SeqNo'] = tableBoxData[editedTyreIndex]['SeqNo'];
          tyre['TyreGUID'] = tableBoxData[editedTyreIndex]['TyreGUID'];
          tableBoxData[editedTyreIndex] = tyre;
          break;

        default:
          if (process.env['NODE_ENV'] == 'development') {
            console.error(
              '[VehiclePageV2] Invalid Tyre Configuration Action. Make sure the action is set when the call to action button is clicked',
            );
          }
          break;
      }

      this.tyreConfigurationTableBoxData = [];
      this.tyreConfigurationTableBoxSelectedRow = [];
      this.tyreGridSelectedTyre = {};
      await this.$nextTick(() => {
        this.tyreConfigurationTableBoxData = tableBoxData;
        this.showTyreConfigurationPopup = false;
        this.updateTyreGridTyreLayout(this.categoryDropdownSelectedItem);
      });
    },
    addAttachmentClicked: function (pointerEvent) {
      this.attachmentsPopupTitle = 'Add Attachments';
      this.attachmentsPopupButtons = [{ text: 'Add' }, { text: 'Cancel', color: 'lightgray' }];
      this.attachmentsInputIsRequired = true;
      this.attachmentsInputErrorMessage = '';
      (this.attachmentsInputMultiFile = true), (this.attachmentsRemarksBoxValue = '');
      this.attachmentsRemarksBoxErrorMessage = '';
      this.attachmentsPopupAttachments = [];

      this.attachmentsAction = 'Add';
      this.showAttachmentsPopup = true;
    },
    editAttachmentClicked: function (pointerEvent) {
      if (this.attachmentsTableBoxSelectedRow.length == 0) {
        this.$showAlert('error', 'No Attachment Selected', 'Please select an attachment to edit', [{ text: 'OK' }]);
        return;
      }

      this.attachmentsPopupTitle = 'Edit Attachment';
      this.attachmentsPopupButtons = [{ text: 'Edit' }, { text: 'Cancel', color: 'lightgray' }];
      this.attachmentsInputIsRequired = false;
      this.attachmentsInputErrorMessage = '';
      (this.attachmentsInputMultiFile = false),
        (this.attachmentsAttachedFileName = this.attachmentsTableBoxSelectedRow[0]['File Name']);
      this.attachmentsRemarksBoxValue = this.attachmentsTableBoxSelectedRow[0]['Remarks'];
      this.attachmentsRemarksBoxErrorMessage = '';
      this.attachmentsPopupAttachments = [];

      this.attachmentsAction = 'Edit';
      this.showAttachmentsPopup = true;
    },
    deleteAttachmentClicked: async function (pointerEvent) {
      if (this.attachmentsTableBoxSelectedRow.length == 0) {
        this.$showAlert('error', 'No Attachment Selected', 'Please select an attachment to delete', [{ text: 'OK' }]);
        return;
      }

      if (
        (await this.$showAlert(
          'warning',
          'Are you sure you want to delete this attachment?',
          `Attachment: ${this.attachmentsTableBoxSelectedRow[0]['File Name']}<br>Remarks: ${this.attachmentsTableBoxSelectedRow[0]['Remarks']}`,
          [
            { text: 'Delete', color: 'red', textColor: 'white' },
            { text: 'Cancel', color: 'lightgray' },
          ],
        )) == 'Cancel' // user clicked Cancel
      ) {
        return; // exit function
      }

      let tableBoxData;
      switch (this.attachmentsTableBoxSelectedRow[0]['_attachmentAction']) {
        case 'New': // newly added by user, not saved yet
          tableBoxData = this.attachmentsTableBoxData.filter((attachment) => {
            // just remove the row from the TableBox
            return attachment['ID'] != this.attachmentsTableBoxSelectedRow[0]['ID'];
          });
          break;

        case 'Edit':
        default: // user edited the saved policy to be deleted OR the saved policy is without any changes
          tableBoxData = [...this.attachmentsTableBoxData];
          tableBoxData[
            tableBoxData.findIndex((attachment) => {
              return attachment['ID'] == this.attachmentsTableBoxSelectedRow[0]['ID'];
            })
          ]['_attachmentAction'] = 'Delete';
          break;
      }
      this.attachmentsTableBoxData = [];
      this.attachmentsTableBoxSelectedRow = [];
      await this.$nextTick(() => {
        this.attachmentsTableBoxData = tableBoxData;
      });
    },
    viewAttachmentClicked: function (pointerEvent) {
      if (this.attachmentsTableBoxSelectedRow.length == 0) {
        this.$showAlert('error', 'No Attachment Selected', 'Please select an attachment to view', [{ text: 'OK' }]);
        return;
      }

      if (this.attachmentsTableBoxSelectedRow[0]['_file']) {
        this.projectAttachment(
          this.attachmentsTableBoxData.find((attachment) => {
            return attachment['ID'] == this.attachmentsTableBoxSelectedRow[0]['ID'];
          })['_file'],
        );
      } else {
        this.projectAttachment({
          'File Name': this.attachmentsTableBoxSelectedRow[0]['File Name'],
          'File Data': this.attachmentsTableBoxSelectedRow[0]['Attachment'],
        });
      }
    },
    attachmentsPopupFilesChanged: async function (files, placeholder) {
      this.attachmentsPopupAttachments = files;
      if (files.length > 0) {
        this.attachmentsInputErrorMessage = ''; // clear error message if at least 1 file was selected
      }
    },
    attachmentsPopupButtonClicked: async function (buttonText) {
      let tableBoxData;
      switch (buttonText) {
        case 'Add':
          let error = false;
          if (this.attachmentsPopupAttachments.length == 0) {
            this.attachmentsInputErrorMessage = 'Please attach at least one file';
            error = true;
          }
          if (this.attachmentsRemarksBoxValue.length > 200) {
            this.attachmentsRemarksBoxErrorMessage = 'Remarks cannot exceed 200 characters!';
            error = true;
          }

          if (error) return;

          let highestID = 0;
          for (let a of this.attachmentsTableBoxData) {
            if (a['ID'] > highestID) {
              highestID = a['ID'];
            }
          }

          tableBoxData = [...this.attachmentsTableBoxData];
          for (const file of this.attachmentsPopupAttachments) {
            tableBoxData.push({
              '#': '*',
              'File Name': file['name'],
              Remarks: this.attachmentsRemarksBoxValue,
              Attachment: '', // hidden column, leave blank, will convert file object into base64 in saveVehicle()
              'Updated on': '', // leave blank
              'Updated by': '', // leave blank
              _attachmentAction: 'New', // hidden column
              _file: file, // hidden column
              ID: parseInt(highestID) + 1, //hidden column
            });
          }
          this.attachmentsTableBoxData = [];
          this.attachmentsTableBoxSelectedRow = [];
          this.$nextTick(() => {
            this.attachmentsTableBoxData = tableBoxData;
            this.showAttachmentsPopup = false;
          });
          break;

        case 'Edit':
          if (this.attachmentsRemarksBoxValue.length > 200) {
            this.attachmentsRemarksBoxErrorMessage = 'Remarks cannot exceed 200 characters!';
            return;
          }

          const editedAttachmentIndex = this.attachmentsTableBoxData.findIndex((attachment) => {
            return attachment['ID'] == this.attachmentsTableBoxSelectedRow[0]['ID'];
          });
          const editedAttachment = { ...this.attachmentsTableBoxData[editedAttachmentIndex] };

          editedAttachment['#'] = '*';
          if (this.attachmentsPopupAttachments.length > 0) {
            // if a file was selected
            editedAttachment['File Name'] = this.attachmentsPopupAttachments[0]['name']; // replace the previous file
            editedAttachment['_file'] = this.attachmentsPopupAttachments[0];
            editedAttachment['Attachment'] = '';
          }
          editedAttachment['Remarks'] = this.attachmentsRemarksBoxValue;
          editedAttachment['_attachmentAction'] = editedAttachment['_attachmentAction'] == 'New' ? 'New' : 'Edit';

          tableBoxData = [...this.attachmentsTableBoxData];
          tableBoxData.splice(editedAttachmentIndex, 1, editedAttachment);
          this.attachmentsTableBoxData = [];
          this.attachmentsTableBoxSelectedRow = [];
          this.$nextTick(() => {
            this.showAttachmentsPopup = false;
            this.attachmentsTableBoxData = tableBoxData;
          });
          break;

        case 'Cancel':
          this.showAttachmentsPopup = false;
          break;
      }
    },
    projectAttachment: async function (file) {
      // file could be a file object or a JS object
      if (!file && process.env['NODE_ENV'] == 'development') {
        console.error('No file was passed to projectAttachment. Check for logic errors');
        console.trace();
        return;
      }

      this.$fullscreenLoading('Preparing file');
      if (file instanceof File) {
        // file object passed
        this.attachmentProjectorFileName = file['name'];
        this.attachmentProjectorType = file['type'];
        this.attachmentProjectorData = URL.createObjectURL(file);
      } else {
        // JS object passed
        this.attachmentProjectorFileName = file['File Name'];
        this.attachmentProjectorType =
          file['MIME Type'] ||
          (await this.$apiCallV2(`/Vehicle/GetMIMEType?filename=${this.attachmentProjectorFileName}`));

        const byteCharacters = atob(
          file['File Data'].startsWith('data:') ? file['File Data'].split(',')[1] : file['File Data'],
        );
        // if File Data is a data URL instead of just base64 data, then remove the prefix, MIME type and base64 indicator before converting to a string
        let untypedByteArray = [];
        for (let index = 0; index < byteCharacters.length; index++) {
          untypedByteArray.push(byteCharacters.charCodeAt(index));
        }

        const blob = new Blob([new Uint8Array(untypedByteArray)], { type: 'application/pdf' });
        this.attachmentProjectorData = URL.createObjectURL(blob);
      }

      this.showAttachmentProjector = true;
      this.$fullscreenLoading();
    },
    downloadAttachment: function () {
      const anchor = document.createElement('a');
      anchor['href'] = this.attachmentProjectorData;
      anchor['download'] = this.attachmentsAttachedFileName;
      anchor.click();
    },
    closeProjector: function (pointerEvent) {
      this.attachmentProjectorType = ''; // clear MIME Type and base64, because base64 is quite inefficient
      this.attachmentProjectorData = ''; // leaving it there after the projector is closed is quite wasteful
      this.showAttachmentProjector = false;
    },
    popupEntryRequiredClicked: function (requiredKey) {
      let errorTab = this.errorMapping.find((e) => e['key'] == requiredKey);

      if (errorTab) {
        this.selectedTabIndex = errorTab['tab'];
      }
      this.$nextTick(() => {
        this.$refs[requiredKey].setFocus();
      });
    },
    popupEntrySaveClicked: async function () {
      // clear PopupEntry message box
      this.popupEntryRequiredError = [];
      this.popupEntrySuccessMessages = [];
      this.popupEntryErrorMessages = [];

      if (this.vehicleAction === 'View') {
        await this.resetVehicleMileage();
        return;
      }

      let error = false;
      if (this.vehicleAction === 'Edit' && this.canResetVehicleMileage && this.mileageHasChanges && !/^[0-9]{1,7}$/.test(String(this.currentMileageInputValue ?? '').trim())) {
        this.currentMileageInputErrorMessage = 'Mileage must be a whole number from 0 to 9999999.';
        error = true;
      }

      if (this.yearInputValue && (this.yearInputValue < 1900 || this.yearInputValue > new Date().getFullYear())) {
        this.yearInputErrorMessage = `Invalid Year. Must be between 1900 to ${new Date().getFullYear()}`;
        error = true;
      }

      if (
        this.datePurchaseDateTimePickerValue &&
        (this.datePurchaseDateTimePickerValue < new Date('1900-01-01') ||
          this.datePurchaseDateTimePickerValue > new Date())
      ) {
        this.datePurchaseDateTimePickerErrorMessage = `Invalid Date. Must be between 1900-01-01 to ${new Date().formatString('yyyy-MM-dd')}`;
        error = true;
      }

      if (this.availabilityRadioValue?.value === '0' && !this.unavailabilityRemarkInputIsDisabled) {
        this.unavailabilityRemarkInputErrorMessage = 'Unavailability Remark is required!';
        this.popupEntryRequiredError.push({
          key: 'remark1',
          value: 'Unavailability Remark is required!',
        });
        error = !this.unavailabilityRemarkInputValue;
      }

      if (error) {
        // if there is at least one error, exit the function. this will allow all checks to run
        return;
      }

      this.$fullscreenLoading('Preparing Vehicle Data To Be Saved');
      const saveVehicleRequestBody = {
        action: this.vehicleAction.substring(0, 1), // only send the initial ("N" for "New", "E" for "Edit"), Delete is handled in menuClicked()

        availability: this.availabilityRadioValue?.value,
        remark1: (() => {
          let unavailabilityRemark = this.unavailabilityRemarkInputValue;
          if (this.availabilityRadioValue?.value === '1') unavailabilityRemark = '';
          else if (this.unavailabilityRemarkInputIsDisabled)
            unavailabilityRemark = this.vehicleDetails['Remark1']; //remain the remark if availability remains unavailabile
          else
            unavailabilityRemark = `INV\\${this.userDetails['LoginUserId']}: ${this.unavailabilityRemarkInputValue} - ${new Date().toLocaleDateString('en-GB')}`;

          return unavailabilityRemark; //return original remark
        })(),

        lorry_no: this.inventoryNoInputValue,
        vehiclecode: this.inventoryCodeInputValue,

        //#region general tab
        active: this.activeCheckBoxIsChecked ? '1' : '0', // create checkbox later
        vehiclecategory:
          this.categoryDropdownSelectedItem && Object.keys(this.categoryDropdownSelectedItem).length > 0 ?
            this.categoryDropdownSelectedItem['Category Code']
          : '',
        type:
          this.categoryDropdownSelectedItem && Object.keys(this.categoryDropdownSelectedItem).length > 0 ?
            this.categoryDropdownSelectedItem['Type']
          : '',
        year: this.yearInputValue,
        datepurchase:
          this.datePurchaseDateTimePickerValue ? this.datePurchaseDateTimePickerValue.formatString('yyyy-MM-dd') : '',
        color: this.colourInputValue,
        netweight: this.weightInputValue,
        lorry_length: this.lengthInputValue,
        lorry_width: this.widthInputValue,
        height: this.heightInputValue,
        maxcapacity: this.maxCapacityInputValue,
        winchcapacity: this.winchCapacityInputValue,
        groundclearance: this.groundClearanceInputValue,
        deptype:
          (
            this.depreciationTypeDropdownSelectedItem &&
            Object.keys(this.depreciationTypeDropdownSelectedItem).length > 0
          ) ?
            this.depreciationTypeDropdownSelectedItem['Type']
          : '',
        lorry_model: this.vehicleModelInputValue,
        trailer_no:
          this.trailerInputQuerySelectedObject && Object.keys(this.trailerInputQuerySelectedObject).length > 0 ?
            this.trailerInputQuerySelectedObject['Trailer No']
          : '',
        trailer_model:
          this.trailerInputQuerySelectedObject && Object.keys(this.trailerInputQuerySelectedObject).length > 0 ?
            this.trailerInputQuerySelectedObject['Model']
          : '',
        made:
          this.vehicleMadeInDropdownSelectedItem && Object.keys(this.vehicleMadeInDropdownSelectedItem).length > 0 ?
            this.vehicleMadeInDropdownSelectedItem['Country Code']
          : '',
        company_code:
          this.companyDropdownSelectedItem && Object.keys(this.companyDropdownSelectedItem).length > 0 ?
            this.companyDropdownSelectedItem['Company Code']
          : '',
        ownership:
          this.ownershipDropdownSelectedItem && Object.keys(this.ownershipDropdownSelectedItem).length > 0 ?
            this.ownershipDropdownSelectedItem['Ownership']
          : '',
        supplier:
          this.supplierInputQuerySelectedObject && Object.keys(this.supplierInputQuerySelectedObject).length > 0 ?
            this.supplierInputQuerySelectedObject['Supplier Code']
          : '',
        price: this.priceInputValue,
        financeby: this.loanFromInputValue,
        regcardno: this.regCardNoInputValue,
        regplace: this.regPlaceInputValue,
        openingmeter: this.openingMileageInputValue,
        team:
          this.teamDropdownSelectedItem && Object.keys(this.teamDropdownSelectedItem).length > 0 ?
            this.teamDropdownSelectedItem['Team']
          : '',
        sideloader: this.sideloaderCheckBoxIsChecked ? '1' : '0',
        //#endregion

        //#region engine information tab
        engineno: this.engineNoInputValue,
        chassisno: this.chassisNoInputValue,
        turbochargerno: this.turboChargerNoInputValue,
        fuel:
          this.fuelDropdownSelectedItem && Object.keys(this.fuelDropdownSelectedItem).length > 0 ?
            this.fuelDropdownSelectedItem['Type']
          : '',
        horsepower: this.horsePowerInputValue,
        enginemodel: this.engineModelInputValue,
        bdm: this.BDMInputValue,
        btt: this.BTTInputValue,
        kerbweight: this.kerbWeightInputValue,
        btm: this.BTMInputValue,
        bg1: this.BG1InputValue,
        bg2: this.BG2InputValue,
        bg3: this.BG3InputValue,
        bg4: this.BG4InputValue,
        bg5: this.BG5InputValue,
        bg6: this.BG6InputValue,
        bg7: this.BG7InputValue,
        bg8: this.BG8InputValue,
        bg9: this.BG9InputValue,
        bg10: this.BG10InputValue,
        bg11: this.BG11InputValue,
        bg12: this.BG12InputValue,
        bg13: '', // ignore for now
        //#endregion
      };

      //#region renewal info tab
      const policies = [];
      for (const policy of this.renewalInfoTableBoxData) {
        if (!policy['_policyAction']) {
          // no actions were performed on this policy
          continue;
        }

        const requestBody = {
          policyaction: policy['_policyAction'].substring(0, 1), // send initial only, "N" | "E" | "D"
          type: policy['_policyType'],
        };

        if (requestBody['policyaction'] == 'E' || requestBody['policyaction'] == 'D') {
          // if action is Edit or Delete
          requestBody['seqno'] = policy['SeqNo']; // assign SeqNo to seqno field, else don't create the property at all

          if (requestBody['policyaction'] == 'D') {
            // if action is Delete
            requestBody['policyno'] = policy['Ref #'];
            policies.push(requestBody);
            continue; // no need to populate other properties, _renewalInfoInputData may be null as well so it might not be able to populate at all
          }
        }

        await this.getRenewalInfoControls(policy['Type']); // get controls from API, if already exists function will just exit instead
        for (const control of this.renewalInfoControls[policy['Type']]) {
          if (!control['sendkey']) {
            // some field values do not need to be sent to the API, e.g. Duration (Months)
            continue;
          }

          if (control['type'] == 'DateTimePicker') {
            requestBody[control['sendkey']] =
              policy['_renewalInfoInputData'][control['#']]['value'] ?
                new Date(policy['_renewalInfoInputData'][control['#']]['value']).formatString('yyyy-MM-dd')
              : '';
          } else {
            requestBody[control['sendkey']] = policy['_renewalInfoInputData'][control['#']]['value'];
          } // input data is stored in a hidden column in the TableBox data
        }

        policies.push(requestBody);
      }

      if (policies.length > 0) {
        saveVehicleRequestBody['policy'] = policies;
      }
      //#endregion

      //#region maintenance tab
      const services = [];
      for (const service of this.maintenanceTableBoxData) {
        if (!service['_serviceAction']) {
          // no actions were performed on this policy
          continue;
        }

        const requestBody = {
          serviceaction: service['_serviceAction'].substring(0, 1), // send initial only
          servicecode: service['ServiceCode'],
          servariant: service['Interval'],
          mileage: '', // fill in later
          duration: '', // fill in later
          repetitive: service['Occurence'],
          expiredate: service['Occurence'] == '1' ? new Date(service['ExpireDate']).formatString('yyyy-MM-dd') : '',
          remark: service['Remark'],
          task: service['Service Task'],
          distid: '0',
          attachment: service['_file'] ? service['_file']['name'] : '',
          image1: service['_file'] ? (await service['_file'].toBase64()).split(',')[1] : '', // send base64 data only, not the entire data URL
        };

        if (requestBody['serviceaction'] == 'E' || requestBody['serviceaction'] == 'D') {
          // if action is Edit or Delete
          requestBody['seqno'] = service['SeqNo'];
        }

        switch (service['Interval']) {
          case '0':
            requestBody['mileage'] = service['Mileage'];
            break;

          case '1':
          case '2':
            requestBody['duration'] = service['Duration'];
            break;

          case '3':
            requestBody['mileage'] = service['Mileage'];
            requestBody['duration'] = service['Duration'];
        }

        services.push(requestBody);
      }
      if (services.length > 0) {
        saveVehicleRequestBody['service'] = services;
      }
      //#endregion

      //#region tyre configuration tab
      const tyres = [];
      for (const tyre of this.tyreConfigurationTableBoxData) {
        if (!tyre['_tyreAction']) {
          // no actions were performed on this tyre
          continue;
        }

        const requestBody = {
          tyreaction: tyre['_tyreAction'].substring(0, 1), // send initial only
          tyretype: tyre['Tyre Type'] ?? tyre['TyreType'],
          serialno: tyre['Serial No'],
          tyresize: tyre['Size'],
          mfgdate: tyre['Mfg Date'],
          datechange: tyre['Date Change'],
          model: tyre['Model'],
          manufacturer: tyre['Manufacturer'],
          madein: tyre['MadeIn'],
          remark: tyre['Remark'],
          align: tyre['Align'],
          columnno: tyre['_colNo'],
          rowno: tyre['_rowNo'],
          inuse: tyre['In Use'],
          tyreindex: tyre['TyreIndex'],
          tyreposition: tyre['Position'],
          startmileage: this.vehicleDetails['MeterReading'], // read from GetDetailsMaster
        };

        if (requestBody['tyreaction'] == 'E' || requestBody['tyreaction'] == 'D') {
          // if action is Edit or Delete
          requestBody['seqno'] = tyre['SeqNo'];
          requestBody['tyreguid'] = tyre['TyreGUID'];
        }

        tyres.push(requestBody);
      }

      if (tyres.length > 0) {
        saveVehicleRequestBody['tyre'] = tyres;
      }

      //#endregion

      //#region attachments tab
      const attachments = [];
      for (const attachment of this.attachmentsTableBoxData) {
        if (!attachment['_attachmentAction']) {
          // no actions were performed on this attachment
          continue;
        }

        const requestBody = {
          attachmentaction: attachment['_attachmentAction'].substring(0, 1), // send initial only
          filename: attachment['File Name'],
          remarks: attachment['Remarks'],
        };

        if (attachment['_file'] instanceof File) {
          // if _file is blank, it means that the user edited the remarks but left the file input blank, leaving out the attachment field will retain the binary data on the database and make no changes to the file
          requestBody['attachment'] = (await attachment['_file'].toBase64()).split(',')[1]; // send base64 data only, not the entire data URL
        }

        if (requestBody['attachmentaction'] == 'E' || requestBody['attachmentaction'] == 'D') {
          // if action is Edit or Delete
          requestBody['id'] = attachment['ID'];
        }

        attachments.push(requestBody);
      }
      if (attachments.length > 0) {
        saveVehicleRequestBody['attachment'] = attachments;
      }
      //#endregion

      this.$fullscreenLoading('Checking Vehicle Data');
      const validity = await this.$apiCallV2('/Vehicle/CheckDataValidity', saveVehicleRequestBody);
      let entryRequiredMessages = [];
      if (validity) {
        // there are errors
        let key = '';
        // populate errorMessage fields for associated controls
        for (const error of validity['error']) {
          // needs to be done this way because i created separate data fields for each error message
          if (key == error['Key']) continue; // Only take the first error message for each key. Since there may be a possibilty for two error messages at one time. For example, format error & value too big.
          key = error['Key'];
          entryRequiredMessages.push(error);
          switch (
            key // basically just map the key to their respective control error message fields
          ) {
            case 'availability':
              this.availabilityRadioErrorMessage = error['Value'];
              break;

            case 'remark1':
              this.unavailabilityRemarkInputErrorMessage = error['Value'];
              break;
            case 'lorry_no':
              this.inventoryNoInputErrorMessage = error['Value'];
              break;
            case 'vehiclecode':
              this.inventoryCodeInputErrorMessage = error['Value'];
              break;

            case 'vehiclecategory':
              this.categoryDropdownErrorMessage = error['Value'];
              break;
            // case 'type':
            //   // If vehicle type is empty:
            //   // If there is a vehicle category error message, leave the error message as is (Since category not specified).
            //   // "Vehicle Category is required!"
            //   // Else, set it as
            //   // "Vehicle Type is required!"
            //   // This does not affect the required error messages at the bottom, only the error message for the field.
            //   if (!this.categoryDropdownErrorMessage) {
            //     this.categoryDropdownErrorMessage = error['Value'];
            //   }
            //   break;
            //remove check for vehicle type, this can be empty

            case 'year':
              this.yearInputErrorMessage = error['Value'];
              break;
            case 'color':
              this.colourInputErrorMessage = error['Value'];
              break;
            case 'netweight':
              this.weightInputErrorMessage = error['Value'];
              break;
            case 'lorry_width':
              this.widthInputErrorMessage = error['Value'];
              break;
            case 'lorry_length':
              this.lengthInputErrorMessage = error['Value'];
              break;
            case 'height':
              this.heightInputErrorMessage = error['Value'];
              break;
            case 'maxcapacity':
              this.maxCapacityInputErrorMessage = error['Value'];
              break;
            case 'winchcapacity':
              this.winchCapacityInputErrorMessage = error['Value'];
              break;
            case 'groundclearance':
              this.groundClearanceInputErrorMessage = error['Value'];
              break;
            case 'company_code':
              this.companyDropdownErrorMessage = error['Value'];
              break;
            case 'lorry_model':
              this.vehicleModelInputErrorMessage = error['Value'];
              break;
            case 'price':
              this.priceInputErrorMessage = error['Value'];
              break;
            case 'financeby':
              this.loanFromInputErrorMessage = error['Value'];
              break;
            case 'regcardno':
              this.regCardNoInputErrorMessage = error['Value'];
              break;
            case 'regplace':
              this.regPlaceInputErrorMessage = error['Value'];
              break;
            case 'openingmeter':
              this.openingMileageInputErrorMessage = error['Value'];
              break;

            case 'engineno':
              this.engineNoInputErrorMessage = error['Value'];
              break;
            case 'chassisno':
              this.chassisNoInputErrorMessage = error['Value'];
              break;
            case 'turbochargerno':
              this.turboChargerNoInputErrorMessage = error['Value'];
              break;
            case 'horsepower':
              this.horsePowerInputErrorMessage = error['Value'];
              break;
            case 'enginemodel':
              this.engineModelInputErrorMessage = error['Value'];
              break;
            case 'bdm':
              this.BDMInputErrorMessage = error['Value'];
              break;
            case 'btt':
              this.BTTInputErrorMessage = error['Value'];
              break;
            case 'kerbweight':
              this.kerbWeightInputErrorMessage = error['Value'];
              break;
            case 'btm':
              this.BTMInputErrorMessage = error['Value'];
              break;
            case 'bg1':
              this.BG1InputErrorMessage = error['Value'];
              break;
            case 'bg2':
              this.BG2InputErrorMessage = error['Value'];
              break;
            case 'bg3':
              this.BG3InputErrorMessage = error['Value'];
              break;
            case 'bg4':
              this.BG4InputErrorMessage = error['Value'];
              break;
            case 'bg5':
              this.BG5InputErrorMessage = error['Value'];
              break;
            case 'bg6':
              this.BG6InputErrorMessage = error['Value'];
              break;
            case 'bg7':
              this.BG7InputErrorMessage = error['Value'];
              break;
            case 'bg8':
              this.BG8InputErrorMessage = error['Value'];
              break;
            case 'bg9':
              this.BG9InputErrorMessage = error['Value'];
              break;
            case 'bg10':
              this.BG10InputErrorMessage = error['Value'];
              break;
            case 'bg11':
              this.BG11InputErrorMessage = error['Value'];
              break;
            case 'bg12':
              this.BG12InputErrorMessage = error['Value'];
              break;
          }
        }

        const vehicleTypeErrorIndex = entryRequiredMessages.findIndex((error) => {
          return error['Key'] == 'type';
        });
        if (vehicleTypeErrorIndex != -1) {
          // there is no control for vehicle type, it's combined with vehicle category
          entryRequiredMessages[vehicleTypeErrorIndex]['Key'] = 'vehiclecategory'; // change the key to vehiclecategory
        }

        this.popupEntryRequiredError = entryRequiredMessages; // populate popup entry message box
        this.$fullscreenLoading();
        return;
      }

      if (this.vehicleAction === 'Edit' && this.canResetVehicleMileage && this.mileageHasChanges) {
        saveVehicleRequestBody.updatemileage = true;
        saveVehicleRequestBody.meterreading = String(this.currentMileageInputValue).trim();
        saveVehicleRequestBody.expectedmeterreading = this.vehicleDetails.RawMeterReading ?? (this.vehicleDetails.MeterReading || 0);
      }
      this.$fullscreenLoading('Saving Vehicle');
      const saveVehicleResult = (await this.$apiCallV2('/Vehicle/SaveVehicle', saveVehicleRequestBody))[0];
      if (saveVehicleResult['Results'] != 0) {
        // there are errors
        this.popupEntryErrorMessages = [saveVehicleResult['ErrMsg']];
        this.$fullscreenLoading();
        return;
      }

      if (this.vehicleAction == 'New' && this.vehicleTodoDraftItems.length > 0) {
        const todoPayload = this.vehicleTodoDraftItems
          .filter((todo) => todo['_recordState'] != 'C' && todo['Status'] != 'Cancelled')
          .map((todo) => ({
            action: 'N',
            inventoryno: saveVehicleResult['Lorry_No'],
            taskname: todo['Task Name'],
            duedate: todo['Due Date'] || '',
            completiondate: todo['Status'] == 'Completed' ? todo['Completion Date'] : '',
            remarks: todo.Remarks || '',
            jobno: '',
          }));
        if (todoPayload.length > 0) {
          const todoResult = await this.$apiCallV2('/Vehicle/SaveVehicleTodo', { todos: todoPayload });
          const todoErrors = (Array.isArray(todoResult) ? todoResult : []).filter(
            (todo) => todo.Results == '1' || todo.Result == 'Failed',
          );
          if (todoErrors.length > 0) {
            this.popupEntryErrorMessages.push(
              `Some Task items could not be saved: ${todoErrors.map((todo) => todo.ErrMsg || todo.Details || 'Save failed').join('; ')}`,
            );
          }
        }
      }

      this.disablePopupEntryFields();
      this.popupEntrySuccessMessages = [
        `Vehicle ${saveVehicleResult['Lorry_No']} successfully ${this.vehicleAction == 'New' ? 'created' : 'edited'}`,
      ];
      await this.getVehicleDetails(saveVehicleResult['Lorry_No']);
      await this.populatePopupEntryFields();
      this.vehicleAction = 'View';
      this.popupEntryTitle = 'View Vehicle';
      this.popupEntryInViewMode = true;
      this.$fullscreenLoading();
      await this.refreshVehicleData();
    },
    reassignSequentialNumbers(data) {
      if (!Array.isArray(data) || data.length === 0) return data;

      return data.map((row, index) => ({
        ...row,
        '#': index + 1,
      }));
    },
    batchAvailabilityMenuClicked: function (menu) {
      if (!menu || (menu.title != 'Mark Available' && menu.title != 'Mark Unavailable')) return;
      if (this.batchAvailabilityTableBoxSelectedRow.length <= 0) {
        this.$showAlert('error', 'Vehicle Required', 'Please select at least 1 vehicle before marking availability', [
          { text: 'OK' },
        ]);
        return;
      }
      this.batchAvailabilityPopupMode = menu.title == 'Mark Available' ? 'A' : 'U';
      this.batchAvailabilityRemarkInputValue = '';
      this.batchAvailabilityRemarkInputErrorMessage = '';
      this.batchAvailabilityActionPopupShow = true;
    },
    batchAvailabilityActionPopupButtonClicked: function (button) {
      if (button == 'Cancel' || button == 'Close') {
        this.batchAvailabilityActionPopupShow = false;
        this.batchAvailabilityRemarkInputValue = '';
        this.batchAvailabilityRemarkInputErrorMessage = '';
        return;
      }
      if (button == 'Mark Available' || button == 'Mark Unavailable') this.batchUpdateAvailability();
    },
    closeBatchAvailabilityPopup: function () {
      this.batchAvailabilityPopupShow = false;
      this.batchAvailabilityActionPopupShow = false;
      this.batchAvailabilityTableBoxSelectedRow = [];
      this.batchAvailabilityRemarkInputValue = '';
      this.batchAvailabilityRemarkInputErrorMessage = '';
    },
    batchUpdateAvailability: async function () {
      const selectedVehiclesIDs = this.batchAvailabilityTableBoxSelectedRow.map((row) => row['Inventory #']);

      if (selectedVehiclesIDs.length <= 0) {
        this.$showAlert('error', 'Vehicle Required', 'Please select at least 1 vehicle to update availability.', [
          { text: 'OK' },
        ]);
        return;
      }
      let remark = '';

      if (this.batchAvailabilityPopupMode === 'U') {
        if (!this.batchAvailabilityRemarkInputValue) {
          this.batchAvailabilityRemarkInputErrorMessage = 'Please enter a remark for Unavailable vehicles.';
          return;
        }
        remark = `INV\\${this.userDetails['LoginUserId']}: ${this.batchAvailabilityRemarkInputValue} - ${new Date().toLocaleDateString('en-GB')}`;
      }

      const requestBody = {
        availabilityaction: this.batchAvailabilityPopupMode === 'A' ? '1' : '0', // 1 for Available, 0 for Unavailable
        vehiclelist: selectedVehiclesIDs,
        unavailabilityremarks: remark,
      };

      this.$fullscreenLoading('Updating Vehicles Availability');
      const results = (await this.$apiCallV2('/Vehicle/BatchUpdateAvailability', requestBody))[0];
      this.$fullscreenLoading();

      if (results['Results'] === '1') {
        this.$showAlert('error', 'Update Failed!', `Error: ${results['ErrMsg']}`);
        return;
      }

      this.batchAvailabilityActionPopupShow = false;
      this.batchAvailabilityTableBoxSelectedRow = [];
      this.batchAvailabilityRemarkInputValue = '';
      this.batchAvailabilityRemarkInputErrorMessage = '';
      this.$showAlert(
        'success',
        'Update Successful!',
        `The availability of ${selectedVehiclesIDs.length} vehicles has been updated successfully.`,
      );
      await this.refreshVehicleData();
    },

    showAllClicked() {
      const table = this.renewalInfoTableBoxData;
      this.renewalInfoTableBoxData = [];
      this.$nextTick(() => {
        this.renewalInfoTableBoxData = table;
      });
    },

    openPMToDriverPopup: async function () {
      this.pmToDriverAction = '';
      this.pmToDriverFieldsAreDisabled = true;
      this.pmToDriverTableBoxSelectedRow = [];
      this.pmToDriverTableBoxData = [];
      this.clearPMToDriverFields();

      this.showPMToDriverPopupEntry = true;
      this.pmToDriverTableBoxIsLoading = true;
      this.$fullscreenLoading('Getting PM to Driver Details');

      try {
        const pmToDriverDataPromises = [];

        pmToDriverDataPromises.push(
          this.$apiCallV2('/Master/GetListMaster', {
            masterfile: 'GETDRIVERVEHICLE',
          }).then((result) => {
            let assignmentData = result || [];

            assignmentData = this.$jsonNullToStringV2(assignmentData);
            assignmentData = this.$deepTrimJsonV3(assignmentData);

            if (!Array.isArray(assignmentData)) {
              this.pmToDriverTableBoxData = [];
              this.pmToDriverTableBoxKey += 1;
              return;
            }

            this.pmToDriverTableBoxData = assignmentData.map((item) => ({
              ...item,
            }));
            this.pmToDriverTableBoxKey += 1;
          }),
        );

        pmToDriverDataPromises.push(
          this.$apiCallV2('/Master/GetListMaster', {
            masterfile: 'TEAM',
          }).then((result) => {
            let teamData = result || [];

            teamData = this.$jsonNullToStringV2(teamData);
            teamData = this.$deepTrimJsonV3(teamData);

            if (!Array.isArray(teamData)) {
              this.pmToDriverTableBoxData = [];
              return;
            }

            this.pmToDriverPMTeamDropdownItems = teamData.map((item) => ({
              ...item,
              'Code - Description': `${item['Team']} - ${item['Description']}`,
            }));
          }),
        );

        pmToDriverDataPromises.push(
          await this.$apiCallV2(`/Vehicle/GetVehicleListByCategory?vehcat=PM`).then((result) => {
            let pmData = result || [];

            pmData = this.$jsonNullToStringV2(pmData);
            pmData = this.$deepTrimJsonV3(pmData);

            if (!Array.isArray(pmData)) {
              this.pmToDriverPMDropdownItems = [];
              return;
            }

            this.pmToDriverPMDropdownItems = pmData.map((item) => ({
              ...item,
              'Code - Description': `${item['VehicleCode']} - ${item['VehicleNo']}`,
            }));
          }),
        );

        pmToDriverDataPromises.push(
          this.$apiCallV2('/VehicleTracker/GetEmployeeListByDepartment').then((result) => {
            let driversData = result || [];

            driversData = this.$jsonNullToStringV2(driversData);
            driversData = this.$deepTrimJsonV3(driversData);

            if (!Array.isArray(driversData)) {
              this.pmToDriverDriverDropdownItems = [];
              return;
            }

            this.pmToDriverDriverDropdownItems = driversData.map((driver) => ({
              ...driver,
              'Code - Description': `${driver['Staff_No']} - ${driver['Staff_Name']}`,
            }));
          }),
        );

        await Promise.all(pmToDriverDataPromises);
      } catch (error) {
        this.pmToDriverTableBoxData = [];
        this.pmToDriverTableBoxKey += 1;
        this.pmToDriverPMDropdownItems = [];
        this.pmToDriverDriverDropdownItems = [];

        this.$showAlert(
          'error',
          'Unable to Get PM to Driver Data',
          error.message || 'An error occurred while getting PM-to-driver data.',
          [{ text: 'OK' }],
        );
      } finally {
        this.pmToDriverTableBoxIsLoading = false;
        this.$fullscreenLoading();
      }
    },
    clearPMToDriverFields: function () {
      this.pmToDriverPMDropdownSelectedItem = null;
      this.pmToDriverPMTeamDropdownSelectedItem = null;
      this.pmToDriverDriver1DropdownSelectedItem = null;
      this.pmToDriverDriver2DropdownSelectedItem = null;
      this.pmToDriverHasDriver2 = false;
    },
    populatePMToDriverFields: function (row) {
      if (!row) {
        this.clearPMToDriverFields();
        return;
      }

      this.pmToDriverPMDropdownSelectedItem = this.pmToDriverPMDropdownItems.find(
        (item) => item['VehicleCode'] === row['Vehicle'],
      );
      this.pmToDriverPMTeamDropdownSelectedItem = this.pmToDriverPMTeamDropdownItems.find(
        (item) => item['Team'] === this.pmToDriverPMDropdownSelectedItem?.['Team'],
      );
      this.pmToDriverDriver1DropdownSelectedItem = this.pmToDriverDriverDropdownItems.find((i) => {
        return i['Staff_No'] === row['Driver Code'];
      });
      this.pmToDriverHasDriver2 = Boolean(row['Driver 2 Code']);
      this.pmToDriverDriver2DropdownSelectedItem = this.pmToDriverDriverDropdownItems.find((i) => {
        return i['Staff_No'] === row['Driver 2 Code'];
      });
    },

    pmToDriverAddClicked: function () {
      this.pmToDriverTableBoxSelectedRow = [];
      this.clearPMToDriverFields();

      this.pmToDriverAction = 'New';
      this.pmToDriverFieldsAreDisabled = false;
    },
    pmToDriverEditClicked: function () {
      if (this.pmToDriverTableBoxSelectedRow.length !== 1) return;

      this.populatePMToDriverFields(this.pmToDriverTableBoxSelectedRow[0]);

      this.pmToDriverAction = 'Edit';
      this.pmToDriverFieldsAreDisabled = false;
    },
    pmToDriverDeleteClicked: async function () {
      if (this.pmToDriverTableBoxSelectedRow.length !== 1) return;
      const selectedRow = this.pmToDriverTableBoxSelectedRow[0];
      const confirmDelete = await this.$showAlert(
        'warning',
        'Delete PM to Driver Relationship',
        `Delete the relationship for ${selectedRow['Vehicle']} (${selectedRow['Vehicle #']})?`,
        [
          { text: 'Delete', color: 'red', textColor: 'white' },
          { text: 'Cancel', color: 'lightgray' },
        ],
      );
      if (confirmDelete === 'Cancel') return;
      const requestBody = {
        action: 'D',
        vehiclecode: String(selectedRow['Vehicle'] || '').trim(),
        vehiclenumber: String(selectedRow['Vehicle #'] || '').trim(),
        originalvehiclenumber: String(selectedRow['Vehicle #'] || '').trim(),
        drivercode: String(selectedRow['Driver Code'] || '').trim(),
        attendantcode: String(selectedRow['Driver 2 Code'] || '').trim(),
      };
      const result = await this.$apiCallV2('/Vehicle/SaveDriverVehicle', requestBody);
      if (result && result[0] && result[0]['Results'] === '1') {
        this.$showAlert('error', 'Delete Failed', result[0]['ErrMsg']);
        return;
      }
      this.pmToDriverTableBoxSelectedRow = [];
      await this.openPMToDriverPopup();
      this.$showAlert('success', 'Deleted', result?.[0]?.['ErrMsg'] || 'Relationship deleted successfully.');
    },
    pmToDriverSaveClicked: async function () {
      const mode = this.pmToDriverAction;
      if (!this.pmToDriverPMDropdownSelectedItem) {
        this.$showAlert('warning', 'PM Required', 'Please select a prime mover.');
        return;
      }

      if (!this.pmToDriverDriver1DropdownSelectedItem) {
        this.$showAlert('warning', 'Driver Required', 'Please select Driver 1.');
        return;
      }

      if (this.pmToDriverHasDriver2 && !this.pmToDriverDriver2DropdownSelectedItem) {
        this.$showAlert('warning', 'Driver 2 Required', 'Please select Driver 2 or untick Driver 2.');
        return;
      }

      const drivercode = String(this.pmToDriverDriver1DropdownSelectedItem['Staff_No'] || '').trim();
      const attendantcode = this.pmToDriverHasDriver2 ? String(this.pmToDriverDriver2DropdownSelectedItem['Staff_No'] || '').trim() : '';

      if (attendantcode && drivercode === attendantcode) {
        this.$showAlert('warning', 'Duplicate Driver', 'Driver 1 and Driver 2 cannot be the same.');
        return;
      }

      const selectedRow = this.pmToDriverTableBoxSelectedRow[0] || {};
      const requestBody = {
        action: mode === 'New' ? 'N' : 'E',
        vehiclecode: String(this.pmToDriverPMDropdownSelectedItem['VehicleCode'] || '').trim(),
        vehiclenumber: String(this.pmToDriverPMDropdownSelectedItem['VehicleNo'] || '').trim(),
        originalvehiclenumber: mode === 'Edit' ? String(selectedRow['Vehicle #'] || selectedRow['VehicleNo'] || '').trim() : '',
        drivercode,
        attendantcode,
      };

      this.$fullscreenLoading(
        mode === 'New' ? 'Creating PM to Driver Relationship' : 'Updating PM to Driver Relationship',
      );
      const result = await this.$apiCallV2('/Vehicle/SaveDriverVehicle', requestBody);
      this.$fullscreenLoading();

      if (result && result[0] && result[0]['Results'] === '1') {
        this.$showAlert('error', 'Save Failed', result[0]['ErrMsg']);
        return;
      }

      this.pmToDriverAction = '';
      this.pmToDriverFieldsAreDisabled = true;
      this.pmToDriverTableBoxSelectedRow = [];

      await this.openPMToDriverPopup();
      this.$showAlert(
        'success',
        mode === 'New' ? 'Created' : 'Updated',
        result?.[0]?.['ErrMsg'] || 'PM to Driver relationship saved successfully.',
      );
    },
    pmToDriverCancelClicked: function () {
      this.pmToDriverAction = '';
      this.pmToDriverFieldsAreDisabled = true;

      if (this.pmToDriverTableBoxSelectedRow.length === 1) {
        this.populatePMToDriverFields(this.pmToDriverTableBoxSelectedRow[0]);
      } else {
        this.clearPMToDriverFields();
      }
    },

    openTeamManagementPopup: async function () {
      this.showTeamManagementAddTeamPanel = false;
      this.showTeamManagementPopupEntry = true;

      if (!this.teamManagementForDropdownSelectedItem) {
        this.teamManagementForDropdownSelectedItem = this.teamManagementForDropdownItems[0];
      }

      await this.teamManagementForSelected(this.teamManagementForDropdownSelectedItem);
    },
    teamManagementAssignClicked: function () {
      if (!this.teamManagementSelectedTeam || !this.teamManagementSelectedTeam['Team']) {
        return;
      }

      if (this.teamManagementAvailableSelectedRows.length === 0) {
        return;
      }

      const selectedTeam = this.teamManagementSelectedTeam['Team'];
      const selectedKeys = new Set(
        this.teamManagementAvailableSelectedRows.map((pm) => this.getTeamManagementPMKey(pm)),
      );

      const movedPMs = this.teamManagementAvailablePMs
        .filter((pm) => {
          return selectedKeys.has(this.getTeamManagementPMKey(pm));
        })
        .map((pm) => ({
          ...pm,
          Team: selectedTeam,
        }));

      this.teamManagementAvailablePMs = this.teamManagementAvailablePMs.filter((pm) => {
        return !selectedKeys.has(this.getTeamManagementPMKey(pm));
      });

      this.teamManagementAssignedPMs = this.removeDuplicateTeamManagementPMs([
        ...this.teamManagementAssignedPMs,
        ...movedPMs,
      ]);

      this.rebuildTeamManagementAllPMs();

      this.teamManagementAvailableSelectedRows = [];
      this.teamManagementAssignedSelectedRows = [];
    },
    teamManagementRemoveClicked: function () {
      if (this.teamManagementAssignedSelectedRows.length === 0) {
        return;
      }

      const selectedKeys = new Set(
        this.teamManagementAssignedSelectedRows.map((pm) => this.getTeamManagementPMKey(pm)),
      );

      const movedPMs = this.teamManagementAssignedPMs
        .filter((pm) => {
          return selectedKeys.has(this.getTeamManagementPMKey(pm));
        })
        .map((pm) => ({
          ...pm,
          Team: '',
        }));

      this.teamManagementAssignedPMs = this.teamManagementAssignedPMs.filter((pm) => {
        return !selectedKeys.has(this.getTeamManagementPMKey(pm));
      });

      this.teamManagementAvailablePMs = this.removeDuplicateTeamManagementPMs([
        ...this.teamManagementAvailablePMs,
        ...movedPMs,
      ]);

      this.rebuildTeamManagementAllPMs();

      this.teamManagementAvailableSelectedRows = [];
      this.teamManagementAssignedSelectedRows = [];
    },
    teamManagementSaveClicked: async function () {
      const changedPMs = this.teamManagementAllPMs
        .filter((pm) => {
          const pmCode = this.getTeamManagementPMKey(pm);
          const originalTeam = this.teamManagementOriginalTeams[pmCode] || '';
          return originalTeam !== String(pm['Team'] || '').trim();
        })
        .map((pm) => {
          const pmCode = this.getTeamManagementPMKey(pm);
          return {
            vehiclecode: pmCode,
            vehiclenumber: String(pm['PM #'] || '').trim(),
            originalteam: String(this.teamManagementOriginalTeams[pmCode] || '').trim(),
            team: String(pm['Team'] || '').trim(),
          };
        });
      if (changedPMs.length === 0) {
        this.$showAlert('warning', 'No Changes', 'No Team assignments were changed.');
        return;
      }

      const requestBody = {
        action: 'E',
        module: String(this.getSelectedTeamManagementModule() || '').trim(),
        vehicles: changedPMs,
      };

      this.teamManagementIsLoading = true;
      try {
        const result = await this.$apiCallV2('/Vehicle/SaveTeamManagement', requestBody);
        if (result && result[0] && result[0]['Results'] === '1') {
          this.$showAlert('error', 'Save Failed', result[0]['ErrMsg']);
          return;
        }

        await this.teamManagementForSelected(this.teamManagementForDropdownSelectedItem);
        this.$showAlert('success', 'Saved', result?.[0]?.['ErrMsg'] || 'Team Management saved successfully.');
      } catch (error) {
        this.$showAlert('error', 'Save Failed', error.message || 'Unable to save Team Management.');
      } finally {
        this.teamManagementIsLoading = false;
      }
    },
    teamManagementTeamSelected: async function (selectedTeam) {
      const team = selectedTeam && typeof selectedTeam === 'object' ? selectedTeam : this.teamManagementSelectedTeam;

      this.teamManagementAssignedSelectedRows = [];
      this.teamManagementAssignedPMs = [];

      if (!team || !team['Team']) {
        return;
      }

      await this.getPMsWithTeam(team['Team']);
    },
    teamManagementForSelected: async function (selectedOption) {
      if (selectedOption && typeof selectedOption === 'object') {
        this.teamManagementForDropdownSelectedItem = selectedOption;
      }

      this.teamManagementIsLoading = true;
      this.teamManagementAssignedIsLoading = true;

      this.teamManagementErrorMessages = [];
      this.teamManagementSuccessMessages = [];

      this.teamManagementTeamItems = [];
      this.teamManagementSelectedTeam = null;

      this.teamManagementAvailableSelectedRows = [];
      this.teamManagementAssignedSelectedRows = [];

      this.teamManagementAvailablePMs = [];
      this.teamManagementAssignedPMs = [];
      this.teamManagementAllPMs = [];
      this.teamManagementOriginalTeams = {};

      try {
        await Promise.all([this.getTeamManagementTeams(), this.getPMsWithoutTeam()]);

        if (this.teamManagementSelectedTeam && this.teamManagementSelectedTeam['Team']) {
          await this.getPMsWithTeam(this.teamManagementSelectedTeam['Team']);
        }
      } catch (error) {
        this.teamManagementErrorMessages = [error.message || 'Unable to refresh Team Management data.'];
      } finally {
        this.teamManagementIsLoading = false;
        this.teamManagementAssignedIsLoading = false;
      }
    },
    getTeamManagementTeams: async function () {
      try {
        const selectedModule = this.getSelectedTeamManagementModule();
        const requestBody = {
          masterfile: 'TEAM',
        };

        if (selectedModule) {
          requestBody.module = selectedModule;
        }

        let result = await this.$apiCallV2('/Master/GetListMaster', requestBody);

        result = this.$jsonNullToStringV2(result || []);
        result = this.$deepTrimJsonV3(result);

        if (!Array.isArray(result)) {
          this.teamManagementTeamItems = [];
          this.teamManagementSelectedTeam = null;
          return;
        }

        this.teamManagementTeamItems = result.map((item) => ({
          ...item,
          'Code - Description': `${item['Team']} - ${item['Description']}`,
        }));

        this.teamManagementSelectedTeam =
          this.teamManagementTeamItems.length > 0 ? this.teamManagementTeamItems[0] : null;
      } catch (error) {
        this.teamManagementTeamItems = [];
        this.teamManagementSelectedTeam = null;

        throw error;
      }
    },
    getPMsWithoutTeam: async function () {
      this.teamManagementIsLoading = true;
      this.teamManagementAvailableSelectedRows = [];

      try {
        const selectedModule = this.getSelectedTeamManagementModule();
        const requestBody = {
          masterfile: 'GETPMNOTEAM',
        };

        if (selectedModule) {
          requestBody.module = selectedModule;
        }

        let result = await this.$apiCallV2('/Master/GetListMaster', requestBody);

        result = this.$jsonNullToStringV2(result || []);
        result = this.$deepTrimJsonV3(result);

        if (!Array.isArray(result)) {
          this.teamManagementAvailablePMs = [];
          return;
        }

        this.teamManagementAvailablePMs = this.removeDuplicateTeamManagementPMs(
          result.map((item) => ({
            ...item,
          })),
        );

        const allPMsByCode = new Map();

        this.teamManagementAvailablePMs.forEach((pm) => {
          const pmKey = this.getTeamManagementPMKey(pm);
          if (!pmKey) return;

          allPMsByCode.set(pmKey, pm);
          this.$set(this.teamManagementOriginalTeams, pmKey, '');
        });

        this.teamManagementAssignedPMs.forEach((pm) => {
          const pmKey = this.getTeamManagementPMKey(pm);
          if (!pmKey) return;

          allPMsByCode.set(pmKey, pm);
        });

        this.teamManagementAllPMs = Array.from(allPMsByCode.values());
      } catch (error) {
        this.teamManagementAvailablePMs = [];

        this.teamManagementErrorMessages = [error.message || 'Unable to retrieve vehicles without a Team.'];
      } finally {
        this.teamManagementIsLoading = false;
      }
    },
    getPMsWithTeam: async function (selectedTeam) {
      this.teamManagementAssignedIsLoading = true;
      this.teamManagementAssignedSelectedRows = [];

      if (!selectedTeam) {
        this.teamManagementAssignedPMs = [];
        this.teamManagementAssignedIsLoading = false;
        return;
      }

      try {
        const selectedModule = this.getSelectedTeamManagementModule();

        const requestBody = {
          masterfile: 'GETPMWITHTEAM',
          action: selectedTeam,
        };

        if (selectedModule) {
          requestBody.module = selectedModule;
        }

        let result = await this.$apiCallV2('/Master/GetListMaster', requestBody);

        result = this.$jsonNullToStringV2(result || []);
        result = this.$deepTrimJsonV3(result);

        if (!Array.isArray(result)) {
          this.teamManagementAssignedPMs = [];
          return;
        }

        this.teamManagementAssignedPMs = this.removeDuplicateTeamManagementPMs(
          result.map((item) => ({
            ...item,
          })),
        );

        const allPMsByCode = new Map();

        this.teamManagementAvailablePMs.forEach((pm) => {
          const pmKey = this.getTeamManagementPMKey(pm);
          if (!pmKey) return;

          allPMsByCode.set(pmKey, pm);
        });

        this.teamManagementAssignedPMs.forEach((pm) => {
          const pmKey = this.getTeamManagementPMKey(pm);
          if (!pmKey) return;

          allPMsByCode.set(pmKey, pm);
          this.$set(this.teamManagementOriginalTeams, pmKey, selectedTeam);
        });

        this.teamManagementAllPMs = Array.from(allPMsByCode.values());
      } catch (error) {
        this.teamManagementAssignedPMs = [];

        this.teamManagementErrorMessages = [error.message || `Unable to retrieve vehicles for Team ${selectedTeam}.`];
      } finally {
        this.teamManagementAssignedIsLoading = false;
      }
    },
    teamManagementAddTeamClicked: function () {
      const selectedModule = String(this.getSelectedTeamManagementModule() || '').trim();

      if (!selectedModule) {
        this.$showAlert(
          'warning',
          'Team Management For Required',
          'Please select Prime Movers or Trucks before adding a new Team.',
        );
        return;
      }

      this.showTeamManagementAddTeamPanel = true;

      this.newTeamCodeInputValue = '';
      this.newTeamCodeInputErrorMessage = '';
      this.newTeamDescriptionInputValue = '';
      this.newTeamDescriptionInputErrorMessage = '';

      this.$nextTick(() => {
        this.$refs.newteam?.focus?.();
      });
    },
    teamManagementActionSaveClicked: async function () {
      const mode = this.teamManagementAction;
      const team = String(this.newTeamCodeInputValue || '').trim().toUpperCase();
      const teamdescription = String(this.newTeamDescriptionInputValue || '').trim();
      const module = String(this.getSelectedTeamManagementModule() || '').trim();
      this.newTeamCodeInputErrorMessage = '';
      this.newTeamDescriptionInputErrorMessage = '';
      if (!module) {
        this.$showAlert(
          'warning',
          'Team Management For Required',
          'Please select Prime Movers or Trucks before adding a new Team.',
        );
        return;
      }

      if (!team) {
        this.newTeamCodeInputErrorMessage = 'Team is required.';
        return;
      }
      if (team.length > 10) {
        this.newTeamCodeInputErrorMessage = 'Team cannot exceed 10 characters.';
        return;
      }
      if (teamdescription.length > 50) {
        this.newTeamDescriptionInputErrorMessage = 'Team Description cannot exceed 50 characters.';
        return;
      }

      const duplicateTeam = this.teamManagementTeamItems.some((item) => {
        return (
          String(item['Team'] || '').trim().toUpperCase() === team
        );
      });

      if (duplicateTeam) {
        this.newTeamCodeInputErrorMessage = `Team ${team} already exists.`;
        return;
      }

      const requestBody = {
        action: 'N',
        team,
        teamdescription,
        module,
      };

      this.teamManagementAddTeamIsSaving = true;

      try {
        const result = await this.$apiCallV2('/Vehicle/SaveTeamManagement', requestBody);

        if (result && result[0] && result[0]['Results'] === '1') {
          this.$showAlert('error', 'Save Failed', result[0]['ErrMsg']);
          return;
        }

        this.teamManagementAction = '';
        this.showTeamManagementAddTeamPanel = false;
        this.teamManagementAvailablePMs = [];
        this.teamManagementAssignedPMs = [];
        this.teamManagementAllPMs = [];
        this.teamManagementOriginalTeams = {};
        await Promise.all([this.getTeamManagementTeams(), this.getPMsWithoutTeam()]);
        const newTeam = this.teamManagementTeamItems.find((item) => {
          return (
            String(item['Team'] || '').trim().toUpperCase() === team
          );
        });
        this.teamManagementSelectedTeam = newTeam || null;
        if (this.teamManagementSelectedTeam) {
          await this.getPMsWithTeam(team);
        }

        this.$showAlert('success', 'Created', result?.[0]?.['ErrMsg'] || `Team ${team} created successfully.`);
      } catch (error) {
        this.$showAlert('error', 'Save Failed', error.message || 'Unable to create the Team.');
      } finally {
        this.teamManagementAddTeamIsSaving = false;
      }
    },
    teamManagementActionCancelClicked: async function () {
      const answer = await this.$showAlert(
        'warning',
        'Cancel New Team',
        'Are you sure you want to cancel? Unsaved Team details will be discarded.',
        [
          { text: 'Yes', color: 'red', textColor: 'white' },
          { text: 'No', color: 'lightgray' },
        ],
      );

      if (answer === 'No') return;
      this.showTeamManagementAddTeamPanel = false;
      this.newTeamCodeInputValue = '';
      this.newTeamCodeInputErrorMessage = '';
      this.newTeamDescriptionInputValue = '';
      this.newTeamDescriptionInputErrorMessage = '';
    },
    getSelectedTeamManagementModule: function () {
      return this.teamManagementForDropdownSelectedItem ?
          this.teamManagementForDropdownSelectedItem['Module'] || ''
        : '';
    },
    getTeamManagementPMKey: function (pm) {
      return String(pm['PM Code'] || pm['VehicleCode'] || pm['Inventory Code'] || '').trim().toUpperCase();
    },

    removeDuplicateTeamManagementPMs: function (pmList) {
      const uniquePMs = new Map();

      (pmList || []).forEach((pm) => {
        const pmKey = this.getTeamManagementPMKey(pm);

        if (pmKey) {
          uniquePMs.set(pmKey, pm);
        }
      });

      return Array.from(uniquePMs.values());
    },

    rebuildTeamManagementAllPMs: function () {
      this.teamManagementAllPMs = this.removeDuplicateTeamManagementPMs([
        ...this.teamManagementAvailablePMs,
        ...this.teamManagementAssignedPMs,
      ]);
    },
  },
  async mounted() {
    await this.refreshVehicleData();
    await this.getMileageAccess();
    await this.getDropdownData();
    this.userDetails = await this.$apiCallV2('/Login/GetDetails');
  },
  watch: {
    selectedTabIndex: function () {
      // delect all selected tablebox rows and tyre
      this.renewalInfoTableBoxSelectedRow = [];
      this.maintenanceTableBoxSelectedRow = [];
      this.tyreGridSelectedTyre = {};
      this.tyreConfigurationTableBoxSelectedRow = [];
      this.attachmentsTableBoxSelectedRow = [];
    },
    categoryDropdownSelectedItem: async function (newCategory) {
      // everytime a new category is selected, update tyre layout for TyreGrid
      if (newCategory) {
        // if the user selected a new category object
        return; // don't do anything, since that is already handled by @selected on the category dropdown
      }

      await this.updateTyreGridTyreLayout(newCategory); // if they cleared the dropdown instead, @selected won't trigger
      // so you'll have to call the method from here
      // the method will then update the layout based on saved tyres only
    },
    policyTypeDropdownSelectedItem: async function (newPolicyType) {
      if (!newPolicyType || Object.keys(newPolicyType).length == 0) {
        // if no policy type is selected
        return;
      }

      await this.getRenewalInfoControls(newPolicyType['PolicyType']);
    },
    intervalUnitRadioButtons: {
      handler: function () {
        if (this.maintenanceAction == 'View') {
          // if the popup is in view mode,
          return; // don't update any of the fields
        }

        const checkedButton = this.intervalUnitRadioButtons.find((button) => {
          return button['checked'];
        });
        if (!checkedButton) {
          // if none of the radio buttons are checked
          this.intervalMileageInputValue = '';
          this.intervalMileageInputIsRequired = false;
          this.intervalMileageInputErrorMessage = '';
          this.intervalMileageInputIsDisabled = true;

          this.intervalDurationInputValue = '';
          this.intervalDurationInputPlaceholder = 'Duration';
          this.intervalDurationInputIsRequired = false;
          this.intervalDurationInputErrorMessage = '';
          this.intervalDurationInputIsDisabled = true;
          return;
        }

        switch (checkedButton['label']) {
          case 'Mileage':
            this.intervalMileageInputIsRequired = true;
            this.intervalMileageInputIsDisabled = false;

            this.intervalDurationInputPlaceholder = 'Duration';
            this.intervalDurationInputValue = '';
            this.intervalDurationInputIsRequired = false;
            this.intervalDurationInputErrorMessage = '';
            this.intervalDurationInputIsDisabled = true;
            break;

          case 'Months':
          case 'Days':
            this.intervalMileageInputValue = '';
            this.intervalMileageInputIsRequired = false;
            this.intervalMileageInputErrorMessage = '';
            this.intervalMileageInputIsDisabled = true;

            this.intervalDurationInputPlaceholder = `Duration (${checkedButton['label']})`;
            this.intervalDurationInputIsRequired = true;
            this.intervalDurationInputIsDisabled = false;
            break;

          case 'Whichever Comes First':
            this.intervalMileageInputIsRequired = true;
            this.intervalMileageInputIsDisabled = false;

            this.intervalDurationInputPlaceholder = 'Duration (Months)';
            this.intervalDurationInputIsRequired = true;
            this.intervalDurationInputIsDisabled = false;
            break;
        }
      },
      deep: true,
    },
    occurrenceRadioButtons: {
      handler: function () {
        if (this.maintenanceAction == 'View') {
          // if the popup is in view mode,
          return; // don't update any of the fields
        }

        const checkedButton = this.occurrenceRadioButtons.find((button) => {
          return button['checked'];
        });

        switch (checkedButton['label']) {
          case 'Once Only':
            this.occurrenceDateTimePickerValue = null;
            this.occurrenceDateTimePickerIsRequired = false;
            this.occurrenceDateTimePickerErrorMessage = '';
            this.occurrenceDateTimePickerIsDisabled = true;
            break;

          case 'Routine until':
            this.occurrenceDateTimePickerIsRequired = true;
            this.occurrenceDateTimePickerIsDisabled = false;
            break;
        }
      },
      deep: true,
    },
    pmToDriverTableBoxSelectedRow: {
      handler: function (selectedRows) {
        if (this.pmToDriverAction === '' && selectedRows && selectedRows.length === 1) {
          this.populatePMToDriverFields(selectedRows[0]);
        }
      },
      deep: true,
    },
    pmToDriverHasDriver2: function (isChecked) {
      if (!isChecked) {
        this.pmToDriverDriver2DropdownSelectedItem = null;
      }
    },
  },
};
</script>

<style lang="scss">
.masterfiles-vehicle-container {
  .availability-info-container {
    .error-message-container {
      position: static !important;
    }
  }
}
</style>

<style lang="scss" scoped>
@import '@/css/all.scss';
.vehicle-status-row { display: flex; flex-wrap: wrap; align-items: center; gap: 8px; }
.vehicle-mileage-section { flex: 0 1 320px; min-width: 0; max-width: 100%; margin-bottom: 10px; }
.vehicle-mileage-controls { display: flex; flex-wrap: wrap; align-items: center; gap: 8px; }
.vehicle-mileage-controls > .input-main { flex: 1 1 140px; min-width: 0; }

.masterfiles-vehicle-container {
  padding: 10px 45px 10px 45px;
  display: flex;
  flex-direction: row;
  font-size: 12px;

  > .left-menu-container {
    height: 100%;
    width: fit-content;
  }

  > .right-tablebox-container {
    height: 100%;
    width: 100%;
    margin-left: 5px;
    padding: 5px;
    border-radius: 5px;
    overflow: hidden;
    background-color: white;
  }

  > .renewal-info-popup-container {
    height: 100%;
    width: 100%;
    position: absolute;
    top: 0;
    left: 0;
    display: flex;
    justify-content: center;
    align-items: center;
    background-color: rgba(0, 0, 0, 0.5);
    z-index: 1500;

    > .renewal-info-popup {
      height: fit-content;
      width: 50%;
      padding: 10px;
      display: flex;
      flex-direction: column;
      background-color: white;
      border-radius: 5px;

      > .controls {
        display: flex;
        flex-direction: column;

        > .positions-1-to-9 {
          display: flex;
          flex-direction: row;

          > .positions-1-to-4 {
            margin-right: 5px;
            display: flex;
            flex-direction: column;
            flex: 1;

            > .position-1 {
              display: flex;
              flex-direction: row;
            }
          }

          > .positions-5-to-9 {
            margin-left: 5px;
            display: flex;
            flex-direction: column;
            flex: 1;
          }
        }

        > .position-10 {
          margin: 5px 0 5px 0;
        }
      }

      > .call-to-action-buttons {
        display: flex;
        flex-direction: row;
        justify-content: flex-end;

        > button {
          margin: 0 3px 0 3px;
        }
      }
    }
  }

  > .maintenance-popup-container {
    height: 100%;
    width: 100%;
    position: absolute;
    top: 0;
    left: 0;
    display: flex;
    justify-content: center;
    align-items: center;
    background-color: rgba(0, 0, 0, 0.5);
    z-index: 1500;

    > .maintenance-popup {
      height: fit-content;
      width: 70%;
      padding: 10px;
      display: flex;
      flex-direction: column;
      background-color: white;
      border-radius: 5px;

      > .controls {
        display: flex;
        flex-direction: row;

        > .maintenance-info {
          margin-right: 5px;
          display: flex;
          flex-direction: column;
          flex: 1;

          > .interval-box-container {
            margin: 16px 0 8px 0;

            .interval-container {
              display: flex;
              flex-direction: row;

              > .interval-unit {
                flex: 1;
              }

              > .interval-input {
                flex: 1;
                display: flex;
                flex-direction: column;
                justify-content: center;
                align-items: center;
              }
            }
          }

          > .occurrence-box-container {
            margin-top: 18px;

            .occurrence-container {
              display: flex;
              flex-direction: column;

              > .occurrence-date-time-picker-container {
                width: 100%;
                display: flex;
                justify-content: flex-start;
                align-items: flex-end;

                > .occurrence-date-time-picker {
                  top: -10px;
                  left: 49%;
                }
              }
            }
          }
        }

        > .maintenance-task-list-container {
          height: 450px;
          margin-left: 5px;
          flex: 1.5;
          overflow: hidden;

          > .task-list-box-container {
            height: 96.5%;

            .call-to-action-buttons {
              margin-bottom: 3px;
              display: flex;
              flex-direction: row;
              justify-content: flex-end;

              > button {
                margin: 0 3px 0 3px;
              }
            }
          }
        }
      }

      > .call-to-action-buttons {
        margin-top: 5px;
        display: flex;
        flex-direction: row;
        justify-content: flex-end;

        > button {
          margin: 0 3px 0 3px;
        }
      }
    }

    > .task-popup {
      ::v-deep {
        // deep needed to target the content class inside the PopupV2 component
        .content {
          // NOT the content class for slot, but content class inside PopupV2 itself
          overflow: visible;
        }
      }

      .task-popup-content {
        display: flex;
        flex-direction: column;
        overflow: visible;

        > div {
          margin: 5px 0 5px 0;
        }

        > .attached-file {
          text-align: left;
          margin: 0;

          > .file-link {
            cursor: pointer;
            user-select: none;

            &:hover {
              text-decoration: underline;
            }

            &:active {
              background-color: black;
              color: white;
            }
          }
        }
      }
    }

    > .duplicate-task-popup {
      ::v-deep {
        // deep needed to target the content class inside the PopupV2 component
        .content {
          // NOT the content class for slot, but content class inside PopupV2 itself
          overflow: visible;
        }
      }

      .duplicate-task-popup-content {
        display: flex;
        flex-direction: column;
        overflow: visible;

        > .warning-message {
          text-align: left;
        }

        > .duplicate-tasks-list-container {
          border: 1px solid lightgray;
          padding: 5px;
          text-align: left;

          > ol {
            margin: 0;
          }
        }

        > .prompt {
          margin-top: 5px;
        }
      }
    }
  }

  > .tyre-configuration-popup-container {
    height: 100%;
    width: 100%;
    position: absolute;
    top: 0;
    left: 0;
    display: flex;
    justify-content: center;
    align-items: center;
    background-color: rgba(0, 0, 0, 0.5);
    z-index: 1500;

    > .tyre-configuration-popup {
      height: fit-content;
      width: 50%;
      padding: 10px;
      display: flex;
      flex-direction: column;
      background-color: white;
      border-radius: 5px;

      > .tyre-position-label {
        margin: 5px 3px 5px 3px;
      }

      > .controls {
        margin: 5px 0 5px 0;
        display: flex;
        flex-direction: row;

        > div {
          margin: 0 5px 0 5px;
          flex: 1;
        }
      }

      > .call-to-action-buttons {
        margin: 5px 0 5px 0;
        display: flex;
        flex-direction: row;
        justify-content: flex-end;

        > button {
          margin: 0 3px 0 3px;
        }
      }
    }
  }

  > .attachments-popup-container {
    > .attachments-popup {
      ::v-deep {
        // deep needed to target the content class inside the PopupV2 component
        .content {
          // NOT the content class for slot, but content class inside PopupV2 itself
          overflow: visible;
        }
      }

      .attachments-popup-content {
        overflow: visible;

        > .attached-file {
          text-align: left;
          margin-bottom: 8px;

          > .file-link {
            cursor: pointer;
            user-select: none;

            &:hover {
              text-decoration: underline;
            }

            &:active {
              background-color: black;
              color: white;
            }
          }
        }
      }
    }
  }

  > .attachment-projector {
    height: 100%;
    width: 100%;
    position: absolute;
    top: 0;
    left: 0;
    z-index: 99999;
    background-color: rgba(0, 0, 0, 0.8);
    display: flex;
    justify-content: center;
    align-items: center;

    > .attachment-projection {
      height: 95%;
      width: 80%;

      > object {
        height: 100%;
        width: 100%;
        object-fit: scale-down;
      }

      > .attachment-not-projectable-container {
        height: 100%;
        width: 100%;
        background-color: rgba(0, 0, 0, 0.7);
        display: flex;
        flex-direction: column;
        justify-content: center;
        align-items: center;
        text-align: center;
        font-size: 40px;

        > span {
          margin: 10px 0 10px 0;
          color: white;
        }

        > button {
          height: 50px;
          width: 150px;
          margin: 10px 0 10px 0;
          font-size: 16px;

          > img {
            height: 24px;
            width: 24px;
          }
        }
      }
    }

    > .close-button {
      height: 50px;
      width: 50px;
      position: absolute;
      top: 10px;
      right: 10px;
      border-radius: 100px;
      background-color: white;
      text-align: center;
      font-size: xx-large;
      cursor: pointer;
      user-select: none;
    }
  }

  .vehicle-todo-popup-list {
    box-sizing: border-box;
    padding: 4px;
  }

  .batch-availability-popup-body {
    height: 100%;
    min-height: 0;
    display: flex;
    gap: 5px;

    > .left-menu-container {
      height: 100%;
      width: fit-content;
      padding: 5px;
      border: 1px solid #d8dee8;
      border-radius: 5px;
    }

    > .right-tablebox-container {
      flex: 1;
      min-width: 0;
      min-height: 0;
      display: flex;
      flex-direction: column;
      padding: 5px;
      border: 1px solid #d8dee8;
      border-radius: 5px;
      overflow: hidden;

      > .table-box-main {
        flex: 1;
        min-height: 0;
      }
    }
  }
}

.batch-availability-action-popup-content {
  display: flex;
  flex-direction: column;
  gap: 8px;
  text-align: left;

  .batch-availability-selected-vehicles {
    max-height: 40vh;
    overflow-y: auto;
    margin: 0;
    padding: 4px 8px 4px 28px;
    border: 1px solid #d8dee8;
    border-radius: 3px;
  }

  .batch-availability-selected-vehicles li {
    padding: 4px 0;
  }
}

.popup-entry-container {
  height: 100%;
  width: 100%;
  display: flex;
  flex-direction: column;

  .availability-info-container {
    margin: 0px 5px 10px 5px;
    display: flex;
    align-items: end;
    gap: 5px;
    border: 1px solid gray;
    width: fit-content;
    padding: 5px 10px;
    border-radius: 10px;
    .availability-radio-container {
      max-width: fit-content;
    }
    .availability-remark-container {
      display: flex;
      align-items: center;
      min-height: 20px;
      width: fit-content;
    }
  }

  > .inventory-info-container {
    display: flex;
    flex-direction: row;

    > .inventory-info-input {
      margin: 0 5px 5px 5px;
    }
  }

  > .tab-component-container {
    height: 100%;
    max-height: calc(100% - 30px); // deduct height of Inventory No and Inventory Code inputs

    > .tab-component {
      height: 100%;

      .tab-container {
        height: 100%;
      }

      .general {
        display: flex;
        flex-direction: column;

        > div {
          flex: 1;
          margin: 0 5px 0 5px;
        }
      }

      .engine-information {
        display: flex;
        flex-direction: column;

        > .engine-information-container {
          display: flex;
          flex-direction: row;
          margin: 5px 0 5px 0;

          > div {
            flex: 1;
            margin: 0 5px 0 5px;
          }
        }

        > .weight-information-container {
          height: fit-content;
          border: 1px solid lightgray;
          border-radius: 5px;
          margin: 10px 5px 0 5px;
          padding: 10px 0 15px 0;
          display: flex;
          flex-direction: row;

          > div {
            flex: 1;
            margin: 0 5px 0 5px;
          }
        }
      }

      .renewal-info {
        display: flex;
        flex-direction: column;

        > .call-to-action-buttons {
          display: flex;
          flex-direction: row;
          justify-content: flex-end;
          margin-bottom: 3px;

          > button {
            margin: 0 3px 0 3px;
          }
        }

        > .table-box-container {
          height: calc(100% - 55px); // deduct height of call to action buttons
        }
      }

      .maintenance {
        display: flex;
        flex-direction: column;

        > .call-to-action-buttons {
          display: flex;
          flex-direction: row;
          justify-content: flex-end;
          margin-bottom: 3px;

          > button {
            margin: 0 3px 0 3px;
          }
        }

        > .table-box-container {
          height: calc(100% - 31px); // deduct height of call to action buttons
        }
      }

      .tyre-configuration {
        display: flex;
        flex-direction: row;

        > .tyre-grid-container {
          height: 100%;
          flex: 1;
        }

        > .tyre-list {
          flex: 1;
          display: flex;
          flex-direction: column;
          min-height: 0;
          min-width: 0;
          overflow: hidden;

          > .call-to-action-buttons {
            padding-top: 2px;
            display: flex;
            flex-direction: row;
            justify-content: flex-end;
            margin-bottom: 3px;

            > button {
              margin: 0 3px 0 3px;
            }
          }

          > .table-box-container {
            flex: 1;
            min-height: 0;
          }
        }
      }

      .attachments {
        display: flex;
        flex-direction: column;

        > .call-to-action-buttons {
          display: flex;
          flex-direction: row;
          justify-content: flex-end;
          margin-bottom: 3px;

          > button {
            margin: 0 3px 0 3px;
          }
        }

        > .table-box-container {
          flex: 1;
        }
      }
    }
  }
}

.pm-to-driver-popup-container {
  width: 100%;
  height: 100%;
  min-height: 0;
  padding: 10px 2px 6px;
  box-sizing: border-box;
  display: flex;
  align-items: stretch;
  gap: 10px;
  overflow: hidden;

  > .pm-to-driver-list {
    width: 0;
    min-width: 0;
    min-height: 0;
    flex: 1 1 auto;
    align-self: stretch;
    display: flex;
    flex-direction: column;
    overflow: hidden;

    > .table-box-container {
      width: 100%;
      min-width: 0;
      min-height: 0;
      flex: 1 1 auto;
      overflow: hidden;

      > * {
        width: 100%;
        height: 100%;
      }
    }
  }

  > .pm-to-driver-details {
    width: 400px;
    min-width: 400px;
    height: auto;
    min-height: 0;
    max-height: none;

    margin: 4px 0 0 auto;
    flex: 0 0 400px;
    align-self: stretch;
    box-sizing: border-box;
    overflow: visible;
  }
}

.pm-to-driver-details-content {
  width: 100%;
  height: auto;
  min-height: 0;

  display: flex;
  flex-direction: column;
  gap: 8px;

  > .pm-to-driver-driver2-box {
    width: 100%;
    height: auto;
    flex: 0 0 auto;
    box-sizing: border-box;
  }

  > button {
    flex: 1;
    min-width: 60px;
    white-space: nowrap;
  }
}

.team-management-popup-container {
  width: 100%;
  height: 100%;
  min-height: 0;
  padding: 8px 4px;
  box-sizing: border-box;

  display: flex;
  flex-direction: column;
  gap: 10px;
  overflow: hidden;

  > .team-management-toolbar {
    flex: 0 0 auto;
    display: grid;
    grid-template-columns: 220px minmax(300px, 1fr) 150px;
    align-items: center;
    gap: 10px;

    > button {
      height: 35px;
      white-space: nowrap;
    }
  }

  > .team-management-tables {
    height: 0;
    min-height: 0;
    flex: 1 1 0;
    display: flex;
    align-items: stretch;
    gap: 8px;
    overflow: hidden;

    > .team-management-table-panel {
      position: relative;
      width: 0;
      height: 100%;
      min-width: 0;
      min-height: 0;
      flex: 1 1 0;
      overflow: hidden;

      > .table-box-container {
        position: absolute;
        inset: 0;
        width: 100%;
        height: 100%;
        min-height: 0;
        overflow: hidden;
      }
    }

    > .team-management-transfer-buttons {
      width: 42px;
      flex: 0 0 42px;
      display: flex;
      flex-direction: column;
      justify-content: center;
      gap: 10px;
    }
  }
}

@media only screen and (max-width: 900px) {
  .team-management-popup-container {
    overflow: auto;

    > .team-management-toolbar {
      grid-template-columns: 1fr;
    }

    > .team-management-tables {
      min-width: 800px;
      min-height: 500px;
    }
  }
}

@media only screen and (max-width: 826px) {
  .masterfiles-vehicle-container
  >.maintenance-popup-container
  >.maintenance-popup
  >.controls
  >.maintenance-info
  >.occurrence-box-container
  .occurrence-container // no child selector needed, it won't work properly with the child selector
  >.occurrence-date-time-picker-container
  >.occurrence-date-time-picker {
    // copy the full css selector from chrome devtools if more media queries are needed
    top: -10px;
    left: 10%;
  }
}
</style>
